// GROUND GEN — the DEVICE ground painter (APP_GENSURF §5e, gs-gpupainter): one
// GmTex slot (j, tx, ty) = 512 px RGBA8 sRGB of albedo x E / W over 128 * 2^j m,
// generated on the GPU from the device's inputs. The GLSL is a LINE-BY-LINE port
// of the CPU reference groundref.js (+ genometex.js genomeTexel, groundways.js's
// ways pass), staged the same way and fed the same per-tile CPU uniforms; the
// constants are groundref / groundways / genometex's own (imported, never
// re-typed). Precision target vs groundref.paintSlot: mean ΔE76 <= 2, p99 <= 6
// (tools/gpuprobe.mjs --ground-check N; window.__vr.ground).
//
// API
//   new GroundGen(tm, meta, vt, getRenderer)   terrain.js, next to its GmTex `vt`
//   setSurface(surf)        surface.js loadSurface's object (the coarse class grid)
//   scan(now)               GmTex.update's scan (vt.onScan, every SCAN_MS): the
//                           finest level, the stale-reservation check, the want
//                           list -> gen.want('ground', tiles, view)
//   state() / read(key) / keys() / refWorld(key) / ensure(key)   window.__vr.ground (probe)
//   dispose()
// THE GENPASS (gensched.js C8): id 'ground', pool null (the target is GmTex's pool
// through ITS producer seam), params ['ground.finest_level'] (the one regen value
// run reads). tiles = vt.wanted(now) whose inputs are all in (the rest load
// through assetq fetchAsset first: a listed tile never waits in run — class 'near' for
// a level GmTex calls near (vt._nearLoad: the beam-in latch waits on it, and 'bulk' is
// held from the Beam to the latch), else 'bulk'; from the Beam to the latch only the
// latch's own tiles (_latchTile) and the top two levels load; the per-world inputs and
// surface.js's coarse class grid (the top levels' classes) 'near') + the reservation
// in flight; need = GmTex's score (near 0 / bulk 10 + rank). inputs() is [] (loaded
// here before a tile is listed): the queue runs such a tile without a fetch slot.
// run(gl, job, ms): first its inputs still cached (the cache evicts what no job pins)
// and vt.reserve(key) (either not: 'done', the key held out of the list HOLD_MS so
// the queue forgets it, then listed again); then the job's steps
// (job.state.steps: CPU prep, then one GL pass or one band of a pass each) while
// the CPU time + the steps' GPU estimate (texels x weight / GPU_RATE) fits ms:
// 'more' between, 'done' after the last mip + vt.commit(key). Every reserve ends
// in a commit or a release: a throw -> release(key, true) + rethrow; a job the
// queue dropped mid-run (_drop: no callback) is found at the next scan / run
// (gen._cur no longer holds it) -> release(key, false). Every run ends with
// renderer.resetState().
// STAGES (groundref.js STAGES; raster = paint()'s HALOED raster PW = 512 + 2 m,
// m = haloPx(texel), texel (i, k) at local (i - m + 0.5) texel, k = 0 north):
//   0. heights   TIN: the level-j nodes' triangles (CPU: the flat gradient, NOSTAND /
//                degenerate dropped, the (ty, tx) node order) -> RGBA32F (h, cov, gx,
//                gz); depth = the triangle's sequence (first triangle wins); then the
//                row fill (nearest covered left, else right) and the column fill
//                (groundref's rule: below a covered row -> the last covered row, else
//                the first) -> H.
//   1. albedo    per texel the class blend (exact 1 m coverage under the 2 m box at
//                texel < 1, else the box's texels' nearest cells), classes in id order:
//                genome (T x class_mean x R x anchor, s2col on MEASURED_KEYS), the
//                hand palette, water deep / the sheet bed (gravel) -> A.
//   2. ways      (1 m class tiles, waysTile live) prep (ring, bare, ring_l) -> box
//                sums horizontal (3 x RGBA32F, 12 channels) -> vertical + resolve +
//                pathTexel + markTexel (fused) -> A'.
//   3. apron     the elevation zones past the class map, the 6 m box of outside texels.
//   4. crowns    the WORLD-aligned crown grid (cell, window: groundref crownGrid): TIN
//                at the cell centres (+ fills), the discs as instanced quads (key =
//                floor((zc + 1000) 256) 16 + cls relative to an integer yref, DEPTH32F
//                GREATER = the tallest), obj / cls / Sf, the sun march (inner cells),
//                the box sums (vo 1 cell, the sky radii; horizontal then vertical, in
//                row bands) -> fsun / fsky.
//   5. light     SHD2 bilinear (the level pick), gridAt(fsun, fsky), light_irradiance,
//                E -> sRGB-8 -> relight; crop + row flip (row 0 = south) -> the pool
//                layer level 0 + a scratch sRGB mip chain (MRT); then level L from
//                the scratch's L - 1 (2 x 2 box in linear: the sRGB fetch / store),
//                both written (MRT), L = 1 .. 9 — every mip of the layer, never a
//                generateMipmap.
// PER-TILE CPU UNIFORMS: the slot origin (x0, z0: integer m), texel, m, r; the
// noise lattice frames split at the slot origin (integer + fraction, float64 on the
// CPU: the GLSL adds float32 local offsets — the world hash in uint32); the genome
// record table at the level's texel (genomeTexelConsts: al / be / pal) + its octave
// frames; the class window (compact class | 0x80 sheet bed), the ways field window +
// its markCells, waysTile's (kc, pk, amp, ea, eb, wa, wb, hh, area, treadKa); the
// SHD2 level pick + windows; the crown window (cell, X0 Z0 nx nz, inner, yref, march
// steps).
// BYTES (GPU scratch, per job, freed at its end): raster PW^2 x 16 B x (H, A, A' + the
// fill spare) = 4 targets, + 3 (ways: 3 prep, each freed once its row-sum target is made: at
// most 3 more at a time) while stage 2 runs, + DEPTH32F;
// crowns: nx nz x (8 TIN RG32F + 8 fill + 4 key + 4 depth, then 16 CG + 4 vo + 8 fsun /
// fsky) + a band of (rows + 2 rmax) x inner cols x 24 B (the sky row sums); the mip
// scratch 512^2 x 4 x 4/3 (per world). state().done[key].peak_mb.
// CPU (the frame's gen ms, desktop-igpu play 2 ms): no step reads a per-texel string key or
// rebuilds a per-world table — the class / ways tiles are mapped once per world (WeakMap on
// the decoded asset) and windows are row-slice copies; every CPU loop over cells / rows /
// vertices is SLICED to the run's deadline (a step returns MORE until done; a slice starts
// only while its last one's time still fits); the GPU work is costed (units / GPU_RATE) and
// a run yields once its estimate is spent, the next frame paying the last run's estimate
// (_carry). A step starts only when its CPU (the label's recent worst unit: a call, or one
// slice of a sliced step) + its GPU estimate + the run's reset fit what the slice leaves;
// the first step of a run that does not fit a PARTIAL slice (a job started after another
// this frame) yields to the next frame's whole one. SUBMISSIONS per run are capped per device class
// (SUBMIT: the bytes of one texSubImage2D / bufferSubData call — every upload in row bands / pieces,
// never one call that scales with the tile —, the draws, the GPU cost units and the GPU estimate a
// run; GL.meter measures them from the calls' own arguments, state().submit). Every GPU pass is
// banded (planRows, the TIN and the disc keys by the scissor) so one run's GPU estimate stays <= the
// class's gpu_ms; state().gpu_run is the GPU ms per RUN (its timer queries). THE CALIBRATION (ruling p8
// (1), COST MODEL): a step's GPU ms = a + units / r per label, fitted (fitModel) on its BACK-TO-BACK timer
// samples (a run's later queries; the first one holds the frame's queued work) — the pieces planned to
// (cap - a) x r at the shown cap, no run factor; at RUN time (ruling p8 (2), packNext) every step is
// re-estimated at the model now x the RUN FACTOR (RUNK: the worst measured / estimated back-to-back GPU
// of the last runs, hidden and shown apart, aged) and a rect piece split to what the run has left —
// only an atomic step (a mip level) or a rect's smallest portion opens a fresh run past its cap
// (state().submit.atomic, by name); every run opens with a LEAD query
// (no work: it takes the queued frame work, state().gpu_run lead) so its steps' sum is the run's own GPU
// (state().gpu_run hidden / shown: over = runs past their gpu_ms). A slice below the run's
// indivisible minimum (minMs) is banked by gensched (PASS.minMs). The CPU work that is no texel loop runs in
// a worker (groundwork.js, __GG_WORKER_SRC__; groundcpu.js): the genome records' texture
// solves + per-texel constants, waysTile's tread pitch + markCells (the field window
// transferred), the ground-tree nodes' decode (codec + lookup index + the TIN's triangles +
// the vertex array; the indexed mesh stays in the worker), the crown packs' decode (they
// stay there) and a slot's crown discs (crownDiscsOf on those meshes -> the instance array,
// asked for the next listed slots ahead, PRE_DISCS) — without a worker, inline. No
// checkFramebufferStatus (a synchronous GPU-process round trip: tens of ms on ANGLE).
// THE SCAN (ruling p6 (c), p7 (2)): no scan part or once-per-world part past 2 ms — the want list checks its
// deadline at every key (a key first seen only while its geometry's recent cost fits: 'scan.key') and
// _load between fetches (the scan's first one always); a fetch's synchronous start (the decode / queue
// set-up, the Request, the browser's fetch()) runs in a task of its own ('fetch.start' / 'fetch.world',
// charged as the scan is), the world's queue made in one scan and started one a scan; the eviction walk
// stops at EVICT_MS; the warm-up makes the GL alone, then a program's sources / its compile a task (the
// world's bands <= WARM_UP_BYTES; state().warm_top the slowest by name); the worker is made off the
// frame (ggWorkerSoon: the one named one-off, state().one_off). gensched charges every scan's ms.
// state().fetch_calls: every main-thread fetch() call's own ms + the synchronous span around it (assetq).
// MEMORY per device class (CAPS, deviceClass()): freed scratch targets go to a reuse pool
// (pool_mb, drained once no job ran for idle_ms) — no texStorage + robust-init clear a
// job; the ground-tree node meshes stay on the GPU node-local (an LRU of node_mb) for the
// neighbour slots that draw them; the decoded inputs (main thread + the worker's share) are
// an LRU of cache_mb. The in-use scratch is the job's (BYTES above), freed at its end.
// state(): steps (per label CPU ms n / ms / max / unit + the timer-query GPU ms, its rates:
// all samples and the back-to-back ones a run's later steps give), gl (per GL call n / ms /
// max on the painter's context), slow (the last steps over SLOW_MS: their GL share + the
// slowest call), trace (every TRACE_MS: the queue's debt, the over-slice runs, the near set
// missing, the held MB), caps.
import { fetchAsset, fetchCallStats, bulkPaused } from './assetq.js';
import { decodeImage } from './surface.js';
import { decodeCrowns } from './crownpack.js';
import { heavyOk, phase, budget, hidden } from './sched.js';
import { gen, genLive, genRaw, onGenChange } from './gensched.js';
import { PAINTER_KEY, paintHit } from './paintkey.js';
import { vtVis } from './gmtex.js';
import { deviceClass } from './device.js';
import { SLOT_PX, TIN_EX, TIN_EZ, strataField, TILE, TILE_KEYS, tilePrepSteps, tileLevel, sealedPalette } from './groundref.js';
import { prepWays, Q4 } from './groundways.js';
import { geomOf, slotReads, strataSum, sunOf } from './slotgeom.js';
import { ANCHOR_DEFAULT, GRASS_SAT_DEFAULT, sheetsOf, slotWorld, worldBase } from './slotworld.js';
import { genomeTexParams, genomeTexelConsts } from './genometex.js';
import { waysCpu, q4Cpu, nodeCpu, discsCpu, bytesOf } from './groundcpu.js';
import GNT from '../../pipeline/genome_noise.json';
import PAL from './painterpal.json';
import WAYSLOOK from '../../pipeline/wayslook.json';
import { NCMAX, RW, MARKS_IN, roundEven, clamp, octaveKeep, staticWays, fixedFrames, genomeRecRows, TILE_MAX, DISC_VERTS, DISC_OCT, DISC_PAD, shaderMakers } from './groundglsl.js';

// the staging estimate: a GL step's cost units / GPU_RATE = its GPU ms until the label's own timer
// queries say (_rate). Calibrated on desktop-igpu (Intel MTL, gpupainter-perf1b + perf3: the
// timer-query means of albedo / ways.path / light / sky / box / march / prep / mips / heights
// ~ 0.8 .. 3.5e7 units a ms, median ~2e7) — the per-label cost formulas in _plan are scaled to
// that one rate
const GPU_RATE = 2e7;
// THE PLANNING RATE (orchestrator ruling p7 (1); p8: the cost model's r, COST MODEL): a label's units a
// ms as its BACK-TO-BACK timer queries measure them (a run's later queries: its first one holds the
// frame's queued work), x RATE_SAFE (p8: 1 — the run factor carries the margin, measured per run; the
// offline sim at 0.8 took 15 % more latch frames at no fewer overs) — never the old clamp to GPU_RATE / 8 .. x 4 (gpupainter-p6 quest: the estimate held a
// hidden run to 8 ms while its timer read 22-23 ms, 138 of 343 runs past 8 ms). RATE_LO / RATE_HI
// are the planner's bounds: a measured slow rate is planned at (x RATE_SAFE) down to RATE_LO — the
// slowest rate at which every INDIVISIBLE piece (a mip level's pass, SLOT_PX^2 x COST.mips = 3.1 M
// units) still fits the smallest class gpu_ms (2 ms) —, a fast one gives no estimate below 1 / (2
// GPU_RATE) a unit (p6: mips / crowns.obj / apron / ways.prep sat at the old x 4 ceiling)
// RATE_LO (ruling p8 (1)): GPU_RATE / 4 = 5 M units a ms, 3 x the p7 spiral's floor (GPU_RATE / 12 =
// 1.67, where every p7 rate ended) and under every label's p5 back-to-back mean (6 .. 40 M a ms on the
// Intel MTL host); a label measured slower is planned at RATE_LO and its runs teach the run factor
const RATE_SAFE = 1.0;
const RATE_LO = GPU_RATE / 4, RATE_HI = GPU_RATE * 2;
const RATE_N = 32;                 // _model: a label's last back-to-back samples
const RATE_B2B_N = 8;             // ... the samples a fit needs (fewer: the prior; p4-3: 3-4 of them read 6x fast)
// THE COST MODEL (ruling p8 (1)): a GL step's GPU ms = a + units / r — a the per-piece fixed cost (the
// pass's set-up, and its share of its neighbour's work the timer spans swap), r the label's rate —
// fitted PER LABEL on its back-to-back samples alone (fitModel). Per label, not one global a: the
// passes differ in their fixed part as in their rate (attachments, bound inputs, the instanced discs),
// and a label's pieces DO differ in size (a pass's remainder rows, the re-split portions, the level's
// raster / crown grid) — where they do not, the per-sample rate given the prior a is exact at the size
// run, and the pieces it sizes converge on (cap - a_true) x r (a fixed point; the round-7 total / total
// had none once a_true passed RATE_SAFE x BAND_MS / K: gpupainter-p7 desktop-igpu, 8992 ways.path pieces
// of 0.4 M units in 14 slots). Samples below U_TINY units are ignored (their span is the fixed part and
// the neighbours' share); a slope needs TS_PAIRS pairs whose sizes differ >= TS_RATIO x (Theil-Sen: the
// median pair slope, a = the median residual), else a = A_PRIOR; a in [0, A_MAX]; r = the median
// per-sample units / (ms - a). THE GUARD: r never below what the label's LARGE pieces showed (rBig: the
// median units / ms of its last BIG_N samples within BIG_FRAC of the largest — units / ms bounds the
// rate from below whatever a is), so a shrinking piece cannot lower the planned rate. Then x RATE_SAFE,
// held to [RATE_LO, RATE_HI]. No measure yet (< RATE_B2B_N back-to-back samples): GPU_RATE x RATE_SAFE and
// the POOLED a (the median a of the labels fitted so far; none: A_PRIOR) — never the all-sample measure (a
// run's first query holds the frame's queued work: the p7 labels planned on it fell to RATE_LO). The
// pooled a: a label the runs rarely time back-to-back (a pass's first pieces open runs) is not planned
// at a fixed cost 10 x too small (the sim: those labels' runs measured 2 x their estimate, the factor
// went to 4)
const A_PRIOR = 0.1, A_MAX = 1.0;
const U_TINY = 0.25e6;
const TS_RATIO = 1.25, TS_PAIRS = 6;
const BIG_N = 8, BIG_FRAC = 0.5;
// THE RE-SPLIT (ruling p8 (2)): a step joining a run (not its first) as a portion of at least this much
// work at its rate (no sliver that is all fixed cost)
const SPLIT_MIN_MS = 0.25;
// THE RUN FACTOR (ruling p7 (1)): a run's measured back-to-back GPU over the planner's estimate of
// those same steps, per sched mode (hidden / shown: the frame's load differs) — the worst of the
// last RUNK_N runs whose estimate is >= RUNK_EST, held to [1, RUNK_MAX] — scales every estimate
// (the pieces are planned without it, ruling p8 (2): the re-split applies it at run time): the per-label
// rates are means, a run's GPU is the sum of its samples. A run's ratio counts only when its steps after
// the first hold >= RUNK_B2B of its estimate (ruling p8): its b2b span also takes the first step's own
// spill (bounded by that step), so a 1-2 step run's ratio is mostly that spill — gpupainter-p7: the shown
// factor sat at 4 on 2-step runs and every shown run after shrank to one step
// A ratio EXPIRES after RUNK_AGE runs of its mode (ruling p8: a high factor makes every run one step —
// no back-to-back sample, no new ratio, no refit — and would hold forever: p7's shown factor sat at 4;
// the offline sim at a 1 ms fixed cost froze the hidden one at 4 and the latch at a third of its rate)
const RUNK_N = 32, RUNK_MAX = 4, RUNK_EST = 0.25, RUNK_B2B = 0.75, RUNK_AGE = 64;
const BAND_MS = 1.0;              // a band of a heavy pass: <= this estimate
const HOLD_MS = 1000;             // a reserve(null) key stays out of the want list
const LIST_MAX = 12;              // tiles listed to the queue at once
const PREFETCH = 4;               // wanted tiles whose inputs load ahead
const GATHER_N = 2;               // keys gathering at once in play (painter-gather: one at a time serialised the queue)
const LOAD_MAX = 8;               // concurrent asset loads
const RETRY_MS = 5000;
// THE SHIPPED SLOTS (APP_PREPAINT §2b, OWNER_RULES 2026-10-31: a cache of this pass): a key at a level of
// meta.surface.paint whose (painter, params) are this client's is ONE input, its slot's lossless WebP, uploaded +
// this pass's mips; any other key paints. THE EXPECTED TIME: a slot's fetch + decode (EWMA, wall ms from its start)
// past a painted job's (EWMA from the job's start; SHIP_PAINT_MS before the first) paints instead, one fetch every
// SHIP_PROBE_MS keeping the measure (a slow network never makes the ground slower than painting it)
const SHIP_PAINT_MS = 2500, SHIP_PROBE_MS = 10e3, SHIP_EW = 0.25;
// an upload call's bytes at most: the class's SUBMIT up_bytes (a texSubImage row band) / buf_bytes
// (a bufferSubData piece; 256 KB ones blocked 4-8 ms on the igpu: gpupainter-p2-4 / p2-5)
const SLICE_CELLS = 8192;         // a sliced CPU loop's chunk: about this many cells (row-aligned)
const MIN_RUN_MAX = 1.5;          // the declared indivisible run (PASS.minMs: the queue banks a slice below it)
const GPU_RUNS = 512;              // state().gpu_run: the last runs' GPU ms (timer queries; med / max)
const PRE_DISCS = 2;              // listed slots whose crown discs the worker makes ahead of their job
const GEO_N = 1024;               // the slot geometries (+ input ids, ready memo) kept (the scan's wanted keys: made once)
const SLOW_MS = 4;                // state().slow: a step call this long (CPU) is logged
const SLOW_GL_MS = 2;             // ... and a GL call this long
const TRACE_MS = 1000;            // state().trace: a sample this often (at the scan)
const UNIT_N = 8;                 // a label's CPU unit: the worst of its last UNIT_N
const WARM_UP_BYTES = 64 * 1024;  // a warm-up upload task's band at most (the class's up_bytes if less)
const WARM_MS = 1.0;              // the warm-up's tasks (programs, uniform tables, world textures) a scan: while the
                                  // time so far + the worst task yet fits (p4-3: 4 ms -> scan.warm 8.5 ms)
const PART_MS = 1.0;              // a sliced scan part (the world's residual rows, the want list's new keys) a scan
const SCAN_PART_MS = 1.25;        // ... the want list ends here too, from the scan's start (the scan's own parts before it)
const PART_MIN_MS = 0.25;         // ... yet the want list gets this much at least
const WANT_MS = 1.5;              // the whole want scan (rank + list + queue + discs + evict) a scan
const WANT_TAIL_MS = 0.4;         // ... the list's end before it (the queue, discs + evict after it)
const CONT_MS = 50;               // a want scan cut short runs again this soon (vt.onTick), not a SCAN_MS later
const PROG_GAP_MS = 200;          // a program's compile + link call this long after the last (and the links polled
                                  // this long after the last): pace-final desktop-igpu, the 15 calls in ~1 s left the
                                  // first LINK_STATUS query blocked 10-123 ms (the driver resolving the queued links)
// THE HIDDEN SET-UP (beam-order F2, probe 2026-10-24: the Beam to the painter's first scan 10.3-11.3 s, igpu +
// quest, the latch's first 10 s): while the beam-in overlay is up (sched hidden) no frame is seen — the set-up's
// sliced parts (the world's genome records + residual rows: PART_MS, the warm-up's tasks: WARM_MS) take
// SETUP_HID_K x their budget a scan and a program's compile + link call follows the last PROG_GAP_HID_MS on
// (a link-status wait then lands behind the overlay). Shown (the Map, play) the paces above hold
const SETUP_HID_K = 6;
const PROG_GAP_HID_MS = 50;
// (hid -> { k: the parts' budget factor, gap: the program gap ms }; the gate's model reads it)
const setupPace = (hid) => (hid ? { k: SETUP_HID_K, gap: PROG_GAP_HID_MS } : { k: 1, gap: PROG_GAP_MS });
const setupK = () => setupPace(hidden()).k;
const progGap = () => setupPace(hidden()).gap;
// the want list's deadline: its own PART_MS from its start (t0), the scan's SCAN_PART_MS from the scan's
// start (tS; at least PART_MIN_MS of its own), and the whole want's WANT_MS from its start (tW) less the
// tail's WANT_TAIL_MS (whole = false: without the last — the gate's red twin)
const listDl = (t0, tS, tW, whole = true) => Math.min(t0 + PART_MS, whole ? tW + WANT_MS - WANT_TAIL_MS : Infinity, Math.max(t0 + PART_MIN_MS, tS + SCAN_PART_MS));
// ... the tail after it at t: the discs only before the whole want's end (dlW), the evict walk to it
// (at least PART_MIN_MS, at most EVICT_MS)
const discsOk = (t, dlW, whole = true) => !whole || t < dlW;
const evictDl = (t, dlW, whole = true) => (whole ? Math.min(t + EVICT_MS, Math.max(t + PART_MIN_MS, dlW)) : t + EVICT_MS);
// THE GATHER SET (refetch-loop: desktop-igpu owner pose, 1436 input fetches in 90 s standing still): a
// wanted key whose inputs started loading, or found all in, stays in `gat` — pinned against _evict —
// until it leaves the want (committed, unwanted). A new key is admitted only under cache_mb and while fewer
// than GATHER_N others gather (gath: gat's keys not all in; the latch's tiles in the beam apart, up to PREFETCH loading
// a scan: pre), so a cap too small for the want slows the queue — the cache passes cache_mb by about one
// key's inputs — and never evicts a gathering key's inputs (the ping-pong: admitted under the cap,
// unpinned over it, its inputs dropped, admitted again). -> 0 skip, 1 list (ready), 2 load, 3 admitted
// + load. Pure; scripts/check-gpupainter.mjs traces it.
function gatherPick(gat, key, ready, over, lt, pre, gath, prefetch = PREFETCH, gn = GATHER_N) {
  if (ready) { gat.add(key); return 1; }
  if (gat.has(key)) return 2;
  if (pre >= prefetch || (!lt && (over || gath >= gn))) return 0;
  gat.add(key);
  return 3;
}
// ... the keys no longer wanted leave it (W: GmTex wanted() rows, seen: a reused Set); -> the keys left
// that still gather (ready(key): its inputs all in)
function gatherPrune(gat, W, seen, ready) {
  if (!gat.size) return 0;
  seen.clear();
  for (const c of W) seen.add(c[0]);
  let n = 0;
  for (const k of gat) if (!seen.has(k)) gat.delete(k); else if (!ready(k)) n++;
  return n;
}
// THE WANT'S REFERENCES (painter-gather: desktop-igpu owner pose, 648 repeated input fetches in 90 s
// standing still — neighbour slots read the same class / ways / node / pack / SHD2 inputs, the LRU
// dropped a shared input of a wanted slot not gathering yet, which fetched it again): every wanted key
// whose ids are made (+ the job's) counts its inputs in refs (refK: key -> its ids). Each input is
// fetched + decoded ONCE while any wanted key reads it: _evict drops the unreferenced ones (LRU) and a
// referenced one only when the queue is stuck — the referenced bytes past cache_mb, nothing gathering,
// nothing ready behind the job — the worst-needed first (refNeed: its first reader's place in the want; Belady's
// choice) to admit the best key. -> refK synced to W (seen: a reused Set); idsOf(k): its made ids or
// null (never made here). Pure; scripts/check-gpupainter.mjs traces it.
function refSync(refK, W, seen, cur, idsOf, add, del) {
  seen.clear();
  for (const c of W) seen.add(c[0]);
  if (cur) seen.add(cur);
  for (const k of refK.keys()) if (!seen.has(k)) del(k);
  for (const k of seen) if (!refK.has(k)) { const ids = idsOf(k); if (ids) add(k, ids); }
}
// LOCALITY (painter-gather): in play a new key is admitted from the first ADMIT_K admissible keys in want
// order (cands [key, ids, ...]) — the one with the fewest inputs not in (have(id): cached or loading), ties
// the want's order — so neighbours sharing inputs paint together while the shared ones are in (the want is
// spatially scattered: every level's ring at once). The beam admits in want order (the latch's tiles).
// GAP FIRST (OWNER_RULES 2026-10-31; w52 p1 A/B: 1-2 on-screen tiles 2+ levels coarser for 54 s): a candidate
// that clears a gap (c[3]: gmtex gain > 0) is admitted first, in want order — locality would pass it over
// for as long as a cheaper key stands within ADMIT_K behind it
const ADMIT_K = 6;
function gatherBest(cands, have) {
  let best = null, bm = Infinity;
  for (const c of cands) {
    if (c[3]) return c;
    let m = 0;
    for (const id of c[1]) if (!have(id)) m++;
    if (m < bm) { bm = m; best = c; }
  }
  return best;
}
// ... GAP FIRST at the cap: a BLOCKED key (blk: its place in W) that clears a gap (gap) frees the inputs placed
// after it while a ready key waits behind it (rdy0: the first ready key's place, -1 none; none ready is the stuck
// rule's own case) — the ready keys behind it would otherwise paint first, its room never freed (ready keys
// are pinned: only the inputs of keys neither ready nor gathering go). Pure; scripts/check-gpupainter.mjs traces it
function gapFirst(over, beam, gath, blk, rdy0, gap) {
  return over && !beam && !gath && blk >= 0 && rdy0 > blk && !!gap;
}
// THE FLOOR FIRST (pp5; w57 diag 2026-10-31, igpu Map entry: the landing's floor key at the want's head 6.7 s not
// admitted after the latch, GATHER_N keys gathering behind it): in play a key neither ready nor gathering is a LOCALITY
// candidate (gatherBest: under cache_mb and GATHER_N) — but the latch's own tiles (gmtex latchKey: the floor under the
// stand) go to gatherPick at once with its lt, past both caps, as in the beam (the cache passes cache_mb by at most the
// floor's inputs). -> true: a candidate. floor = false: the old rule (the floor a candidate too) — the gate's red twin. Pure
function candOf(beam, gathering, lt, floor = true) {
  return !beam && !gathering && !(floor && lt);
}
// THE KNOWN TAIL (pp5, the same diag: after the latch ~130 new L0/L1 keys head the want, every scan cut at the first of
// them past its unit — 5.6 s nothing listed with 12 ready keys behind, the painter idle, the gathering keys behind never
// loading): a FRESH key (geometry + ids not made) is made while its worst recent unit fits the list's deadline less
// KNOWN_MS; past it (kc: this scan's fresh cut) the walk SKIPS the fresh keys (made next scan, head first) and goes on
// over the known ones to the deadline — the ready listed, the gathering loading. -> 0 make it, 1 skip it, 2 end the walk
// (tail = false: the old rule, a fresh key past its unit ends the walk — the gate's red twin). Pure
const KNOWN_MS = 0.25;
function freshStep(t, unit, dl, kc, tail = true) {
  if (!kc && t + unit <= dl - (tail ? KNOWN_MS : 0)) return 0;
  return tail ? 1 : 2;
}
// ... the room a stuck queue frees: the first key in W (from W[from]: a blocked key's place) with inputs not
// in (have), their count x mean bytes
function stuckRoom(W, idsOf, have, mean, from = 0) {
  for (let i = Math.max(0, from); i < W.length; i++) {
    const c = W[i], ids = idsOf(c[0]);
    if (!ids) continue;
    let m = 0;
    for (const id of ids) if (!have(id)) m++;
    if (m) return m * mean;
  }
  return 0;
}
// ... each referenced input's need: the place in W of the first key that reads it (lower = sooner)
function refNeed(W, idsOf, out) {
  out.clear();
  for (let i = 0; i < W.length; i++) {
    const ids = idsOf(W[i][0]);
    if (ids) for (const id of ids) if (!out.has(id)) out.set(id, i);
  }
  return out;
}
const STALL_MS = 2;               // state().stats.stall: a run.state / run.reset call this long (an outlier)
const EVICT_MS = 0.5;             // _evict's walk a scan (the rest next scan)
const EVICT_CHUNK = 16;           // ... its deadline checked every this many entries
// MEMORY CAPS per device class (deviceClass(); APP_GENSURF §8 / §4b: the GmTex pools are not the
// painter's, these are): pool_mb the freed-target reuse pool, idle_ms its drain once no job ran,
// node_mb the GPU node-mesh LRU, cache_mb the decoded inputs (main thread + worker). A job's own
// scratch (BYTES above, ~40-55 MB at L0) is not capped: it is freed at the job's end
const CAPS = {
  'desktop-dgpu': { pool_mb: 64, idle_ms: 3000, node_mb: 32, cache_mb: 160 },
  'desktop-igpu': { pool_mb: 48, idle_ms: 2000, node_mb: 24, cache_mb: 128 },
  quest: { pool_mb: 24, idle_ms: 1000, node_mb: 16, cache_mb: 80 },
  'phone-hi': { pool_mb: 16, idle_ms: 750, node_mb: 12, cache_mb: 64 },
  'phone-lo': { pool_mb: 8, idle_ms: 400, node_mb: 8, cache_mb: 48 },
  software: { pool_mb: 8, idle_ms: 400, node_mb: 8, cache_mb: 48 },
};
const caps = () => CAPS[deviceClass()] || CAPS['phone-lo'];
// SUBMISSION CAPS per device class (gs-gpupainter ruling): what ONE run hands the driver.
//   up_bytes   one texSubImage2D's payload (every texture upload goes in row bands of it: planChunks)
//   buf_bytes  one bufferSubData's payload (_bufUp's pieces)
//   draws      draw calls a run (drawArrays / drawElements / drawArraysInstanced)
//   units      the GPU cost units a run (_plan's per-step cost, GPU_RATE units a nominal ms)
//   gpu_ms     the run's GPU estimate (cost / the label's measured, clamped rate): the GPU per RUN
//              (= per frame: a run 'yield's) — no gen GPU budget exists in gensched / the governor;
//              proposed here (Quest: a slot's GPU work landing in one frame is a VR comfort fault)
// A pass is cut into PIECES (planRect: row bands, a row past the limit in column pieces — the scissor)
// whose cost fits units and gpu_ms at the label's planning rate; a run takes pieces while they fit
// (fitsRun). Every piece of every pass at every level is held to the class's caps
// (scripts/check-gpupainter.mjs: slotPieces over geomOf).
// BEAM (orchestrator ruling p6 (a): BEAM_K = 4 accepted, while HIDDEN only): from the Beam until the
// beam-in overlay drops (sched hidden — main.js showBeamIn / hideBeamIn; NOT the phase, which turns
// 'play' at the latch while the overlay still holds) the view is the overlay (flat / phone) or black
// (VR, main.js beamBlack): no frame is seen, and the latch's near slots must be painted inside
// load.dismiss_timeout_ms (30 s). A hidden run's draws / units / gpu_ms are the class's x BEAM_K (the
// bytes of one call are not: an upload's stall is the driver's either way) — the pieces stay planned
// to the class's own caps, so the first run of the first shown frame holds them (runCaps). Quest: a
// hidden run's GPU <= 8 ms (2 x 4; state().gpu_run.hidden measures it). gpupainter-p4-3 quest: the
// 2 ms a run (0.89 ms a run as packed) left 26 latch slots at ~110 runs each, the latch timed out.
// TIER RULE — phone-lo coarse to the latch (orchestrator ruling p6 (b)(i), in the line of APP_GENSURF
// §13 ruling 6, "the software tier generates coarse levels only"): phone-lo generates coarse levels
// only up to the beam-in latch, then refines. Until the latch (sched phase 'play') its finest level
// is raised to LATCH_FINEST (2 = load.near_levels, the coarsest level the latch waits on; never past
// it): nothing finer is wanted, waited for, or kept (GmTex.minLevel, vtCut), so the latch waits on
// the near tiles' level-2 ancestors (4 at the p5 poses, not 10-26 slots of levels 0-2) — real
// lower-resolution data, the slot texel 1 m, not invention: the ground tree draws a node once a
// texture is resident at its level OR ABOVE (GmTex.texReady). At the latch it drops back to the
// class's own finest level (ground.finest_level, L0) and levels 0-1 are wanted and painted. The raise
// is no regen value: the level-2 slots painted under it are the slots painted without it (finestFor).
// gpupainter-p5 phone-lo: 8-10 slots committed in 300 s, the latch timed out at every pose.
// quest + desktop-igpu: the same rule from the beam to the latch (orchestrator ruling, gs-gpupainter-pace
// item 1: gpupainter-p8 chalchera-win, 26 latch slots of levels 0-2, the latch timed out on both).
const SUBMIT = {
  'desktop-dgpu': { up_bytes: 256 * 1024, buf_bytes: 64 * 1024, draws: 48, units: 8e7, gpu_ms: 4 },
  'desktop-igpu': { up_bytes: 256 * 1024, buf_bytes: 64 * 1024, draws: 32, units: 6e7, gpu_ms: 3 },
  quest: { up_bytes: 128 * 1024, buf_bytes: 32 * 1024, draws: 24, units: 4e7, gpu_ms: 2 },
  'phone-hi': { up_bytes: 128 * 1024, buf_bytes: 32 * 1024, draws: 24, units: 4e7, gpu_ms: 2 },
  'phone-lo': { up_bytes: 64 * 1024, buf_bytes: 16 * 1024, draws: 16, units: 4e7, gpu_ms: 2 },
  software: { up_bytes: 64 * 1024, buf_bytes: 16 * 1024, draws: 16, units: 4e7, gpu_ms: 2 },
};
const BEAM_K = 4;
const submitCaps = (cls = deviceClass()) => SUBMIT[cls] || SUBMIT['phone-lo'];
const runCaps = (cap, hid) => (hid ? { ...cap, draws: cap.draws * BEAM_K, units: cap.units * BEAM_K, gpu_ms: cap.gpu_ms * BEAM_K } : cap);
// the tier rule's pre-latch finest level per class (TIER RULE above; absent: none)
const LATCH_FINEST = { 'phone-lo': 2, quest: 2, 'desktop-igpu': 2 };
// the finest level a scan holds the want to: the class's own (ground.finest_level), raised to its
// LATCH_FINEST (never past near, the latch's levels) until the latch (sched phase 'play')
const finestFor = (cls, finest, ph, near = 2, rule = LATCH_FINEST) => {
  const r = rule[cls];
  return r === undefined || ph === 'play' ? finest : Math.max(finest, Math.min(r, near));
};
// PREEMPTED — a scored over that is another GPU context's, not ours (orchestrator ruling, gs-gpupainter-pace
// item 2): gpupainter-p8 + the pace localisation probe, 58 of 59 back-to-back overs were ONE step measured
// 5-25 ms (quantised ~5 / 10 / 15 / 25 ms: another context's span inside the step's TIME_ELAPSED on the
// shared iGPU) against an estimate of 0.04-1.1 ms, the rest of the run inside its cap. A scored over is
// preempted when (i) its spiked steps — each measured > max(PRE_K x its estimate, its estimate + PRE_ADD_MS)
// — (ii) explain it: the run's back-to-back GPU without all their excesses holds the cap, and (iii) none of
// their labels is systematically underestimated: each label's median measured / estimate over every
// back-to-back sample <= PRE_MED_MAX (a step we mis-plan is never hidden as preemption). (Every spiked step,
// not one: orchestrator ruling, pace-final quest se — one run, two albedo spikes.) The bar: 0 own overs
// (preempted apart, reported: state().gpu_run.<mode>.preempted)
const PRE_K = 4, PRE_ADD_MS = 2.5, PRE_MED_MAX = 1.5;
// (i) + (ii): a run's timed steps q (flat [label, units, est, ms, flags] — 16 back-to-back) -> its spiked
// steps when their excesses explain the over {labels, at (their offsets in q), ms (the worst spike's), ex
// (summed)}, else null
const preemptStep = (q, cap, b2b) => {
  let w = null;
  for (let i = 0; i < q.length; i += 5) {
    if (!(q[i + 4] & 16)) continue;
    const est = q[i + 2], ms = q[i + 3];
    if (!(ms > Math.max(PRE_K * est, est + PRE_ADD_MS))) continue;
    if (!w) w = { labels: [], at: [], ms: 0, ex: 0 };
    w.labels.push(q[i]); w.at.push(i); w.ex += ms - est;
    if (ms > w.ms) w.ms = ms;
  }
  return w && b2b - w.ex <= cap + 1e-9 ? w : null;
};
// (iii): a candidate (preemptStep) is preempted when every spiked label's median ratio (medOf) holds PRE_MED_MAX
const preempted = (c, medOf) => !!c && c.labels.every((l) => medOf(l) <= PRE_MED_MAX);
// THE LEARNING SKIPS PREEMPTION (painter-throughput, orchestrator ruling): a SHOWN scored run (in cap or over)
// classed PREEMPTED by the same (i) + (ii) + (iii) gives the run factor no ratio (RUNK: one spike of another
// context pinned it at RUNK_MAX — every shown run one step) and its spiked steps give their labels' cost
// models no sample (the rest of the run's steps do). For the learning (iii) also holds on the LOWER QUARTILE
// of the label's last PRE_REC_N samples (medRec, spikes in): a label whose rate truly collapses (every sample
// slow) is no longer 'preempted' after PRE_REC_N - 1 of its samples, so RUNK + its model take it then, not
// after half its 256-sample ring; spikes on half a label's samples (the shared iGPU) keep it low. A HIDDEN
// run learns every sample as before (ruling (a): skipped there, the x BEAM_K runs packed toward their cap and
// the probe's hidden overs rose) (gate rows 'learning skips preemption', 'recent guard', 'hidden learns all')
const PRE_REC_N = 8;
const learnPre = (c, medOf, medRec) => preempted(c, medOf) && c.labels.every((l) => medRec(l) <= PRE_MED_MAX);
// the median of a ring's first n values
const ringMed = (a, n) => { const v = Array.from(a.subarray(0, n)).sort((x, y) => x - y); return n ? v[n >> 1] : Infinity; };
// the lower quartile of a label ring's last k values (L {a, n, i}: i the next write; none: Infinity)
const REC_V = new Float64Array(PRE_REC_N);
const ringRecent = (L, k = PRE_REC_N) => {
  const m = Math.min(k, L.n, REC_V.length), N = L.a.length;
  if (!m) return Infinity;
  for (let j = 0; j < m; j++) REC_V[j] = L.a[(L.i - 1 - j + N) % N];
  const v = REC_V.subarray(0, m).sort();
  return v[(m - 1) >> 2];
};
// the submission planner (pure: _plan, _bufUp, GL.tex and the gate's mock-GL row use it)
// THE OLD BANDS (the gate's red twin only): rows [0, n) of perRow units in bands of about
// min(BAND_MS at rate, cap.units, cap.gpu_ms at rate) — a band can pass it by a row (the ceil), and a
// row is the indivisible unit (a row past the limit is a band past it)
function planRows(n, perRow, rate, cap) {
  const lim = Math.min(BAND_MS * rate, cap.units, cap.gpu_ms * rate);
  const nb = Math.max(1, Math.ceil((n * perRow) / lim)), h = Math.max(1, Math.ceil(n / nb)), out = [];
  for (let y = 0; y < n; y += h) out.push([y, Math.min(n, y + h)]);
  return out;
}
// a piece's cost limit at a label's model (rate, a: ruling p8 (1)): the class's units, and what its
// gpu_ms holds past the fixed cost, (gpu_ms - a) x the rate (never above GPU_RATE: a few fast samples
// must not size a piece the true rate takes 4x as long) — the largest piece a shown run takes whole
// (fewer pieces, less fixed cost; a hidden run packs several, a shown one at a run factor > 1 re-splits)
const pieceLim = (rate, cap, a = 0) => { const r = Math.min(rate, GPU_RATE); return Math.min(cap.units, Math.max(0, cap.gpu_ms - a) * r); };
// rows [0, n) x columns [0, w) of a pass costing per units a texel -> pieces [y0, y1, x0, x1, units]
// of <= pieceLim: equal row bands of whole rows, or (a row past the limit) each row in equal column
// pieces. Every pass here writes texel-independent outputs (the scissor rect), so a piece is any rect
function planRect(n, w, per, rate, cap, a = 0) {
  const lim = Math.max(per, pieceLim(rate, cap, a)), row = w * per, out = [];
  if (row <= lim) {
    const nb = Math.ceil(n / Math.max(1, Math.floor(lim / row))), h = Math.ceil(n / nb);
    for (let y = 0; y < n; y += h) { const y1 = Math.min(n, y + h); out.push([y, y1, 0, w, (y1 - y) * row]); }
  } else {
    const nc = Math.ceil(row / lim), cw = Math.ceil(w / nc);
    for (let y = 0; y < n; y++) for (let x = 0; x < w; x += cw) { const x1 = Math.min(w, x + cw); out.push([y, y + 1, x, x1, (x1 - x) * per]); }
  }
  return out;
}
// an upload of h rows of rowBytes in calls of <= maxBytes (whole rows; one row a call at least) -> rows a call
const planChunks = (rowBytes, maxBytes) => Math.max(1, Math.floor(maxBytes / rowBytes));
// a run's submissions so far {n steps, units, draws, est ms} + one more step {units, draws, est}: fits
// the class's caps. split: the step can be split (a rect piece: ruling p8 (2) — never admitted past the
// caps; the run re-splits it, packNext); else (an atomic step) a run's first one always runs
const fitsRun = (run, s, cap, split = false) => (!split && run.n === 0)
  || (run.units + s.units <= cap.units && run.draws + s.draws <= cap.draws && run.est + s.est <= cap.gpu_ms + 1e-9);
// the planning rate of a label's measured units / ms (its back-to-back samples' cost over their ms)
const planRate = (measured) => clamp(measured * RATE_SAFE, RATE_LO, RATE_HI);
// THE OLD RATE (the gate's red twin only): the measured rate clamped to GPU_RATE / 8 .. x 4
const oldRate = (measured) => clamp(measured, GPU_RATE / 8, GPU_RATE * 4);
// THE ROUND-7 RATE (the gate's red twin only): the cost / ms of the last n samples (S: [units, ms]
// pairs) x RATE_SAFE, held to GPU_RATE / 12 .. x 2 — no fixed cost: a piece's fixed part reads as a
// slower rate, the smaller pieces it plans read slower still (the spiral)
const rate7 = (S, n) => { let c = 0, m = 0; for (let k = 0; k < Math.min(n, RATE_N) * 2; k += 2) { c += S[k]; m += S[k + 1]; } return clamp((m > 0 ? c / m : GPU_RATE) * RATE_SAFE, GPU_RATE / 12, GPU_RATE * 2); };
// the run factor of the last runs' measured / estimated back-to-back GPU ratios (RUNK above)
const runK = (ratios) => { let m = 1; for (const r of ratios) if (r > m) m = r; return Math.min(RUNK_MAX, m); };
// the aged factor (RUNK_AGE): ring r of ratios, at[i] the mode's run clock when r[i] landed, n pushed, t the clock now
const runKAged = (r, at, n, t) => {
  let m = 1;
  for (let i = 0, k = Math.min(n, RUNK_N); i < k; i++) if (t - at[i] < RUNK_AGE && r[i] > m) m = r[i];
  return Math.min(RUNK_MAX, m);
};
// CROWN SKIP (painter-throughput, STAGE SKIPS): a slot's crown passes are skipped when its window holds no
// crown disc (nd: discsCpu's count over the slot's span) — groundref crownGrid with no disc gives obj 0, so
// vo = vob = ov = 1, c = 0 and fsun = fsky = 1 EXACTLY (a 3 x 3 mean of ones; a bilinear of ones is 1 in
// float32, FMA or not), the light's crowns-off value (uCG.w 0: gridAt 1). Gate row 'stage skips'
const crownSkip = (nd) => !(nd > 0);
// a step's GPU ms at its label's model {a, r} (ruling p8 (1)): no units, no GPU
const modelMs = (m, units) => (units > 0 ? m.a + units / m.r : 0);
const MS_EPS = 1e-3;
const TS_S = new Float64Array((RATE_N * (RATE_N - 1)) / 2), TS_V = new Float64Array(RATE_N + BIG_N);   // fitModel's scratch
const medOf = (A, k) => { const v = A.subarray(0, k).sort(); return k & 1 ? v[k >> 1] : 0.5 * (v[(k >> 1) - 1] + v[k >> 1]); };
// THE FIT (COST MODEL above): S the label's last n back-to-back samples ([units, ms] pairs, a ring of
// RATE_N), B its large pieces' (a ring of BIG_N, nb pushed) -> out {a, r, ts (1: the slope fitted), big
// (rBig, 0: none), n (samples used)}; fewer than RATE_B2B_N usable samples: the prior
function fitModel(S, n, B, nb, out, aPrior = A_PRIOR) {
  const k = Math.min(n, RATE_N);
  let m = 0;
  for (let i = 0; i < k; i++) if (S[2 * i] >= U_TINY && S[2 * i + 1] > 0) m++;
  out.n = m; out.ts = 0; out.big = 0;
  if (m < RATE_B2B_N) { out.a = aPrior; out.r = planRate(GPU_RATE); return out; }
  // Theil-Sen: the median slope (ms a unit) over the pairs whose sizes differ enough
  let np = 0;
  for (let i = 0; i < k; i++) {
    const ui = S[2 * i], mi = S[2 * i + 1];
    if (!(ui >= U_TINY && mi > 0)) continue;
    for (let j = i + 1; j < k; j++) {
      const uj = S[2 * j], mj = S[2 * j + 1];
      if (!(uj >= U_TINY && mj > 0) || Math.max(ui, uj) < TS_RATIO * Math.min(ui, uj)) continue;
      TS_S[np++] = (mj - mi) / (uj - ui);
    }
  }
  let a;
  if (np >= TS_PAIRS) {
    const sl = Math.max(0, medOf(TS_S, np));
    let q = 0;
    for (let i = 0; i < k; i++) if (S[2 * i] >= U_TINY && S[2 * i + 1] > 0) TS_V[q++] = S[2 * i + 1] - sl * S[2 * i];
    a = medOf(TS_V, q);
    out.ts = 1;
  } else {
    let q = 0;
    for (let i = 0; i < k; i++) if (S[2 * i] >= U_TINY && S[2 * i + 1] > 0) TS_V[q++] = S[2 * i + 1];
    a = Math.min(aPrior, 0.5 * medOf(TS_V, q));
  }
  a = clamp(a, 0, A_MAX);
  let q = 0;
  for (let i = 0; i < k; i++) if (S[2 * i] >= U_TINY && S[2 * i + 1] > 0) TS_V[q++] = S[2 * i] / Math.max(S[2 * i + 1] - a, MS_EPS);
  let r = medOf(TS_V, q);
  // THE GUARD: what the large pieces showed (units / ms: a lower bound of the rate, whatever a is)
  const kb = Math.min(nb, BIG_N);
  if (kb >= 3) {
    for (let i = 0; i < kb; i++) TS_V[i] = B[2 * i] / Math.max(B[2 * i + 1], MS_EPS);
    out.big = medOf(TS_V, kb);
    if (out.big > r) r = out.big;
  }
  out.a = a; out.r = planRate(r);
  return out;
}
// a label with no sample yet: the prior
const PRIOR_M = Object.freeze({ a: A_PRIOR, r: Math.min(RATE_HI, Math.max(RATE_LO, GPU_RATE * RATE_SAFE)), ts: 0, big: 0, n: 0 });   // (= planRate(GPU_RATE): clamp is not up yet)
// a sample into the large pieces' ring B (nb pushed; B's top: the largest units it holds): one within
// BIG_FRAC of its top enters (a smaller one never displaces them) -> the new nb
function bigAdd(B, nb, u, ms) {
  let top = 0;
  for (let i = 0; i < Math.min(nb, BIG_N); i++) if (B[2 * i] > top) top = B[2 * i];
  if (!(u >= U_TINY && ms > 0) || u < BIG_FRAC * top) return nb;
  const i = (nb % BIG_N) * 2;
  B[i] = u; B[i + 1] = ms;
  return nb + 1;
}
// A RECT STEP (ruling p8 (2): a pass's piece, split at run time): {per (units a texel), y0, y1, x0, x1,
// cx (a part-done first row's next column; x0: none)} -> its next portion of <= u units into P [units,
// est0, est, y0, y1, x0, x1, y0 after, cx after] (s is not moved: packNext's caller commits P[7], P[8]):
// the rest of a part-done row, else whole rows, else (not one row fits) the first row's columns; 0: not
// one texel fits
function rectNext(s, u, P) {
  const W = s.x1 - s.x0;
  if (s.cx > s.x0) {
    const c = Math.min(s.x1 - s.cx, Math.floor(u / s.per));
    if (c < 1) return 0;
    P[3] = s.y0; P[4] = s.y0 + 1; P[5] = s.cx; P[6] = s.cx + c;
    const done = s.cx + c >= s.x1;
    P[7] = done ? s.y0 + 1 : s.y0; P[8] = done ? s.x0 : s.cx + c;
    return c * s.per;
  }
  const rows = Math.min(s.y1 - s.y0, Math.floor(u / (s.per * W)));
  if (rows >= 1) {
    P[3] = s.y0; P[4] = s.y0 + rows; P[5] = s.x0; P[6] = s.x1; P[7] = s.y0 + rows; P[8] = s.x0;
    return rows * W * s.per;
  }
  const c = Math.min(W - 1, Math.floor(u / s.per));
  if (c < 1) return 0;
  P[3] = s.y0; P[4] = s.y0 + 1; P[5] = s.x0; P[6] = s.x0 + c; P[7] = s.y0; P[8] = s.x0 + c;
  return c * s.per;
}
// THE RUN-TIME RE-SPLIT (ruling p8 (2)): a run's next step, re-estimated at its label's model m now (x
// the run factor K) -> RUN (P: what runs — a rect step's rest, or its largest portion within what the
// run has left), STOP (the run ends: the step waits for the next run) or OVER (a FRESH run admits it past
// its cap: an atomic step — a mip level's pass —, or a rect step whose fixed cost alone, K x a, leaves no
// portion; counted and named, state().submit.atomic). c: the step's units (a rect step: its rest, P
// filled by rectNext at Infinity), dr its draws, cap the run's caps, left the GPU ms the run has left
// (the cap's; a later step or a partial slice: the slice's share too), late: not the run's first step
// or a partial slice (a portion then: >= SPLIT_MIN_MS of work)
const RUN = 1, STOP = 0, OVER = 2;
function packNext(sub, s, c, dr, m, K, cap, left, late, P) {
  const rect = s.per !== undefined && c > 0;
  let est0 = modelMs(m, c);
  if (rect && (est0 * K > left + 1e-9 || sub.units + c > cap.units)) {
    const u = Math.min(cap.units - sub.units, (left / K - m.a) * m.r);
    c = u > 0 && u >= (late ? SPLIT_MIN_MS * m.r : 0) ? rectNext(s, u, P) : 0;
    if (!(c > 0)) {
      if (late || sub.n > 0) return STOP;
      c = rectNext(s, Math.max(s.per, SPLIT_MIN_MS * m.r), P);
      P[0] = c; P[1] = modelMs(m, c); P[2] = P[1] * K;
      return OVER;
    }
    est0 = modelMs(m, c);
  }
  P[0] = c; P[1] = est0; P[2] = est0 * K;
  if (fitsRun(sub, { units: c, draws: dr, est: P[2] }, cap, true)) return RUN;
  return !rect && sub.n === 0 ? OVER : STOP;
}
export { setupPace, WARM_MS, gatherPick, gatherPrune, gatherBest, gapFirst, candOf, freshStep, KNOWN_MS, ADMIT_K, GATHER_N, refSync, refNeed, stuckRoom, PREFETCH, LIST_MAX, caps, SUBMIT, BEAM_K, submitCaps, runCaps, LATCH_FINEST, finestFor, listDl, discsOk, evictDl, WANT_MS, WANT_TAIL_MS, PRE_K, PRE_ADD_MS, PRE_MED_MAX, PRE_REC_N, preemptStep, preempted, learnPre, ringRecent, crownSkip, planRows, planRect, pieceLim, planChunks, fitsRun, GPU_RATE, RATE_LO, RATE_HI, RATE_SAFE, BAND_MS,
  planRate, ALB_FINE_K, albCost, DISC_OCT, DISC_PAD, oldRate, rate7, runK, runKAged, RUNK_MAX, RUNK_B2B, RUNK_EST, RUNK_AGE, RUNK_N, modelMs, fitModel, bigAdd, rectNext, packNext, RUN, STOP, OVER, A_PRIOR, A_MAX, U_TINY, SPLIT_MIN_MS, RATE_N, BIG_N };
const TEXELS = Array.from({ length: 9 }, (_, j) => (128 * 2 ** j) / SLOT_PX);   // the levels' texels (gmtex LV_MAX 8)
// a step's fn(deadline) returns MORE (call it again: CPU work left past the deadline) or WAIT (an
// answer from the worker is due: the run ends, the job yields the frame); anything else = done
const MORE = 1, WAIT = 2;
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const named = (nm, fn) => Object.assign(fn, { nm });      // a warm-up task with its name (state().warm_top)


// ---------------------------------------------------------------- GL helpers (raw WebGL2)
const FMT = {
  RGBA32F: ['RGBA32F', 'RGBA', 'FLOAT', 16], RG32F: ['RG32F', 'RG', 'FLOAT', 8], R32F: ['R32F', 'RED', 'FLOAT', 4],
  R8UI: ['R8UI', 'RED_INTEGER', 'UNSIGNED_BYTE', 1], RGBA8UI: ['RGBA8UI', 'RGBA_INTEGER', 'UNSIGNED_BYTE', 4],
  D32F: ['DEPTH_COMPONENT32F', 'DEPTH_COMPONENT', 'FLOAT', 4], SRGB8A8: ['SRGB8_ALPHA8', 'RGBA', 'UNSIGNED_BYTE', 4],
};
const SETTER = {
  0x1406: 'uniform1fv', 0x8b50: 'uniform2fv', 0x8b51: 'uniform3fv', 0x8b52: 'uniform4fv',
  0x1404: 'uniform1iv', 0x8b53: 'uniform2iv', 0x8b54: 'uniform3iv', 0x8b55: 'uniform4iv', 0x8b56: 'uniform1iv',
  0x1405: 'uniform1uiv', 0x8dc6: 'uniform2uiv', 0x8dc7: 'uniform3uiv', 0x8dc8: 'uniform4uiv',
};
const SAMPLERS = new Set([0x8b5e, 0x8dd2, 0x8dca, 0x8b5f, 0x8dc1, 0x8dcf, 0x8dd7]);   // 2D, u2D, i2D, 3D, 2DArray, ...
const ONE = [0];                  // a scalar uniform's value (use(): no array a call)

// the painter's view of the context: every method timed (rec(name, ms): state().gl / .slow), the
// constants copied — the probe's per-call measure of the main thread's GL time
// The submissions (SUBMIT) are metered here too, from the calls' own arguments: every upload's
// payload bytes (texSubImage2D / texImage2D / bufferSubData / bufferData with data) and every draw
// -> M (GL.meter: per run, _run resets it)
const PAYLOAD = { texSubImage2D: (a) => a[8], texImage2D: (a) => a[a.length - 1], bufferSubData: (a) => a[2], bufferData: (a) => a[1] };
const DRAWS = new Set(['drawArrays', 'drawElements', 'drawArraysInstanced', 'drawElementsInstanced']);
const payloadOf = (k, a) => {
  const v = PAYLOAD[k] && PAYLOAD[k](a);
  if (!v || typeof v !== 'object' || !(v.byteLength >= 0)) return 0;
  // bufferSubData(target, offset, src, srcOffset, length): the length's elements
  return k === 'bufferSubData' && a.length > 4 ? a[4] * (v.BYTES_PER_ELEMENT || 1) : v.byteLength;
};
function timedGL(gl, rec, M) {
  const w = Object.create(null);
  for (const k in gl) {
    const v = gl[k];
    if (typeof v === 'function') {
      const up = !!PAYLOAD[k], dr = DRAWS.has(k), buf = k.startsWith('buffer');
      w[k] = function () {
        if (up) { const b = payloadOf(k, arguments); if (b) M.up(b, buf); } else if (dr) M.draws++;
        const t0 = now(), r = v.apply(gl, arguments);
        rec(k, now() - t0);
        return r;
      };
    } else if (typeof v === 'number') w[k] = v;
  }
  w.canvas = gl.canvas;
  return w;
}
// a run's submissions (reset per run): the largest texture / buffer upload call, the calls, the draws
class Meter {
  constructor() { this.reset(); }
  reset() { this.tex = 0; this.buf = 0; this.calls = 0; this.bytes = 0; this.draws = 0; }
  up(b, buf) { this.calls++; this.bytes += b; if (buf) { if (b > this.buf) this.buf = b; } else if (b > this.tex) this.tex = b; }
}

class GL {
  constructor(gl0, rec = null) {
    this.meter = new Meter();
    const gl = rec ? timedGL(gl0, rec, this.meter) : gl0;
    this.gl = gl;
    if (!gl.getExtension('EXT_color_buffer_float')) throw new Error('groundgen: EXT_color_buffer_float missing — the ground painter needs float render targets (WebGL2 + EXT_color_buffer_float)');
    this.par = gl.getExtension('KHR_parallel_shader_compile');
    this.vao = gl.createVertexArray();
    this.bytes = 0;
    this.peak = 0;
    this.pool = new Map();                        // 'f w h levels' -> freed targets for reuse (the class's pool_mb)
    this.poolBytes = 0;
    this.cap = null;                              // the SUBMIT caps (null: the device class's)
  }
  caps() { return this.cap || submitCaps(); }
  // a program, linked asynchronously when KHR_parallel_shader_compile is there
  program(name, vs, fs) {
    const gl = this.gl, p = gl.createProgram();
    for (const [t, src] of [[gl.VERTEX_SHADER, vs], [gl.FRAGMENT_SHADER, fs]]) {
      const s = gl.createShader(t);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      gl.attachShader(p, s);
      gl.deleteShader(s);
    }
    gl.linkProgram(p);
    return { name, p, vs, fs, u: null, attr: {} };
  }
  done(P) { return !this.par || this.gl.getProgramParameter(P.p, this.par.COMPLETION_STATUS_KHR); }
  // link status + the uniform table (once, after done)
  finish(P) {
    const gl = this.gl;
    if (!gl.getProgramParameter(P.p, gl.LINK_STATUS)) {
      const sh = gl.getAttachedShaders(P.p) || [];
      const logs = sh.map((s) => gl.getShaderInfoLog(s)).filter(Boolean).join('\n');
      throw new Error(`groundgen: program ${P.name} failed: ${gl.getProgramInfoLog(P.p)}\n${logs}`);
    }
    P.u = new Map();
    const n = gl.getProgramParameter(P.p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < n; i++) {
      const a = gl.getActiveUniform(P.p, i), nm = a.name.replace(/\[0\]$/, '');
      P.u.set(nm, { loc: gl.getUniformLocation(P.p, a.name), type: a.type, size: a.size });
    }
    for (const nm of ['aP', 'aC', 'aT']) P.attr[nm] = gl.getAttribLocation(P.p, nm);
  }
  tex(f, w, h, data = null, levels = 1) {
    // a target (no data): a freed one of the same format + size when the pool has it — no
    // allocation, no robust-init clear in the GPU process (every pass writes its target whole or
    // clears it first, so a reused one's old texels are never read)
    const sig = `${f} ${w} ${h} ${levels}`;
    if (!data) {
      const a = this.pool.get(sig);
      if (a && a.length) {
        const T = a.pop();
        this.poolBytes -= T.b;
        this.bytes += T.b;
        this.peak = Math.max(this.peak, this.bytes);
        return T;
      }
    }
    const gl = this.gl, [ifmt, fmt, type, bpp] = FMT[f], t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texStorage2D(gl.TEXTURE_2D, levels, gl[ifmt], w, h);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, levels > 1 ? gl.NEAREST_MIPMAP_NEAREST : gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const b = Math.round(w * h * bpp * (levels > 1 ? 4 / 3 : 1));
    const T = { t, w, h, f, b, sig, pooled: !data };
    // the data in row bands of the class's up_bytes (a small table: one call)
    if (data) { const n = planChunks(w * bpp, this.caps().up_bytes); for (let y = 0; y < h; y += n) this.sub(T, data, y, Math.min(h, y + n)); }
    this.bytes += b;
    this.peak = Math.max(this.peak, this.bytes);
    return T;
  }
  // a target back to the pool (while it holds < the class's pool_mb), else deleted; the caller's handle is spent
  free(T) {
    if (!T || !T.t) return;
    this.bytes -= T.b;
    if (T.pooled && this.poolBytes + T.b <= caps().pool_mb * 1e6) {
      let a = this.pool.get(T.sig);
      if (!a) this.pool.set(T.sig, (a = []));
      a.push({ t: T.t, w: T.w, h: T.h, f: T.f, b: T.b, sig: T.sig, pooled: true });
      this.poolBytes += T.b;
    } else this.gl.deleteTexture(T.t);
    T.t = null;
  }
  // the pool's targets deleted (idle: no job ran for the class's idle_ms)
  drain() {
    for (const a of this.pool.values()) for (const T of a) this.gl.deleteTexture(T.t);
    this.pool.clear();
    this.poolBytes = 0;
  }
  // a framebuffer of color textures (+ depth); entries {t} (2D level 0) or {t, layer, level} (array)
  fbo(cols, depth = null) {
    const gl = this.gl, f = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, f);
    cols.forEach((c, i) => {
      if (c.layer !== undefined) gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + i, c.t, c.level, c.layer);
      else gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0 + i, gl.TEXTURE_2D, c.t, c.level || 0);
    });
    if (depth) gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, depth.t, 0);
    const db = this._db || (this._db = []);       // the draw-buffer lists, made once per count (no array a call)
    gl.drawBuffers(db[cols.length] || (db[cols.length] = cols.map((_, i) => gl.COLOR_ATTACHMENT0 + i)));
    // no checkFramebufferStatus: it is a synchronous round trip to the GPU process that waits
    // behind the queued frame (gpupainter-perf1b: 9 calls, 233 ms, up to 51 ms one). Every
    // attachment here is an immutable whole level of a colour-renderable format
    // (EXT_color_buffer_float, required in the constructor: RGBA32F / RG32F / R32F; SRGB8_ALPHA8;
    // DEPTH_COMPONENT32F), equal sizes per framebuffer — complete by construction
    return f;
  }
  // the pipeline state every pass assumes (three's is restored by renderer.resetState after a run)
  // (no allocation a run: the enum list made once)
  state() {
    const gl = this.gl, off = this._off || (this._off = [gl.BLEND, gl.CULL_FACE, gl.DITHER, gl.STENCIL_TEST, gl.POLYGON_OFFSET_FILL, gl.SAMPLE_ALPHA_TO_COVERAGE, gl.SAMPLE_COVERAGE, gl.RASTERIZER_DISCARD]);
    for (let i = 0; i < off.length; i++) gl.disable(off[i]);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(true);
    gl.colorMask(true, true, true, true);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
    gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, null);
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
  }
  // bind P, set its uniforms (textures: {t} -> the next unit)
  use(P, U) {
    const gl = this.gl;
    gl.useProgram(P.p);
    let unit = 0;
    // (no allocation a call: gs-gpupainter-pace, the pass's uniforms walked in place)
    for (const nm in U) {
      const u = P.u.get(nm), v = U[nm];
      if (!u) continue;                           // optimised out
      if (SAMPLERS.has(u.type)) {
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, v.t);
        gl.uniform1i(u.loc, unit++);
        continue;
      }
      let a = v;
      if (typeof v === 'number') { ONE[0] = v; a = ONE; }
      gl[SETTER[u.type]](u.loc, a);
    }
  }
  // rows [y0, y1) of data (row-major, T's width) -> T's level 0 rows [y0 + dy, y1 + dy): ONE call
  // (the callers keep it to the class's up_bytes: planChunks)
  sub(T, data, y0, y1, dy = 0) {
    const gl = this.gl, [, fmt, type, bpp] = FMT[T.f], rl = (T.w * bpp) / data.BYTES_PER_ELEMENT;
    gl.bindTexture(gl.TEXTURE_2D, T.t);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, y0 + dy, T.w, y1 - y0, gl[fmt], gl[type], data.subarray(y0 * rl, y1 * rl));
  }
  // a buffer's bytes [o, ..) of data in calls of <= the class's buf_bytes (COPY_WRITE_BUFFER: any
  // buffer's kind) while the last piece's time still fits before dl (at least one) -> [the new offset, the worst piece ms]
  bufUp(b, data, o, dl) {
    const gl = this.gl, u8 = new Uint8Array(data.buffer, data.byteOffset, data.byteLength), n = u8.length, piece = this.caps().buf_bytes;
    if (o >= n) return [o, 0];
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, b);
    let unit = 0;
    do {
      const t0 = now(), e = Math.min(n, o + piece);
      gl.bufferSubData(gl.COPY_WRITE_BUFFER, o, u8.subarray(o, e));
      o = e;
      unit = Math.max(unit, now() - t0);
    } while (o < n && now() + unit < dl);
    gl.bindBuffer(gl.COPY_WRITE_BUFFER, null);
    return [o, unit];
  }
  // a full-target pass (rows [y0, y1) when banded)
  pass(P, fbo, w, h, U, y0 = 0, y1 = h, x0 = 0, x1 = w) {
    const gl = this.gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.viewport(0, 0, w, h);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x0, y0, x1 - x0, y1 - y0);
    this.use(P, U);
    gl.bindVertexArray(this.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.disable(gl.SCISSOR_TEST);
  }
}

export { GL as GroundGL };   // the gate's mock-GL submission row (scripts/check-gpupainter.mjs)

// ---------------------------------------------------------------- the CPU-prep worker
/* global __GG_WORKER_SRC__ */
// groundwork.js inlined by scripts/build-client.js; null: the main thread runs groundcpu.js
// itself (no bundle / Worker, a failure) — the same code, in time-sliced steps where it can be
// MADE OFF THE FRAME (ruling p6 (c)): the Worker constructor (the Blob of the inlined source, its
// object URL, new Worker: 12.6 ms in a phone-lo scan, gpupainter-p5) runs in an idle callback the
// painter's constructor asks for (ggWorkerSoon: requestIdleCallback, at most WK_IDLE_MS late; else a
// task) — never in a scan or a run: the world waits for it (_world), every other caller only reads
// it (ggWorker). Its ms are the painter's one named one-off (state().one_off['worker.new'])
let _wk;                                          // undefined: not made yet; null: none (no bundle / Worker, a failure)
let _wkAsked = false;
let _wkMs = null;                                 // the Worker constructor's ms (the one-off)
let _wkAt = null;                                 // ... made at (s since the page's start: state().t_log.worker)
const WK_IDLE_MS = 500;
// a task off the frame: an idle callback (at most WK_IDLE_MS late), else a task of its own
const idle = (fn) => (typeof requestIdleCallback === 'function' ? requestIdleCallback(fn, { timeout: WK_IDLE_MS }) : setTimeout(fn, 0));
let _serial = 0;                                  // a fetch's identity (module-wide: the worker's keys are shared by every
                                                  // painter; a re-fetched node rebuilds its buffers)
function ggWorkerMake() {
  if (_wk !== undefined) return;
  _wk = null;
  if (typeof __GG_WORKER_SRC__ !== 'undefined' && typeof Worker !== 'undefined' && typeof Blob !== 'undefined') {
    const t0 = now();
    try {
      const w = new Worker(URL.createObjectURL(new Blob([__GG_WORKER_SRC__], { type: 'text/javascript' })));
      const K = { w, id: 0, wait: new Map(), dead: false };
      w.onmessage = (e) => { const f = K.wait.get(e.data.id); if (f) { K.wait.delete(e.data.id); f(e.data); } };
      w.onerror = () => { K.dead = true; for (const f of K.wait.values()) f({ error: 'groundgen worker gone' }); K.wait.clear(); };
      _wk = K;
    } catch { _wk = null; }
    _wkMs = now() - t0;
  }
  _wkAt = Math.round(now() / 100) / 10;
  if (_inst) _inst._wkKick();
}
function ggWorkerSoon() {
  if (_wk !== undefined || _wkAsked) return;
  _wkAsked = true;
  if (typeof requestIdleCallback === 'function') requestIdleCallback(() => ggWorkerMake(), { timeout: WK_IDLE_MS });
  else setTimeout(ggWorkerMake, 0);
}
// the worker when made and alive (never makes it: ggWorkerSoon does, off the frame)
function ggWorker() {
  return _wk && !_wk.dead ? _wk : null;
}
// a request -> its handle {done, out, error}, filled when the answer lands
function ask(K, msg, transfer = []) {
  const h = { done: false, out: null, error: null }, id = ++K.id;
  K.wait.set(id, (d) => { h.done = true; h.out = d.out ?? null; h.error = d.error || null; });
  K.w.postMessage({ ...msg, id }, transfer);
  return h;
}
// ... as a promise of its out (a worker error rejects)
function askP(K, msg, transfer = []) {
  return new Promise((res, rej) => {
    const id = ++K.id;
    K.wait.set(id, (d) => (d.error ? rej(new Error(`groundgen worker: ${d.error}`)) : res(d.out ?? null)));
    K.w.postMessage({ ...msg, id }, transfer);
  });
}

// ---------------------------------------------------------------- a slot's GPU pieces (pure; its geometry: slotgeom.js)
// THE GPU COST of a pass texel (GPU_RATE units a nominal ms; calibrated per label, gpupainter-perf1b /
// perf3): the fixed ones; ways.boxh / crowns.march / crowns.sky* from the slot (slotPieces)
const COST = { heights: 120, 'heights.row': 120, 'heights.col': 120, albedo: 1500, 'ways.prep': 120, 'ways.path': 1200, apron: 100,
  'crowns.tin': 100, 'crowns.row': 80, 'crowns.col': 80, 'crowns.key': 60, 'crowns.obj': 80, light: 150, mips: 12,
  strata: 100 };                  // + the light's on a slot by the rock field (4 class + 4 cell fetches; prior, per label measured)
// the slot-made costs' scale (ruling p7 (1), the prior a label is planned at before its own measure):
// the back-to-back means over gpupainter-p5 / p6 (quest, desktop-igpu, phone-lo on the one Intel MTL
// host) put ways.boxh at ~8.5 M units a ms (x 2.4 to GPU_RATE), crowns.skyh ~13.5 (x 1.5), skyv ~14.6 (x 1.4)
const COST_K = { boxh: 2.4, skyh: 1.5, skyv: 1.4 };
// THE FINE ALBEDO (ground-paint-cost): a texel below 1 m (L0 / L1) counts the 1 m cells under its class box
// and keeps every genome octave — on desktop-igpu (gs ground-paint-cost replay c2-A) 3.4 M units a ms back to
// back at COST.albedo vs 9.9 M from L2: under RATE_LO, so its pieces were planned ~3x short and their runs
// held the run factor up. Its texel counts ALB_FINE_K x COST.albedo (both levels then plan near one rate)
const ALB_FINE_K = 3;
const albCost = (texel) => (texel < 1 ? ALB_FINE_K : 1) * COST.albedo;
// the host / luma box radii (texels) of the ways' row sums (waysTile's; WAYSLOOK's)
const waysRad = (texel) => {
  const lm = WAYSLOOK.steps.luma || null;
  return [Math.max(1, roundEven(WAYSLOOK.colour.host.radius_m / texel)), lm ? Math.max(1, roundEven(lm.radius_m / texel)) : 0];
};
// every GPU pass of slot G as its pieces (planRect at the label's rate: rateOf(label)) -> {label:
// [[y0, y1, x0, x1, units], ...], sky: {R, HH, HW, bands: [{r0, r1, k0, k1, h: pieces, v: pieces}]}}
// (_plan draws exactly these; the TIN's pieces are further split into node groups of <= cap.draws).
// fixOf(label): its fixed cost a piece (the cost model's a: ruling p8 (1)); old: the red twin's plan
// (planRows' row bands, the sky bands of >= 16 rows a whole row wide)
function slotPieces(G, cap, rateOf = () => GPU_RATE, old = false, fixOf = () => 0) {
  const PW = G.PW, cr = G.cr, out = {};
  const R = (l, n, w, per) => {
    out[l] = old ? planRows(n, w * per, rateOf(l), cap).map(([a, b]) => [a, b, 0, w, (b - a) * w * per]) : planRect(n, w, per, rateOf(l), cap, fixOf(l));
  };
  for (const l of ['heights', 'heights.row', 'heights.col']) R(l, PW, PW, COST[l]);
  R('albedo', PW, PW, albCost(G.texel));
  if (G.ways || G.q4) {                           // the 1 m field or (past it) the 4 m mass: the same three passes
    const rad = waysRad(G.texel);
    R('ways.prep', PW, PW, COST['ways.prep']); R('ways.boxh', PW, PW, (2 * rad[0] + 2 * rad[1] + 2) * COST_K.boxh); R('ways.path', PW, PW, COST['ways.path']);
  }
  if (!G.allIn) R('apron', PW, PW, COST.apron);
  if (cr.on) {
    const nx = cr.nx, nz = cr.nz;
    for (const l of ['crowns.tin', 'crowns.row', 'crowns.col', 'crowns.key', 'crowns.obj']) R(l, nz, nx, COST[l]);
    R('crowns.march', nz, nx, cr.steps.length * 3.5 + 2);
    // the sky box sums in row bands of the inner rows [la, lb): a band's rows [r0, r1) read the row
    // sums of [r0 - rmax, r1 + rmax) (clipped), made into a band-sized pair (H0, H1: inner cols x
    // HH) just before — the full-grid sums never exist at once. R rows a band: the halo at most half
    // the band's row sums (>= 4 rmax rows); its passes in pieces (planRect, H0's / FS's own coords)
    // (the reads a texel: the widest box's cells, FS_SKYH / FS_SKYV walking the nested radii once; skyv's H0
    // within the middle radius; old: the three boxes apart, the red twin's)
    const rmax = Math.max(...cr.sr), sw = cr.sr.reduce((a, r) => a + 2 * r + 1, 0), HW = cr.jb - cr.ja, nv = cr.lb - cr.la;
    const wH = old ? sw : 2 * rmax + 1, wV = old ? sw : 2 * rmax + 2 * cr.sr[1] + 2;
    const perH = wH * 2.3 * COST_K.skyh, perV = (wV * 3.6 + 54) * COST_K.skyv, rh = rateOf('crowns.skyh'), rv = rateOf('crowns.skyv');
    const Rr = old ? Math.max(16, Math.min(nv, Math.floor((BAND_MS * Math.min(rh, rv)) / (HW * (perH + perV)))))
      : Math.max(1, Math.min(nv, Math.max(16, 4 * rmax)));
    const sky = { R: Rr, HH: Math.min(nz, Rr + 2 * rmax), HW, bands: [] };
    for (let r0 = cr.la; r0 < cr.lb; r0 += Rr) {
      const r1 = Math.min(cr.lb, r0 + Rr), k0 = Math.max(0, r0 - rmax), k1 = Math.min(nz, r1 + rmax);
      sky.bands.push({ r0, r1, k0, k1,
        h: old ? [[0, k1 - k0, 0, HW, (k1 - k0) * HW * perH]] : planRect(k1 - k0, HW, perH, rh, cap, fixOf('crowns.skyh')),
        v: old ? [[0, r1 - r0, 0, HW, (r1 - r0) * HW * perV]] : planRect(r1 - r0, HW, perV, rv, cap, fixOf('crowns.skyv')) });
    }
    out.sky = sky;
  }
  R('light', SLOT_PX, SLOT_PX, COST.light + (G.str ? COST.strata : 0));
  // the mips 1 .. 9: a pass a level (its cost at the level-0 texels: the per-pass fixed cost dominates)
  out.mips = [];
  for (let l = 1; l <= Math.log2(SLOT_PX); l++) out.mips.push([0, SLOT_PX >> l, 0, SLOT_PX >> l, SLOT_PX * SLOT_PX * COST.mips]);
  return out;
}
export { geomOf, slotPieces, sunOf, COST };

// ---------------------------------------------------------------- the GenPass (one, module level)
let _inst = null, _registered = false;
const PASS = {
  id: 'ground', pool: null, params: ['ground.finest_level', 'ground.strata', 'ground.tiling'],
  key: (t) => t.key,
  need: (t) => t.sc,
  lead: (t) => t.lead,                            // 2 = a gap (vt.urgent: gain or stand; gensched's gap lead), else a near load (vt._nearLoad)
  inputs: () => [],                               // GroundGen loads them before it lists a tile
  run: (gl, job, ms) => (_inst ? _inst._run(gl, job, ms) : 'done'),
  minMs: () => (_inst ? _inst.minMs() : 0),        // the declared indivisible run (gensched banks a smaller slice)
};
function registerPass() {
  if (_registered) return;
  gen.register(PASS);
  _registered = true;
}

export class GroundGen {
  // tm: the TerrainManager (baseUrl, gm.index); meta: the bake's; vt: its GmTex; getRenderer: () => the WebGLRenderer
  constructor(tm, meta, vt, getRenderer) {
    this.tm = tm;
    this.meta = meta;
    this.vt = vt;
    this.getRenderer = getRenderer;
    this.base = tm.baseUrl;
    this.surf = null;
    this.cache = new Map();                       // asset id -> {st, data, bytes, at}
    this.cacheBytes = 0;
    this.loads = 0;
    this.loadsB = 0;
    this.held = new Map();                        // key -> listed again after
    this.geo = new Map();                         // key -> its input ids (pure of the key)
    this.done = new Map();                        // key -> {ms, cpu, steps}
    this.fails = 0;
    this.fatal = null;
    this.g = null;                                // GL (warm-up)
    this.P = null;                                // the programs
    this.world = null;                            // the per-world constants (_prepWorld)
    this._wp = null;                              // its staged preparation
    this._job = null;                             // {job, key, st}
    this.finest = 0;
    this.stats = { jobs: 0, committed: 0, dropped: 0, abandoned: 0, held: 0, failed: 0, gpu_ms_est: 0, max_run_ms: 0, over_runs: 0, over_ms: 0, yields: 0,
      over_cpu: 0, over_cpu_max: 0, over_gl: 0, over_gl_max: 0, capped: 0, scan_cut: 0, evict_cut: 0, refetch: 0, escalate: 0, evict_ref: 0,
      // first-minute-measure: commits of a key already painted this page (repaint), resident keys released by a
      // finest-level move (fin_drop; the tier rule's latch → play, a Map in the game)
      repaint: 0, fin_drop: 0,
      // ground-paint-coverage: scans whose BLOCKED key freed referenced inputs placed after it (blk_gap: of them
      // GAP FIRST's, a ready key behind it)
      blocked: 0, blk_gap: 0 };
    this._painted = new Set();                    // every key committed this page (stats.repaint)
    this._pinB = new Set();                       // _evict's pinned ids (reused)
    this._glTot = 0;                              // every GL call's ms (a run's GL share: runGl)
    this._rid = 0;                                // a run's id (its timer queries: the GPU ms per RUN)
    this._runG = new Map();                       // run id -> {ms, b2b, left, ended, est, J, bad}
    this._gpuRuns = [];                           // the last GPU_RUNS runs' [timer ms, b2b ms, est ms, lead ms, cap ms, hidden]
    // every run's GPU maxima, hidden / shown (sched hidden): ms = the run's step queries (its lead query
    // apart), b2b = all but its first step's, lead = the lead query (the frame's queued work), est the
    // planner's (x the run factor); over = runs whose ms passed their gpu_ms cap (over_b2b: whose b2b
    // did), over8 = past 8 ms
    // (ruling p8 (3)) a run of ONE timed step is LEAD-ONLY: its one span holds the frame's queued work, no
    // back-to-back sample — not scored as a painter over (lead_only, lead_only_over: its ms past the cap);
    // the scored overs are over_b2b (a run's b2b past its cap; over_atomic: of runs holding a named atomic
    // step past the cap)
    const gm = () => ({ n: 0, max_ms: 0, max_b2b: 0, max_lead: 0, max_est: 0, over8: 0, over: 0, over_b2b: 0, over_atomic: 0, lead_only: 0, lead_only_over: 0, scored: 0, cap: 0 });
    this._gpuMax = { hidden: gm(), shown: gm() };
    const rk = () => ({ r: new Float64Array(RUNK_N), at: new Float64Array(RUNK_N), n: 0, t: 0, k: 1, max: 1, pre: 0 });
    this._runK = { hidden: rk(), shown: rk() };   // the run factor per mode (RUNK)
    this.fin = 0;                                 // the finest level the scan holds the want to (finestFor)
    this._finLog = [];                            // its changes [t s, level, phase]
    // the per-run submission maxima (all runs; play / beam apart: a beam run's caps are x BEAM_K), the
    // runs past their phase's caps (over: the last 8 in overs, the step labels), past its GPU estimate (over_est)
    const sb = () => ({ runs: 0, tex: 0, buf: 0, draws: 0, units: 0, est: 0 });
    this._sub = { tex: 0, buf: 0, calls: 0, draws: 0, units: 0, est: 0, runs: 0, over: 0, over_est: 0, worst: null, overs: [], play: sb(), beam: sb() };
    this._subB = { n: 0, units: 0, draws: 0, est: 0, untimed: false };   // a run's submissions (reused)
    this._ranB = [];                              // a run's steps (label, ms; reused)
    this._pr = new Float64Array(9);               // packNext's portion (reused)
    // the steps a fresh run admitted past its cap (packNext OVER: atomic, or a rect step's fixed cost
    // alone) by name: {n, max_est}
    this._atomic = { n: 0, by: {} };
    this._aPool = { a: A_PRIOR, v: 0, m: { ...PRIOR_M } };   // the pooled fixed cost (_model): its value, version, the prior model at it
    // run.state / run.reset calls > STALL_MS: n, max ms, the GL ms inside (run.state's timed calls;
    // three's resetState is untimed), the last few {k, ms, gl, call, at}
    this._stalls = { state: { n: 0, max: 0, gl: 0 }, reset: { n: 0, max: 0, gl: 0 }, last: [] };
    this._idleAt = 0;                             // the last run's end (the scratch pool's idle release)
    this._nodes = new Map();                      // node id -> its GPU buffers {d, vao, vb, ib, count, yn, bytes} (LRU, the class's node_mb)
    this._nodeBytes = 0;
    this._nodePin = new Set();                    // the current job's node ids (never evicted under it)
    this._clsMap = new WeakMap();                 // decoded class tile -> its mapped cells (_clsTile)
    this._clsNone = new Map();                    // absent class tile 'tx_ty' -> the none (+ sheet) cells
    this._waysMap = new WeakMap();                // decoded ways tile -> its interleaved 8-byte cells (_waysCells)
    this._clsPart = new Map();                    // 'tx_ty' -> a class tile being mapped {d, m, r} (sliced)
    this._waysPart = new Map();                   // 'tx_ty' -> a ways tile being interleaved {d, c, r}
    this.prof = new Map();                        // label -> {n, ms, max, gn, gms, gc} (CPU ms; timer-query GPU ms + cost units)
    this._tq = [];                                // pending timer queries {q, label, cost, b2b}
    this._warmMs = 0;
    this._scanMs = { n: 0, ms: 0, max: 0, charged: 0 };
    this._rdyGen = 0;                             // bumped at every input cache change (the scan's ready memo: G.rg / G.rd)
    this._warmWorst = 0;                          // the warm-up's worst task ms (its tasks a scan: WARM_MS)
    this._warmTop = [];                           // the slowest warm-up tasks [name, ms] (state().warm_top)
    this._glc = new Map();                        // GL call -> {n, ms, max} (timedGL)
    this._label = 'warm';                         // the step / scan part the GL calls count to
    this._stepGl = 0;                             // the GL ms of the step call running
    this._slowGl = null;                          // its slowest GL call [name, ms]
    this._slow = [];                              // the last steps over SLOW_MS {label, ms, gl_ms, call, at}
    this._slowCalls = [];                         // the last GL calls over SLOW_GL_MS {call, ms, label, at}
    this._unitMs = null;                          // a sliced step's last slice ms (the label's CPU unit)
    this._trace = [];                             // every TRACE_MS (state().trace)
    this._traceAt = 0;
    this._crIx = new WeakMap();                   // a crown family's tile list -> its set (i * 65536 + j: _inputs)
    this._tl = {};                                // the start-up timeline: event -> s since the page's start, first time (state().t_log)
    this._tlm('ctor');
    this._oversB = [];                            // the last scored back-to-back overs with their timed steps (state().overs_b2b)
    this._preC = [];                              // scored overs' preemption candidates {label, ms, est, ex, b2b, cap, hid} (PREEMPTED (i) + (ii))
    this._ratioL = new Map();                     // label -> its back-to-back measured / estimate ring {a, n, i} (PREEMPTED (iii))
    // a label's median ratio over its ring / its last PRE_REC_N (learnPre)
    this._medL = (l) => { const L = this._ratioL.get(l); return L ? ringMed(L.a, L.n) : Infinity; };
    this._medR = (l) => { const L = this._ratioL.get(l); return L ? ringRecent(L) : Infinity; };
    this._touched = new Set();                    // pool layers ('p:slot') a job has written (a step's first-touch flag)
    this._pre = new Map();                        // key -> its crown discs asked ahead (PRE_DISCS) {h, at}
    this._overs = [];                             // the last over-slice runs {key, at, slice, run, partial, steps}
    this._gat = new Set();                        // the gather set (gatherPick): keys whose inputs stay pinned
    this._gatSeen = new Set();                    // gatherPrune's reused want set
    this._gatRdy = (k) => { const ids = this._inputs(k); return !!ids && this._readyM(k, ids); };
    this._refs = new Map();                       // input id -> the wanted keys (+ the job's) reading it (refSync)
    this._refK = new Map();                       // key -> its ids, counted in _refs
    this._refSeen = new Set();                    // refSync's reused want set
    this._refBytes = 0;                           // the cached (in) bytes of referenced inputs
    this._refIds = (k) => { const G = this.geo.get(k); return G ? G.ids || null : null; };
    this._have = (id) => { const e = this.cache.get(id); return !!e && e.st !== 'err'; };
    this._cands = [];                             // the scan's admissible keys (gatherBest; reused)
    this._refAddF = (k, ids) => this._refAdd(k, ids);
    this._refDelF = (k) => this._refDel(k);
    this._fetchN = new Map();                     // input id -> its fetches (stats.refetch: an input fetched again)
    this._probePin = new Set();                   // ensure(): the probe's checked slots' inputs (never evicted until cleared)
    // THE SHIPPED SLOTS' hit rule (_shipNow: on, the gensurf it was made from, its generation — a key's ids are made again
    // when it moves), the keys that missed (absent / failed: painted), the EWMAs (THE EXPECTED TIME) and counts
    this.painterKey = PAINTER_KEY;                // this build's painter (__PAINTER_KEY__; the gate's stand-ins set their own)
    this._ship = { on: false, raw: undefined, gen: 0, miss: new Set(), fetchMs: null, paintMs: SHIP_PAINT_MS, slow: false, probeAt: 0,
      fetched: 0, committed: 0, missed: 0, painted: 0, flips: 0 };
    const s = meta.surface;
    if (!s || !s.tiles || !s.ways) { this.fatal = 'meta.surface (tiles + ways) missing: no ground painter'; console.error('groundgen:', this.fatal); }
    _inst = this;
    registerPass();
    ggWorkerSoon();                               // the worker, made in an idle callback (off the frame, before the world needs it)
    // THE START-UP (gs-gpupainter-pace item 1): the world's downloads start here (each in a task of its own),
    // the worker's init + genome solve as soon as the worker and genome.json are there (_wkKick) — no GL
    // before heavyOk; until the painter is ready the scan runs every frame (vt.onTick), not every SCAN_MS
    if (!this.fatal) this._worldPrime();
    vt.onScan = (t) => this.scan(t);
    vt.onTick = (t) => { if (!this.fatal && (!(this.P && this.P.ok) || (this._more && t - this._wantAt >= CONT_MS))) this.scan(t); };
    this._look(genLive());
    onGenChange((kinds, live) => this._look(live));
  }

  _look(live) {
    const L = (live && live.look && live.look.ground) || {};
    this.vt.uniforms.uVtLook.value.set(L.gain ?? 1, L.sat ?? 1);
    this.finest = (live && live.ground && live.ground.finest_level) | 0;
    this.strata = clamp(+((live && live.ground && live.ground.strata) || 0), 0, 1);
    this.tiling = !!(live && live.ground && live.ground.tiling);
  }
  // the albedo pass's tiling uniforms at slot G (groundref tileTexel: the level by the texel, the
  // lattice of the slot origin split integer + fraction in float64); ground.tiling off: none tiled
  _tileU(G) {
    const W = this.world, R = [-1, -1, -1, -1], I = [], S = [0, 0, 0, 0], P = [], O = [], F = [];
    const TL = this.tiling && W.tiles ? [...W.tiles] : [];
    for (let t = 0; t < TILE_MAX; t++) {
      const e = TL[t];
      if (!e) { I.push(1, 0, 0, 0); P.push(1, 0, 0, 1); O.push(0, 0, 0, 0); F.push(0, 0, 0, 0); continue; }
      const [k, E] = e, l = tileLevel(E, G.texel), pl = E.pxM * (1 << l), Sm = TILE.CELL * E.extent, margin = Sm + pl;
      const sx = (G.x0 - G.z0 / Math.sqrt(3)) / Sm, sy = (2 * G.z0) / (Math.sqrt(3) * Sm), ox = Math.floor(sx), oy = Math.floor(sy);
      R[t] = W.recRow.get(k);
      S[t] = E.seed;
      I.push(E.n >> l, E.offs[l], E.lRow + l, 0);
      P.push(pl, margin, Math.max(0, E.extent - 2 * margin), Sm);
      O.push(ox, oy, 0, 0);
      F.push(sx - ox, sy - oy, 0, 0);
    }
    return { uExR: R, uExI: I, uExS: S, uExP: P, uExO: O, uExF: F };
  }

  setSurface(surf) { this.surf = surf; }

  // ---------------------------------------------------------------- the scan (GmTex SCAN_MS)
  // its ms are charged to the gen budget (gensched charge: off the next frames' slices)
  scan(t) {
    const t0 = now();
    this._scanN = (this._scanN || 0) + 1;
    try { return this._scan(t); } finally {
      if (t0 - this._traceAt >= TRACE_MS) { this._traceAt = t0; this._part('scan.trace', () => this._sample(t0)); }
      const dt = now() - t0, S = this._scanMs;
      S.n++; S.ms += dt; if (dt > S.max) S.max = dt;
      gen.charge(dt);
      S.charged += dt;
    }
  }
  // a scan part's CPU under its own label (state().steps 'scan.*'; its GL calls count to it)
  // a start-up event's first time (state().t_log: s since the page's start)
  _tlm(ev) { if (!(ev in this._tl)) this._tl[ev] = Math.round(now() / 100) / 10; }
  _part(label, fn) {
    const t0 = now(), l0 = this._label;
    this._label = label;
    try { return fn(); } finally { this._label = l0; this._acc(label, now() - t0); }
  }
  // the world, then the warm-up: one sliced part a scan (each to PART_MS / WARM_MS; hidden x SETUP_HID_K)
  // (the programs need only the ways look: they compile while the worker solves the genome) -> both done
  _setup(r, t) {
    const tW = now(), wd = this._world(t);
    if (this._wp && this._wp.ST0 && (wd || now() - tW < PART_MIN_MS) && !this._part('scan.warm', () => this._warm(r))) return false;
    return wd;
  }
  // THE MAP SET-UP (owner ruling 2026-10-24, beam-order F3; main.js mapLoop): from the player's first
  // touch of the Map, before the quiet start ends (heavyOk), the set-up's parts alone a Map frame at the
  // shown pace — no want scan, no pool, no paint; the scan takes over from heavyOk
  prep() {
    if (this.fatal || heavyOk() || (this.P && this.P.ok)) return;
    const r = this.getRenderer && this.getRenderer();
    if (!r) return;
    this._tlm('setup');
    const t0 = now();
    try {
      if (this._setup(r, t0)) this._tlm('setup.done');
    } catch (e) {
      this.fatal = String((e && e.message) || e);
      console.error('groundgen:', this.fatal);
    } finally { this._acc('map.setup', now() - t0); }
  }
  _scan(t) {
    const tS = now();
    if (this.fatal) return this._failAll(t);
    const r = this.getRenderer && this.getRenderer();
    if (!r || !heavyOk()) return;
    this._tlm('heavy');
    try {
      if (!this._setup(r, t)) return;
    } catch (e) {
      this.fatal = String((e && e.message) || e);
      console.error('groundgen:', this.fatal);
      return this._failAll(t);
    }
    const vt = this.vt;
    // the finest level (the software tier's ground.finest_level; phone-lo's / quest's / igpu's raised to its
    // LATCH_FINEST until the latch: the TIER RULE, finestFor): nothing below it is wanted, waited
    // for, or kept
    const fin = (this.fin = finestFor(deviceClass(), this.finest, phase(), this.tm._nearLevels ?? 2));
    vt.viewOn = phase() === 'play';   // THE VIEW ORDER (gmtex vtScore): in play only, the latch order the plain one
    if (vt.minLevel !== fin) {
      vt.minLevel = fin;
      this._finLog.push([Math.round(t / 100) / 10, fin, phase()]);
      if (this._finLog.length > 16) this._finLog.shift();
      for (const key of [...vt.res.keys()]) if (+key.split('_')[0] < fin) { vt.release(key); this.stats.fin_drop++; }
    }
    // a job the queue dropped (unlisted, a regen param moved): its reservation ends here
    if (this._job && (!gen._cur || gen._cur.job !== this._job.job)) this._abandon('dropped');
    for (const [k, h] of this.held) if (h <= t) this.held.delete(k);
    const tiles = [], gat = this._gat;
    const cur = this._job && this._job.key;
    if (cur) {
      const sc = vt.score(cur);
      if (sc < Infinity) tiles.push({ key: cur, sc, lead: vt.urgent(cur) ? 2 : vt._nearLoad(cur) });
    }
    // from the Beam to the latch only what the latch waits on loads: its own tiles and the top
    // two levels (the ground tree draws nothing until a node's texture is resident at its level
    // or above: gpupainter-p2-5, the tree's wait all 'w_none' with every latch tile in)
    const beam = phase() === 'beam';
    // THE WHOLE WANT to WANT_MS (gs-gpupainter-pace item 4: gpupainter-p8 cold pose, scan.want 3.2-6.7 ms):
    // the list to WANT_MS less the tail's WANT_TAIL_MS, the discs only inside it; a scan cut short runs
    // again CONT_MS on (vt.onTick), not a SCAN_MS later
    const tWant = (this._wantAt = now()), dlW = tWant + WANT_MS;
    this._more = false;
    this._part('scan.want', () => {
      const W = this._part('scan.want.rank', () => vt.wanted(t));
      // INCREMENTAL: a key's inputs (geomOf + its ids) are made once (the geo cache), its ready
      // state memoised against the cache's generation (_rdyGen); the keys a scan looks at are held
      // to the deadline (PART_MS, the scan's own time so far within SCAN_PART_MS), checked at EVERY
      // key, and _load stops between fetches at it (a fetch starts while its worst recent sync ms
      // fits: 'scan.fetch'; gpupainter-p5: up to 8 fetches a scan, the deadline every 8 keys —
      // scan.want.list 5-10 ms) — the wanted order (near first, so the latch's tiles lead), the
      // rest next scan. Over the class's cache_mb no new tile's inputs load (but the latch's in the
      // beam): THE GATHER SET (gatherPick) — the listed + gathering keys, every scan, cut or not — is kept
      // (_evict), a load would only evict another wanted tile's
      this._part('scan.want.list', () => {
        const t0 = now();
        refSync(this._refK, W, this._refSeen, cur, this._refIds, this._refAddF, this._refDelF);
        // over: the referenced bytes (the unreferenced ones go at this scan's _evict)
        const over = this._refBytes > caps().cache_mb * 1e6;
        let gath = gatherPrune(gat, W, this._gatSeen, this._gatRdy);
        const dl = listDl(t0, tS, tWant);
        let pre = 0, seen = 0, nm = 0, kc = false, lcut = false, blk = -1, rdy0 = -1;
        const cands = this._cands;
        cands.length = 0;
        for (const c of W) {
          if (tiles.length >= LIST_MAX) break;
          if (seen++ && now() > dl) { this.stats.scan_cut++; this._more = true; break; }
          const key = c[0];
          if (key === cur || this.held.has(key) || (fin && +key.split('_')[0] < fin)) continue;
          // a key first seen: its geometry (geomOf: 0.1-0.9 ms, 'scan.geom'), then its input ids
          // ('scan.key') — each started while its worst recent cost still fits (freshStep: THE KNOWN TAIL;
          // the scan's first key always; gpupainter-p8 cold pose, the two in one were scan.key 1.8-4 ms)
          // (a unit cut 2 scans running without a fresh key made: the next scan makes one — a unit past the
          // window would starve the list, its worst recent cost never sampled again)
          const G0 = this.geo.get(key), fresh = !G0 || !G0.ids, force = this._keyCut >= 2 && !nm;
          let st = 0;
          if (!G0) {
            if (!force && seen > 1 && (st = freshStep(now(), this._unit('scan.geom'), dl, kc))) {
              if (!kc) { this.stats.scan_cut++; this._more = true; kc = true; }
              if (st === 2) break;
              continue;
            }
            const tg = now();
            this._geom(key);
            this._acc('scan.geom', now() - tg);
          }
          if (fresh && !force && (seen > 1 || !G0) && (st = freshStep(now(), this._unit('scan.key'), dl, kc))) {
            if (!kc) { this.stats.scan_cut++; this._more = true; kc = true; }
            if (st === 2) break;
            continue;
          }
          const tk = fresh ? now() : 0, ids = this._inputs(key);
          if (fresh) nm++;
          if (fresh && ids) this._acc('scan.key', now() - tk);
          if (!ids) continue;
          if (!this._refK.has(key)) this._refAdd(key, ids);
          if (this._readyM(key, ids)) { gatherPick(gat, key, true); if (rdy0 < 0) rdy0 = seen - 1; tiles.push({ key, sc: c[3], lead: vt.urgent(key) ? 2 : vt._nearLoad(key) }); continue; }
          // THE FLOOR (beam and play: candOf): the latch's own tiles past the caps
          const lt = this._latchTile(key);
          if (beam && !lt && +key.split('_')[0] < vt.top - 1) continue;
          // (in the beam only the latch's own tiles and the top two levels get here: none waits on a gathering one)
          // in play a new key is a candidate (LOCALITY: gatherBest after the walk)
          // (BLOCKED: the first one only the referenced bytes keep out — its place in W, seen - 1)
          if (candOf(beam, gat.has(key), lt)) {
            if (cands.length < ADMIT_K && pre < PREFETCH && !over && gath < GATHER_N) cands.push([key, ids, vt._nearLoad(key), vt.urgent(key)]);
            else if (over && blk < 0 && pre < PREFETCH) blk = seen - 1;
            continue;
          }
          const pick = gatherPick(gat, key, false, over, lt, pre, beam ? 0 : gath);
          if (!pick) continue;
          pre++;
          if (pick === 3) gath++;
          if (this._load(ids, t, beam || vt._nearLoad(key) ? 'near' : 'bulk', dl) === false) { this.stats.scan_cut++; this._more = true; lcut = true; break; }
        }
        // up to GATHER_N gathering: each the candidate with the fewest inputs not in (a pick's own loads count)
        while (cands.length && !lcut) {
          const b = gatherBest(cands, this._have);
          cands.splice(cands.indexOf(b), 1);
          if (gatherPick(gat, b[0], false, over, false, pre, gath) !== 3) break;
          pre++; gath++;
          if (this._load(b[1], t, b[2] ? 'near' : 'bulk', dl) === false) { this.stats.scan_cut++; this._more = true; lcut = true; }
        }
        cands.length = 0;
        this._keyCut = kc && !nm ? (this._keyCut || 0) + 1 : 0;
        // stuck: the referenced inputs past cache_mb, nothing gathering and nothing ready behind the job
        // (_evict frees the worst-needed referenced ones for the best key: one key gathers while the job
        // paints — requiring no job too serialised paint -> evict -> fetch -> paint, ~1 slot listed).
        // BLOCKED (ground-paint-coverage; f1 replays, igpu: ref_mb = cache_mb 128-140 MB, loads 0, 131-141
        // on-screen keys never admitted — the scan cut 274 of 366 times, so the stuck rule, which needs a
        // whole scan, never held): a scan cut short with a key the referenced bytes alone keep out, nothing
        // gathering and nothing ready behind the job frees the referenced inputs whose first reader is WORSE
        // placed than that key (refNeed > blk; Belady) — never one it or a key before it reads, so the order
        // it was admitted in cannot ping-pong (a key gathering or ready: it lands / paints first, the shared
        // inputs stay — each fetched once)
        // GAP FIRST (gapFirst): a blocked key that clears a gap frees its room with ready keys behind it too
        const idle = over && !beam && !gath && !tiles.some((x) => x.key !== cur);
        const gapB = !idle && gapFirst(over, beam, gath, blk, rdy0, blk >= 0 && vt.urgent(W[blk][0]));
        const cutB = (idle && this._more && blk >= 0) || gapB, stuck = (idle && (!this._more || cutB)) || gapB;
        this._stuck = stuck ? W : null;
        this._blk = cutB ? blk : -1;
        if (cutB) this.stats.blocked++;
        if (gapB) this.stats.blk_gap++;
      });
      this._part('scan.want.queue', () => { gen.want('ground', tiles, null); });
      this._listed = tiles.length;
      this._tlm('scan1');
      if (tiles.length) this._tlm('listed1');
      // the crown discs of the best listed slots, made in the worker ahead of their jobs
      const K = ggWorker();
      if (K && !discsOk(now(), dlW)) this._more = true;
      else if (K) {
        this._part('scan.want.discs', () => {
          let best0 = null, best1 = null;
          for (const x of tiles) {
            if (x.key === cur) continue;
            if (!best0 || x.sc < best0.sc) { best1 = best0; best0 = x; } else if (!best1 || x.sc < best1.sc) best1 = x;
          }
          for (const x of PRE_DISCS >= 2 ? [best0, best1] : [best0]) if (x && !this._pre.has(x.key) && !this._geom(x.key).ship) this._discsAsk(K, x.key, t);
          for (const [k, P] of this._pre) if (t - P.at > 30e3) this._pre.delete(k);
          while (this._pre.size > PRE_DISCS + 2) this._pre.delete(this._pre.keys().next().value);
        });
      }
      this._part('scan.want.evict', () => this._evict(gat, evictDl(now(), dlW), this._stuck, this._blk));
      this._stuck = null;
    });
    // idle: the scratch pool goes (the next job allocates again)
    if (!this._job && this.g && this.g.poolBytes && t - this._idleAt > caps().idle_ms) this._part('scan.drain', () => this.g.drain());
  }

  // a trace sample (state().trace): s since the page's start, the sched phase, the gen queue's
  // debt now / summed (all passes) + its runs, this painter's over-slice runs + their ms over,
  // the slots committed, the near set's missing latch tiles (main.js latchState's count) and
  // terrain.nearReady, the held GPU MB (scratch + pool + nodes), the input cache MB, the tiles
  // listed, its loads in flight, the queue's fetches in flight and the ground tree's near wait
  // by its blocking stage (groundtree.js _waitOf: none net dec tex vt job, s since the last jump),
  // sched hidden (1: the overlay up) and the finest level the scan holds (finestFor)
  // (the queue's own fields, not gen.state(): that walks every tile of every pass)
  _sample(t) {
    const q = { debt: gen._debt, debt_ms: gen.stats.debt_ms, runs: gen.stats.runs }, vt = this.vt, S = (this.tm.gm && this.tm.gm.stats) || {};
    let miss = 0;
    let lt = 0;
    for (const [key] of vt.want.naked) if (this._latchTile(key)) { lt++; if (!vt.res.has(key) && !vt.failed.has(key)) miss++; }
    if (lt && !miss) this._tlm('latch.tiles');
    if (this.tm.nearReady) this._tlm('near');
    if (phase() === 'play') this._tlm('play');
    if (hidden()) this._tlm('hidden'); else if ('hidden' in this._tl) this._tlm('shown');
    const g = this.g, r1 = (v) => Math.round(v * 10) / 10;
    this._trace.push([r1(t / 1000), phase(), r1(q.debt), r1(q.debt_ms), q.runs, this.stats.over_runs, r1(this.stats.over_ms),
      this.stats.committed, miss, this.tm.nearReady ? 1 : 0, g ? r1((g.bytes + g.poolBytes + this._nodeBytes) / 1e6) : 0, r1(this.cacheBytes / 1e6),
      this._listed ?? -1, this.loads, gen._fetching ?? -1,
      ['w_none', 'w_net', 'w_dec', 'w_tex', 'w_vt', 'w_job'].map((k) => r1((S[k] || 0) / 1000)), hidden() ? 1 : 0, this.fin,
      gen.debtOf === PASS.id ? r1(q.debt) : 0]);
    if (this._trace.length > 900) this._trace.splice(0, this._trace.length - 900);
  }

  // a tile the beam-in latch waits on (gmtex latchKey, nearReady's test)
  _latchTile(key) { return this.vt.latchKey(key); }

  // a fatal start (no float targets, a broken bake): every wanted tile backs off as failed, so the
  // near latch never waits for a texture that cannot come
  _failAll(now) {
    for (const [key] of this.vt.wanted(now)) this.vt.release(key, true);
  }

  // ---------------------------------------------------------------- the world (once, staged per scan)
  // (sliced: one part a scan, each under its own label — scan.world.fetch / .genome / .ways / .res /
  // .prep; gpupainter-p4-3: the whole prep in one scan was 44 ms on quest)
  // (the fetches one a scan: gpupainter-p5 quest, the world's ~6 fetches started in one scan were
  // 12.7 ms; the worker made first — ggWorkerSoon's idle callback —, so no scan makes it)
  _world(now) {
    if (this.world) return true;
    if (_wk === undefined) { ggWorkerSoon(); return false; }
    const M = this.meta, s = M.surface;
    if (!this._wp) return this._part('scan.world.fetch', () => this._worldFetch(M, s));
    const st = this._wp;
    if (st.fq.length) return this._part('scan.world.fetch', () => { this._soon(st.fq.shift(), 'fetch.world'); return false; });
    this._wkKick();
    if (st.err) throw new Error(`genome.json: ${st.err.message || st.err}`);
    // the ways look first (the programs wait for it alone), then the inputs, the genome
    if (!st.ST0) return this._part('scan.world.ways', () => this._worldWays(st, M, s)) && false;
    if (!st.genome || !st.water || st.res === null || st.s2 === null || st.rf === null || st.ex === null) return false;
    this._tlm('w.inputs');
    if (!(st.gN >= Object.keys(st.genome.classes).length)) return this._part('scan.world.genome', () => this._worldGenome(st)) && false;
    this._tlm('w.genome');
    if (!st.tiles) return this._part('scan.world.tiles', () => this._worldTiles(st)) && false;
    if (st.codes === undefined || st.rk < st.rn) return this._part('scan.world.res', () => this._worldRes(st, M, s)) && false;
    if (!st.sheets) return this._part('scan.world.sheets', () => { st.sheets = sheetsOf(st.water); return false; });
    // (the class table a part of its own: gpupainter-p8 phone-lo cold pose, scan.world.prep 2.1 ms)
    if (!st.cls) return this._part('scan.world.cls', () => { st.cls = this._prepCls(st); return false; });
    this.world = this._part('scan.world.prep', () => this._prepWorld(st));
    this._tlm('world');
    return true;
  }
  // the world's fetches as a queue (st.fq; this scan makes it), started one a scan (_world), each in a
  // task of its own (_soon 'fetch.world': gpupainter-p6 phone-lo, the queue + the first fetch in one
  // scan part were 4.2 ms)
  // the world's downloads at the painter's construction: every one queued, each started in a task of its own
  _worldPrime() {
    const M = this.meta, s = M.surface;
    this._worldFetch(M, s);
    for (const f of this._wp.fq.splice(0)) this._soon(f, 'fetch.world');
  }
  // the worker's requests as soon as each can go (its init: the ways look + crowns' constants; the genome
  // solve once genome.json is in) — from the worker's making, genome.json's landing and every world scan
  _wkKick() {
    const st = this._wp, K = ggWorker();
    if (!st || !K || st.err) return;
    if (!st.iq) st.iq = ask(K, this._initMsg(st));
    if (st.genome && !st.gq && !st.gP) st.gq = ask(K, { op: 'genome', recs: st.genome.classes, texels: TEXELS });
  }
  _crownC(st) {
    const M = this.meta;
    return (st.crownC = st.crownC || { species: M.vegetation && M.vegetation.species, appearHist: (M.trees && M.trees.appear_hist) || [], treelineM: M.bands.treeline_m });
  }
  _initMsg(st) {
    const M = this.meta, s = M.surface, bands = M.bands;
    this._crownC(st);
    return { op: 'init', reply: true, ids: st.ids, noneId: s.none_id, treelineM: bands.treeline_m, empty: Array.from(s.ways.empty), look: WAYSLOOK, marks: MARKS_IN, pal: PAL.palette, crownC: st.crownC };
  }
  _worldFetch(M, s) {
    const classes = s.classes || [], ids = {};
    for (const c of classes) ids[c.key] = c.id;
    const st = { ids, docs: 0, fq: [], ex: null }, fq = st.fq;
    this._wp = st;
    this._tlm('w.fq');
    // 'near', every slot's input (fetchJsonOnce is 'bulk': held from the Beam to the latch,
    // which waits on the near slots — gpupainter-perf1b..3: every pose released by timeout)
    fq.push(() => fetchAsset(`${this.base}/${s.genome.file}`, { prio: 'near', as: 'json' }).then((g) => { st.genome = g; this._tlm('w.genome.in'); this._wkKick(); this._worldEx(st); }, (e) => { st.err = e; }));
    const wf = M.water && M.water.file ? M.water.file : 'water.json';
    fq.push(() => fetchAsset(`${this.base}/${wf}`, { prio: 'near', as: 'json' }).then((w) => { st.water = w || { lakes: [] }; }, () => { st.water = { lakes: [] }; }));
    const R = s.residual;
    st.res = R ? null : false;
    if (R) {
      const n = (R.nx || 1) * (R.ny || 1), tiles = [];
      let left = n;
      for (let ty = 0; ty < (R.ny || 1); ty++) for (let tx = 0; tx < (R.nx || 1); tx++) {
        fq.push(() => decodeImage(`${this.base}/${R.dir}/${tx}_${ty}.${R.ext}`, 3, 'near').then((r) => {
          tiles.push({ tx, ty, r });
          if (--left === 0) { st.res = tiles; this._tlm('w.res.in'); }
        }));
      }
    }
    // s2col (C5, one image; 0 0 0 = none), when the bake measured one
    const S2 = s.s2col && s.s2col.file ? s.s2col : null;
    st.s2 = S2 ? null : false;
    if (S2) {
      fq.push(() => decodeImage(`${this.base}/${S2.file}`, 3, 'near').then((r) => {
        const bb = M.bbox;
        st.s2 = r.status === 200 ? { img: r.data, w: r.w, h: r.h, cell: S2.cell_m, x0: S2.origin[0] - bb.e0, z0: bb.n1 - S2.origin[1] } : false;
      }));
    }
    // the rock field (C7, one image, cell 0 0 0 = no strata), when the tier measured one (gs-family-rock)
    const RF = s.rockfield && s.rockfield.file ? s.rockfield : null;
    st.rf = RF ? null : false;
    if (RF) {
      if (RF.ver !== 1) throw new Error(`rockfield ver ${RF.ver}: this client reads ver 1`);
      fq.push(() => decodeImage(`${this.base}/${RF.file}`, 3, 'near').then((r) => {
        st.rf = r.status === 200 && r.w === RF.w && r.h === RF.h ? { img: r.data } : false;
      }));
    }
    return false;
  }
  // the genome's exemplars of the tiled family (gs-family-wet-gravel; C4 WebP, ~4 KB each), queued once
  // genome.json names them -> st.ex {key: {img, n, pxM}} (a failed one: that record keeps the genome T)
  _worldEx(st) {
    const recs = st.genome.classes || {}, keys = TILE_KEYS.filter((k) => recs[k] && recs[k].exemplar && recs[k].exemplar.file), ex = {};
    let left = keys.length;
    if (!left) { st.ex = ex; return; }
    for (const k of keys) {
      const E = recs[k].exemplar;
      st.fq.push(() => decodeImage(`${this.base}/${E.file}`, 3, 'near').then((r) => {
        if (r.status === 200 && r.w === E.px && r.h === E.px) ex[k] = { img: r.data, n: r.w, pxM: E.px_m };
        if (--left === 0) st.ex = ex;
      }));
    }
  }
  // groundref tilePrepSteps, one step a scan part (R8 M5b) -> st.tiles Map key -> prep (+ its rows in tExG / tExL)
  _worldTiles(st) {
    if (!st.tq) { st.tq = Object.keys(st.ex).slice(0, TILE_MAX); st.tm = new Map(); st.gRows = 0; st.lRows = 0; }
    if (!st.tit && st.tq.length) { const k = st.tq[0], e = st.ex[k]; st.tit = tilePrepSteps(e.img, e.n, e.pxM, k); }
    if (st.tit) {
      const s = st.tit.next();
      if (!s.done) return false;
      st.tit = null;
      const k = st.tq.shift(), E = s.value;
      E.gRow = st.gRows; E.lRow = st.lRows;
      st.lRows += E.levels;
      E.offs = [];
      for (let l = 0, o = E.gRow; l < E.levels; o += E.n >> l, l++) E.offs.push(o);
      st.gRows += 2 * E.n - 1;
      st.tm.set(k, E);
    }
    if (!st.tq.length) st.tiles = st.tm;
    return false;
  }
  _worldGenome(st) {
    if (st.genome.ver !== 1) throw new Error(`genome.json ver ${st.genome.ver}: this client paints ver 1`);
    // the genome records' texture params (an iterative solve each) + their constants at every
    // level's texel: in the worker (one request), else on the main thread one record per scan
    // (the constants then at a slot's first use)
    const recs = st.genome.classes, K = ggWorker();
    if (K && !st.gP) {
      if (!st.gq) st.gq = ask(K, { op: 'genome', recs, texels: TEXELS });
      if (!st.gq.done) return false;
      if (st.gq.error) { console.warn('groundgen worker:', st.gq.error, '- the main thread solves the genome'); K.dead = true; }
      else {
        // the worker's answer as maps, records to PART_MS a scan (pace-final phone-lo: all in one, 6.3 ms)
        if (!st.gW) { st.gW = new Map(); st.gC = new Map(); }
        const t0 = now();
        for (const k of Object.keys(recs)) {
          if (st.gW.has(k)) continue;
          const o = st.gq.out[k];
          st.gW.set(k, o.P); st.gC.set(k, new Map(o.C));
          if (now() - t0 > PART_MS * setupK()) break;
        }
        if (st.gW.size < Object.keys(recs).length) return false;
        st.gP = st.gW;
      }
    }
    st.gP = st.gP || new Map();
    for (const k of Object.keys(recs)) {
      if (st.gP.has(k)) continue;
      st.gP.set(k, genomeTexParams(recs[k], k));
      break;
    }
    st.gN = st.gP.size;
    return true;
  }
  // the static ways look (prepWays' constants): the worker's (its init answers with it — its own
  // ways requests follow in order), else here (one call)
  _worldWays(st, M, s) {
    const bands = M.bands, wk = ggWorker();
    this._crownC(st);
    if (wk && !wk.dead) {
      if (!st.iq) st.iq = ask(wk, this._initMsg(st));
      if (!st.iq.done) return false;
      if (st.iq.out && st.iq.out.ST0) { st.ST0 = st.iq.out.ST0; return true; }
      if (st.iq.error) console.warn('groundgen worker init:', st.iq.error, '- the static ways look made here');
    }
    st.ST0 = staticWays(st.ids, s.none_id, bands.treeline_m, s.ways.empty);
    return true;
  }
  // the residual grid (C5), whole: RGBA8UI codes from its tiles, in row slices to PART_MS a scan
  _worldRes(st, M, s) {
    const R = s.residual;
    if (st.codes === undefined) {
      st.codes = null; st.rk = 0; st.rn = 0;
      if (!(st.res && st.res.length)) return true;
      st.codes = new Uint8Array(R.w * R.h * 4).fill(128);
      st.rl = st.res.filter((x) => x.r.status === 200);
      st.rn = st.rl.reduce((a, x) => a + x.r.h, 0);      // rows over all tiles
      st.ri = 0; st.rr = 0;                              // the tile, its row
      return true;
    }
    const w = R.w, h = R.h, px = R.tile_px, codes = st.codes, t0 = now();
    do {
      const { tx, ty, r } = st.rl[st.ri];
      for (let e = Math.min(r.h, st.rr + 16); st.rr < e; st.rr++, st.rk++) {
        const k = st.rr, Z = ty * px + k;
        if (Z >= h) continue;
        for (let i = 0; i < r.w; i++) {
          const X = tx * px + i;
          if (X >= w) break;
          const o = 4 * (Z * w + X), q = 3 * (k * r.w + i);
          codes[o] = r.data[q]; codes[o + 1] = r.data[q + 1]; codes[o + 2] = r.data[q + 2];
        }
      }
      if (st.rr >= r.h) { st.ri++; st.rr = 0; }
    } while (st.rk < st.rn && now() - t0 < PART_MS * setupK());
    return true;
  }

  _prepCls(st) {
    const M = this.meta, s = M.surface, ids = st.ids, waterId = s.water_id, noneId = s.none_id;
    const recs = st.genome.classes;
    const keyById = new Map();
    for (const [k, id] of Object.entries(ids)) if (!keyById.has(id)) keyById.set(id, k);
    const gRec = new Map(Object.keys(recs).map((k) => [k, k]));
    if (recs.forest_floor) gRec.set('forest', 'forest_floor');
    const recRow = new Map(Object.keys(recs).map((k, i) => [k, i]));
    const ST0 = st.ST0;
    // the compact class table (id order: the blend's summation order)
    const raw = [...new Set([...Object.values(ids), waterId, noneId])].sort((a, b) => a - b);
    if (raw.length > NCMAX) throw new Error(`groundgen: ${raw.length} classes, the painter holds ${NCMAX}`);
    const lut = new Uint8Array(256).fill(raw.indexOf(noneId));
    raw.forEach((id, c) => { lut[id] = c; });
    const meas = new Set(PAL.measured_keys);
    // SEALED-MAT (groundref prepWorld): a sealed class paints its way kind's flat colour, the palette path
    const sp = sealedPalette(PAL, WAYSLOOK);
    for (const k of Object.keys(sp)) gRec.delete(k);
    const CB = new Float32Array(NCMAX * 4), CP = new Float32Array(NCMAX * 4), CI = new Int32Array(NCMAX * 4), bare = new Int32Array(NCMAX);
    raw.forEach((id, c) => {
      const key = keyById.get(id) || 'meadow';
      const p = sp[key] || PAL.palette[key] || PAL.palette.meadow;
      CB.set([...p.base, p.mid], 4 * c);
      CP.set([...p.patch, 0], 4 * c);
      let mode = key === 'heath' || key === 'wetland' ? 2 : key === 'rock' ? 3 : key === 'sport' ? 4 : 1, row = 0;
      if (id === waterId) mode = 5;
      else if (gRec.has(key)) { mode = 0; row = recRow.get(gRec.get(key)); }
      CI.set([mode, row, meas.has(key) ? 1 : 0, sp[key] ? 1 : 0], 4 * c);
      bare[c] = (ST0.bare.has(id) ? 1 : 0) | (ST0.bareHi.has(id) ? 2 : 0);
    });
    return { keyById, gRec, recRow, raw, lut, CB, CP, CI, bare };
  }
  _prepWorld(st) {
    const M = this.meta, s = M.surface, ids = st.ids, waterId = s.water_id, noneId = s.none_id;
    const bands = M.bands, recs = st.genome.classes;
    const { keyById, gRec, recRow, raw, lut, CB, CP, CI, bare } = st.cls, ST0 = st.ST0, crownC = st.crownC;
    // the residual grid (C5), whole: RGBA8UI (its codes: _worldRes)
    let res = null;
    const R = s.residual;
    if (st.codes) {
      const bb = M.bbox;     // bake2 genome_inputs: the grid corner in the game frame (x = E - e0, z = n1 - N)
      res = { codes: st.codes, w: R.w, h: R.h, cell: R.cell_m, x0: R.origin[0] - bb.e0, z0: bb.n1 - R.origin[1] };
      st.codes = st.rl = null;
    }
    // the sheet lakes (WATER-G beds): sheetsOf, a scan part of its own (_world)
    const sheets = st.sheets;
    const L = M.tex.light, lg = M.lighting;
    const sh = s.shade && s.shade.shd2;
    if (!sh) throw new Error('bake meta.surface.shade.shd2 missing (C6): re-bake');
    const lv = (a) => a.map((l) => ({ cell: l.cell_m, w: l.w, h: l.h, nx: l.nx, ny: l.ny })).sort((a2, b2) => a2.cell - b2.cell);
    const s2 = st.s2 || null;
    // the rock field: groundref strataField's cells + an integral of the strata cells (geomOf's G.str) +
    // the compact class masks of its rock / cliff classes
    const strataIn = st.rf ? { meta: s.rockfield, img: st.rf.img } : null, strata = strataIn ? strataField(strataIn.meta, strataIn.img, ids) : null;
    if (strata) {
      strata.sum = strataSum(strata);
      const mask = (set) => { let m = 0; for (const id of set) if (lut[id] !== undefined) m |= 1 << lut[id]; return m >>> 0; };
      strata.mR = mask(strata.rock); strata.mC = mask(strata.cliff);
    }
    return {
      ids, waterId, noneId, keyById, gRec, recRow, ST0, raw, lut, CB, CP, CI, bare, res, sheets, bands, s2,
      gP: st.gP, gC: st.gC || new Map(), recs, genome: st.genome, W: s.w, H: s.h,
      anchor: (M.tex.s2 && M.tex.s2.anchor) ?? ANCHOR_DEFAULT,
      grassSat: (M.region && M.region.texture && M.region.texture.grass_sat) ?? (M.tex.s2 && M.tex.s2.grass_sat) ?? GRASS_SAT_DEFAULT,
      light: { sky: L.sky, ground: L.ground, sun: L.sun, white: L.white, sunAltDeg: lg.sun_alt_deg, sunAzDeg: lg.sun_az_deg },
      sun: sunOf(lg.sun_az_deg, lg.sun_alt_deg),
      shd2: { dir: sh.dir, ext: sh.ext, px: sh.tile_px, vis: lv(sh.vis), sky: lv(sh.sky) },
      wayP: s.ways, cls: s.tiles, waterC: lut[waterId], gravelC: ids.gravel !== undefined ? lut[ids.gravel] : lut[noneId],
      crownC, strata, strataIn, tiles: st.tiles, exIn: st.ex,
    };
  }

  // ---------------------------------------------------------------- warm-up: the programs + world textures
  _warm(r) {
    if (this.P && this.P.ok) return true;
    const t0 = now();
    try { return this._warm1(r); } finally { this._warmMs += now() - t0; }
  }
  // a scan's share: the GL alone (its first scan), then the task list (its own scan), then the
  // tasks — a program's sources, then its compile + link call, a task each (gpupainter-p5: the GL +
  // every source in one scan was up to 6.9 ms on phone-lo) —, the links awaited, then the uniform
  // tables + the world textures a task: tasks while the time so far + the worst task yet fits
  // WARM_MS (at least one; gpupainter-p4-3: every program made in one scan was 8.5 ms on quest)
  _warm1(r) {
    const gl = r.getContext();
    if (!this.g) {
      this.g = new GL(gl, (k, ms) => this._glAcc(k, ms));
      this.g.tq = gl.getExtension('EXT_disjoint_timer_query_webgl2');
      this._tlm('gl');
      return false;
    }
    if (!this.P) {
      // a program's sources in an idle callback of their own, off the frame (state().steps 'warm.src';
      // gpupainter-p8 cold pose: src.wprep 1.3-2.1 ms in scan.warm), then its compile + link call a task
      const ST = this._wp.ST0, g = this.g, P = (this.P = { ok: false, list: [], make: [], tasks: null, src: 0 });
      for (const [nm, src] of Object.entries(shaderMakers(ST))) {
        P.src++;
        idle(() => {
          if (this._gone || this.fatal) return;
          const t0 = now();
          try {
            const vf = src();
            P.make.push(named(`prog.${nm}`, () => { P.list.push(g.program(nm, vf[0], vf[1])); }));
            P.src--;
          } catch (e) { this.fatal = `shader ${nm}: ${(e && e.message) || e}`; console.error('groundgen:', this.fatal); }
          finally { this._acc('warm.src', now() - t0); }
        });
      }
      return false;
    }
    const g = this.g, W = this.world, P = this.P;
    if (P.make.length || P.src) {
      if (!P.make.length || now() - (P.at ?? -Infinity) < progGap()) return false;
      this._tasks([P.make.shift()]);
      P.at = now();
      r.resetState();
      return false;
    }
    this._tlm('progs');
    if (now() - P.at < progGap() || !P.list.every((p) => g.done(p))) return false;
    this._tlm('linked');
    if (!W) return false;                         // the world (its textures' data) not prepared yet
    // linked: each program's uniform table, then the world's textures (the gradient table, the
    // Gaussianising table, the residual grid, the s2 colour, the mip scratch; the big ones in row
    // bands of the class's up_bytes)
    if (!P.tasks) P.tasks = [...P.list.map((p) => named(`unif.${p.name}`, () => { g.finish(p); P[p.name] = p; })), ...this._worldTex()];
    g.state();
    this._tasks(P.tasks);
    r.resetState();
    if (P.tasks.length) return false;
    W.worldBytes = g.bytes;
    g.bytes = 0;
    g.peak = 0;
    P.ok = true;
    this._tlm('warm');
    return true;
  }
  _tasks(q) {
    const t0 = now();
    do {
      const t1 = now(), f = q.shift();
      f();
      const d = now() - t1;
      if (d > this._warmWorst) this._warmWorst = d;
      // state().warm_top: the slowest tasks by name
      const T = this._warmTop, nm = f.nm || '?';
      if (T.length < 8 || d > T[T.length - 1][1]) { T.push([nm, Math.round(d * 100) / 100]); T.sort((a, b) => b[1] - a[1]); if (T.length > 8) T.pop(); }
    } while (q.length && now() - t0 + this._warmWorst < WARM_MS * setupK());
  }
  _worldTex() {
    const g = this.g, gl = g.gl, W = this.world, out = [];
    // (the two tables a task each: gpupainter-p8 cold pose, tex.grad 0.6-1.4 ms)
    out.push(named('tex.grad', () => {
      const gr = new Float32Array(512);
      for (let k = 0; k < 256; k++) { const a = (k + 0.5) * ((2 * Math.PI) / 256); gr[2 * k] = Math.fround(Math.cos(a)); gr[2 * k + 1] = Math.fround(Math.sin(a)); }
      W.tGrad = g.tex('RG32F', 256, 1, gr);
    }));
    out.push(named('tex.gz', () => {
      const gz = W.gP.values().next().value.gz;
      W.tGz = g.tex('R32F', gz.length, 1, Float32Array.from(gz));
    }));
    // THE world-band upload (every world texture's rows go through it): rows [y, y + n) x [0, w) of
    // W[key] from data (its format's upload type), one planned band
    const band = (key, y, w, n, data) => {
      const [, fmt, type] = FMT[W[key].f];
      gl.bindTexture(gl.TEXTURE_2D, W[key].t);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, y, w, n, gl[fmt], gl[type], data);
    };
    // an RGBA8UI image's rows from rows(y0, y1) -> its bytes, in bands of the class's up_bytes
    // (a task's band: at most WARM_UP_BYTES — gpupainter-p6 desktop-igpu, scan.warm 2.4 ms at 256 KB bands)
    const banded = (key, w, h, rows) => {
      out.push(named(`tex.${key}`, () => { W[key] = g.tex('RGBA8UI', w, h); }));
      const n = planChunks(4 * w, Math.min(WARM_UP_BYTES, g.caps().up_bytes));
      for (let y = 0; y < h; y += n) {
        const y0 = y, y1 = Math.min(h, y + n);
        out.push(named(`band.${key}`, () => band(key, y0, w, y1 - y0, rows(y0, y1))));
      }
    };
    if (W.res) banded('tRes', W.res.w, W.res.h, (y0, y1) => W.res.codes.subarray(4 * y0 * W.res.w, 4 * y1 * W.res.w));
    else out.push(named('tex.tRes', () => { W.tRes = g.tex('RGBA8UI', 1, 1, new Uint8Array([128, 128, 128, 0])); }));
    if (W.s2) {
      const S2 = W.s2;
      banded('tS2', S2.w, S2.h, (y0, y1) => {
        const a = new Uint8Array(4 * S2.w * (y1 - y0)), img = S2.img, o = 3 * y0 * S2.w;
        for (let q = 0, n = S2.w * (y1 - y0); q < n; q++) { a[4 * q] = img[o + 3 * q]; a[4 * q + 1] = img[o + 3 * q + 1]; a[4 * q + 2] = img[o + 3 * q + 2]; }
        return a;
      });
    } else out.push(named('tex.tS2', () => { W.tS2 = g.tex('RGBA8UI', 1, 1, new Uint8Array(4)); }));
    // the rock field's cells (RGBA32F, groundref strataField; 1 x 1 zero when none), in row bands too
    const SF = W.strata;
    if (SF) {
      out.push(named('tex.tStr', () => { W.tStr = g.tex('RGBA32F', SF.w, SF.h); }));
      const n = planChunks(16 * SF.w, Math.min(WARM_UP_BYTES, g.caps().up_bytes));
      for (let y = 0; y < SF.h; y += n) {
        const y0 = y, y1 = Math.min(SF.h, y + n);
        out.push(named('band.tStr', () => band('tStr', y0, SF.w, y1 - y0, SF.F.subarray(4 * y0 * SF.w, 4 * y1 * SF.w))));
      }
    } else out.push(named('tex.tStr', () => { W.tStr = g.tex('RGBA32F', 1, 1, new Float32Array(4)); }));
    // the tiled records (gs-family-wet-gravel): G mips + LUTs (RGBA32F, groundref tilePrep; 1 x 1 when none),
    // a record's rows in bands of the class's up_bytes
    const TL = W.tiles ? [...W.tiles.values()] : [];
    const gw = Math.max(1, ...TL.map((E) => E.n)), gh = Math.max(1, TL.reduce((a, E) => a + 2 * E.n - 1, 0));
    const lh = Math.max(1, TL.reduce((a, E) => a + E.levels, 0));
    out.push(named('tex.tExG', () => { W.tExG = g.tex('RGBA32F', gw, gh, TL.length ? null : new Float32Array(4)); }));
    out.push(named('tex.tExL', () => {
      const L = new Float32Array(TILE.LUT_N * 4 * lh);
      for (const E of TL) for (let l = 0; l < E.levels; l++) for (let i = 0; i < TILE.LUT_N; i++) for (let c = 0; c < 3; c++) {
        L[4 * ((E.lRow + l) * TILE.LUT_N + i) + c] = E.lut[(l * 3 + c) * TILE.LUT_N + i];
      }
      W.tExL = g.tex('RGBA32F', TILE.LUT_N, lh, L);
    }));
    for (const E of TL) {
      for (let l = 0; l < E.levels; l++) {
        const nl = E.n >> l, rows = planChunks(16 * nl, Math.min(WARM_UP_BYTES, g.caps().up_bytes));
        for (let y = 0; y < nl; y += rows) {
          const y0 = y, y1 = Math.min(nl, y + rows);
          out.push(named('band.tExG', () => {
            const a = new Float32Array(4 * nl * (y1 - y0)), G = E.G[l];
            for (let q = 0, o = 3 * y0 * nl; q < nl * (y1 - y0); q++) { a[4 * q] = G[o + 3 * q]; a[4 * q + 1] = G[o + 3 * q + 1]; a[4 * q + 2] = G[o + 3 * q + 2]; }
            band('tExG', E.offs[l] + y0, nl, y1 - y0, a);
          }));
        }
      }
    }
    // the mip scratch PAIR (MIP-PINGPONG, _mips): level l lives in tMip (l even) / tMip1 (l odd)
    out.push(named('tex.tMip', () => { for (const k of ['tMip', 'tMip1']) W[k] = g.tex('SRGB8A8', SLOT_PX, SLOT_PX, null, Math.log2(SLOT_PX) + 1); }));
    return out;
  }

  // ---------------------------------------------------------------- a slot's geometry + inputs (pure of the key)
  _geom(key) {
    let G = this.geo.get(key);
    if (G) return G;
    G = geomOf(key, this.world, this.meta.surface.coarse.stride);
    this.geo.set(key, G);
    if (this.geo.size > GEO_N) this.geo.delete(this.geo.keys().next().value);
    return G;
  }

  // the asset ids a slot reads (null: not computable yet — the ground index, the surface)
  _inputs(key) {
    const G = this._geom(key), M = this.meta, gen = (this._shipNow(), this._ship.gen);
    if (G.ids) {
      // a key of a shipped level whose ids an earlier hit rule made: made again (never the running job's)
      if (G.shipG === gen || !(G.ship || this._shipLv(G.j)) || (this._job && this._job.key === key)) return G.ids;
      this._reIds(key, G);
    }
    G.shipG = gen;
    if (this._shipKey(key, G)) {
      const id = `s:${key}`;
      if (!this.spec) this.spec = new Map();
      if (!this.spec.has(id)) this.spec.set(id, { id, kind: 'slot', key, url: `${this.base}/surface/paint/L${G.j}/${G.tx}_${G.ty}.webp` });
      G.ids = [id];
      G.ship = true;
      return G.ids;
    }
    const ix = this.tm.gm && this.tm.gm.index;
    if (!ix || (!G.fine1 && !this.surf)) return null;
    const b = this.base, ids = [];
    if (!this.spec) this.spec = new Map();
    for (const r of slotReads(G, this.world, M, (k) => ix.nodes.has(k), (D) => this._crIx.get(D) || this._crIx.set(D, new Set(D.list.map(([i, jj]) => i * 65536 + jj))).get(D))) {
      ids.push(r.id);
      if (!this.spec.has(r.id)) this.spec.set(r.id, { ...r, url: `${b}/${r.rel}` });
    }
    G.ids = ids;
    return ids;
  }

  // ---- THE SHIPPED SLOTS (header constants): the hit rule now — meta.surface.paint's key against this build's painter
  // (__PAINTER_KEY__) and the tunables' params (paintkey.js, every class's: a device's own flag governs live paint
  // only), no season term; its generation moves when it changes
  _shipNow() {
    const S = this._ship, raw = genRaw();
    if (S.raw === raw) return S.on;
    S.raw = raw;
    const on = paintHit(this.meta.surface && this.meta.surface.paint, this.painterKey, raw);
    if (on !== S.on) { S.on = on; S.gen++; }
    return on;
  }
  _shipLv(j) { const P = this.meta.surface && this.meta.surface.paint; return !!P && P.levels.includes(j); }
  // key ships: the rule on, its level shipped, it never missed, and fetching the faster way (else one probe a SHIP_PROBE_MS)
  _shipKey(key, G) {
    const S = this._ship;
    if (!S.on || !this._shipLv(G.j) || S.miss.has(key)) return false;
    if (!S.slow) return true;
    const t = now();
    if (t < S.probeAt) return false;
    S.probeAt = t + SHIP_PROBE_MS;
    return true;
  }
  // a sample of THE EXPECTED TIME (kind 'fetch': a slot fetched + decoded; else a painted job), wall ms
  _shipAdd(kind, ms) {
    const S = this._ship;
    if (kind === 'fetch') S.fetchMs = S.fetchMs === null ? ms : S.fetchMs + SHIP_EW * (ms - S.fetchMs);
    else S.paintMs += SHIP_EW * (ms - S.paintMs);
    const slow = S.fetchMs !== null && S.fetchMs > S.paintMs;
    if (slow !== S.slow) { S.slow = slow; S.gen++; S.flips++; }
  }
  // a shipped key whose slot is absent / undecodable: painted from now on (a miss)
  _shipFail(key) {
    this._ship.miss.add(key);
    this._ship.missed++;
    const G = this.geo.get(key);
    if (G && G.ship) this._reIds(key, G);
  }
  // a key's ids dropped (made again at its next _inputs; its references with them)
  _reIds(key, G) {
    if (this._refK.has(key)) this._refDel(key);
    G.ids = null;
    G.ship = false;
    G.rg = -1;
  }

  // (touch: the LRU touch — a job's start; the scan's listed / loading keys are kept by _evict instead)
  _ready(ids, t, touch = true) {
    const K = ggWorker();
    for (const id of ids) {
      const e = this.cache.get(id);
      if (!e || (e.st !== 'ok' && e.st !== 'absent')) return false;
      // held by a worker that is gone: loaded again (decoded here)
      if (e.data && e.data.wk && !K) { this._drop(id, e); return false; }
    }
    if (touch) for (const id of ids) { const e = this.cache.get(id); this.cache.delete(id); this.cache.set(id, e); }   // LRU touch
    return true;
  }
  // ... memoised a key against the cache's generation (any load landing / failing, any drop bumps it)
  _readyM(key, ids) {
    const G = this._geom(key);
    if (G.rg !== this._rdyGen) { G.rd = this._ready(ids, 0, false); G.rg = this._rdyGen; }
    return G.rd;
  }

  // prio: the asset class (assetq) of the slot that wants them; dl: the scan's deadline — a fetch
  // (its synchronous start: the fetch() / decode set-up) starts while its worst recent ms
  // ('scan.fetch') still fits, the scan's first one always -> false: stopped at the deadline.
  // THE LATCH'S SLOTS (painter-latch): a bulk ask while bulk is paused (the Beam to the latch) starts
  // nothing — it would hold one of LOAD_MAX in assetq's paused queue; a 'near' ask for an input still
  // loading as bulk re-asks it as near and frees the bulk one's slot (live: 20 L1/L2 shd2 inputs asked
  // bulk 0.4 s after the Beam, the latch's near asks then skipped as 'load' until the dismiss timeout);
  // a near ask counts no slot a paused bulk load holds (loadsB: one started before the pause)
  _load(ids, t, prio = 'bulk', dl = Infinity) {
    const held = bulkPaused();
    if (prio !== 'near' && held) return true;
    for (const id of ids) {
      if (this.loads - (held ? this.loadsB : 0) >= LOAD_MAX) return true;
      const e = this.cache.get(id), up = !!e && e.st === 'load' && prio === 'near' && e.prio === 'bulk';
      if (!up && e && (e.st !== 'err' || t < e.at + RETRY_MS)) continue;
      if (this._fetched === this._scanN && now() + this._unit('scan.fetch') > dl) return false;
      this._fetched = this._scanN;
      this._part('scan.fetch', () => this._fetch(id, prio, up ? e : null));
    }
    return true;
  }

  // the fetch's entry now (st 'load', a load in flight); its synchronous start — the decode / asset
  // queue set-up, the Request, the browser's fetch() — in a task of its own (_soon: off the scan)
  _fetch(id, prio = 'bulk', was = null) {
    const e = { st: 'load', data: null, bytes: 0, at: 0, prio };
    this.cache.set(id, e);
    this._loadAdd(e, 1);
    if (was) { this._loadAdd(was, -1); was.up = true; this.stats.escalate++; } else {
      const n = this._fetchN.get(id) || 0;
      this._fetchN.set(id, n + 1);
      if (n) this.stats.refetch++;
    }
    this._soon(() => this._fetchGo(id, e, prio));
  }
  // fn in a task of its own (setTimeout 0: not in the scan's rAF callback, nor its microtasks) under
  // 'fetch.start'; its ms charged to the gen budget as the scan's are (gensched charge)
  _soon(fn, label = 'fetch.start') {
    setTimeout(() => {
      if (this._gone) return;
      const t0 = now(), l0 = this._label;
      this._label = label;
      try { fn(); } finally {
        this._label = l0;
        const dt = now() - t0;
        this._acc(label, dt);
        gen.charge(dt);
        this._scanMs.charged += dt;
      }
    }, 0);
  }
  _fetchGo(id, e, prio) {
    // dropped before its start (dispose, a cache reset): nothing to fetch
    if (this.cache.get(id) !== e) { if (!e.up) this._loadAdd(e, -1); return; }
    const sp = this.spec.get(id);
    const ABSENT = {};
    let p;
    if (sp.kind === 'slot') {
      // a shipped slot: decoded off the main thread, no colour or alpha change, never through a 2D canvas (EXACT-DATA)
      const t0 = now();
      p = fetchAsset(sp.url, { prio, status: true }).then((r) => {
        if (!r.body) { if (r.dropped) throw new Error(`${sp.url}: dropped`); return ABSENT; }
        return createImageBitmap(new Blob([r.body], { type: 'image/webp' }), { colorSpaceConversion: 'none', premultiplyAlpha: 'none', imageOrientation: 'none' });
      }).then((bmp) => {
        if (bmp === ABSENT) return ABSENT;
        if (bmp.width !== SLOT_PX || bmp.height !== SLOT_PX) { bmp.close(); return ABSENT; }
        this._shipAdd('fetch', now() - t0);
        this._ship.fetched++;
        return { bmp, w: SLOT_PX, h: SLOT_PX };
      }).catch((err) => {
        if (/dropped/.test(String(err && err.message))) throw err;
        console.warn('groundgen: shipped slot', sp.key, err && err.message);
        return ABSENT;
      }).then((d) => { if (d === ABSENT) this._shipFail(sp.key); return d; });
    } else if (sp.kind === 'img') {
      p = decodeImage(sp.url, sp.ch, prio).then((r) => {
        if (r.status === 404) return ABSENT;
        if (r.status !== 200) throw new Error(`${sp.url}: ${r.dropped ? 'dropped' : r.status}`);
        if (sp.ch === 8 && !(r.w === sp.px && r.h === 3 * sp.px)) return ABSENT;     // surface.js: no ways layer there
        if (sp.q4 && !(r.w === sp.px && r.h === 3 * sp.px)) throw new Error(`${sp.url}: ${r.w} x ${r.h} is no ${sp.px} x ${3 * sp.px} WAYS-Q4 tile`);
        if (sp.ch === 1 && sp.px && !(r.w === sp.px && r.h === sp.px)) return ABSENT; // surface.js tileOk: none_id
        return { w: r.w, h: r.h, data: r.data };
      });
    } else {
      // a crown pack / a ground-tree node: decoded in the worker, which keeps the pack / the indexed
      // mesh (wkey: this fetch's own key there) and hands back what the main thread draws (the
      // node's vertex array + triangles); without one, here (groundcpu nodeCpu, decodeCrowns)
      const K = ggWorker(), serial = ++_serial, wkey = `${id}#${serial}`;
      p = fetchAsset(sp.url, { prio, status: true }).then((r) => {
        if (!r.body) { if (r.status === 404) return ABSENT; throw new Error(`${sp.url}: ${r.dropped ? 'dropped' : r.status}`); }
        if (sp.kind === 'cpk') {
          if (K) return askP(K, { op: 'cpk', key: wkey, body: r.body }, [r.body]).then((o) => ({ wk: wkey, n: o.n, wbytes: o.bytes }));
          return decodeCrowns(r.body);
        }
        const nd = { tx: sp.tx, ty: sp.ty, j: sp.j, serial };
        if (K) {
          return askP(K, { op: 'node', key: wkey, body: r.body, j: sp.j, tx: sp.tx, ty: sp.ty }, [r.body])
            .then((o) => Object.assign(nd, { wk: wkey, ymin: o.ymin, yn: o.yn, va: o.va, tri: o.tri, wbytes: o.bytes }));
        }
        const o = nodeCpu(r.body, sp.j, sp.tx, sp.ty);
        return Object.assign(nd, { mesh: o.mesh, ymin: o.ymin, yn: o.yn, va: o.va, tri: o.tri });
      });
    }
    p.then((d) => {
      if (this.cache.get(id) !== e) { if (d && d.wk) this._wkDrop([d.wk]); return; }
      this._rdyGen++;
      e.st = d === ABSENT ? 'absent' : 'ok';
      e.data = d === ABSENT ? null : d;
      e.bytes = d === ABSENT ? 64 : d.bmp ? 4 * d.w * d.h : d.wk ? sizeOf({ ...d, wbytes: null }) + d.wbytes : sizeOf(d);
      this.cacheBytes += e.bytes;
      if (this._refs.has(id)) this._refBytes += e.bytes;
    }, (err) => {
      this._rdyGen++;
      e.st = 'err';
      e.at = now();
      if (!/dropped/.test(String(err && err.message))) console.warn('groundgen input', id, err && err.message);
    }).finally(() => { if (!e.up) this._loadAdd(e, -1); });
  }
  // a load in flight counted (loads; loadsB: the bulk ones), d +1 at its start, -1 at its end or escalation
  _loadAdd(e, d) {
    this.loads += d;
    if (e.prio === 'bulk') this.loadsB += d;
  }

  // a key's inputs counted in / out of the want's references (refSync); _refBytes follows the cached ones
  _refAdd(key, ids) {
    this._refK.set(key, ids);
    for (const id of ids) {
      const n = this._refs.get(id) || 0;
      this._refs.set(id, n + 1);
      if (!n) { const e = this.cache.get(id); if (e && (e.st === 'ok' || e.st === 'absent')) this._refBytes += e.bytes; }
    }
  }
  _refDel(key) {
    const ids = this._refK.get(key);
    if (!ids) return;
    this._refK.delete(key);
    for (const id of ids) {
      const n = this._refs.get(id) - 1;
      if (n > 0) { this._refs.set(id, n); continue; }
      this._refs.delete(id);
      const e = this.cache.get(id);
      if (e && (e.st === 'ok' || e.st === 'absent')) this._refBytes -= e.bytes;
    }
  }

  // keep: the gather set (gatherPick: the listed + gathering keys; their inputs stay; the job's and the probe's always).
  // Over cache_mb the inputs no wanted key reads go (LRU: refSync); stuck (the scan's W: the referenced
  // bytes past cache_mb, nothing gathering / ready behind the job) the referenced ones too, the worst-needed
  // first (refNeed), to cache_mb less the room the best waiting key lacks (stuckRoom) — a cap too small for
  // the want, the one case an input still wanted
  // is fetched again (stats.evict_ref). To a deadline (dl; ruling p7 (2): gpupainter-p6 phone-lo,
  // scan.want.evict 2.1 ms with none): the LRU walk checks it every EVICT_CHUNK entries, the rest next scan
  // (blk >= 0: a BLOCKED key's place in stuck — only the inputs whose first reader is placed after it go,
  // for its room)
  _evict(keep = null, dl = Infinity, stuck = null, blk = -1) {
    const cap = caps().cache_mb * 1e6;
    if (this.cacheBytes <= cap) return;
    const pin = this._pinB;
    pin.clear();
    if (this._job) for (const id of this._inputs(this._job.key) || []) pin.add(id);
    for (const id of this._probePin) pin.add(id);
    if (keep) for (const k of keep) { const G = this.geo.get(k); if (G && G.ids) for (const id of G.ids) pin.add(id); }
    const drop = [];
    let i = 0, cut = false;
    for (const [id, e] of this.cache) {
      if (this.cacheBytes <= cap * 0.9) break;
      if (++i % EVICT_CHUNK === 0 && now() > dl) { this.stats.evict_cut++; cut = true; break; }
      if (e.st === 'load' || pin.has(id) || this._refs.has(id)) continue;
      this._drop(id, e, drop);
    }
    if (stuck && !cut && this._refBytes > cap) {
      const need = refNeed(stuck, this._refIds, this._needB || (this._needB = new Map())), c = [];
      for (const [id, e] of this.cache) {
        if (e.st === 'load' || pin.has(id) || !this._refs.has(id)) continue;
        const n = need.get(id) ?? Infinity;
        if (blk < 0 || n > blk) c.push([n, id, e]);
      }
      c.sort((a, b) => b[0] - a[0]);
      // only the room the best waiting key lacks (its inputs not in x the mean entry): each referenced input
      // dropped is one fetched again later
      const to = cap - stuckRoom(stuck, this._refIds, this._have, this.cacheBytes / Math.max(1, this.cache.size), blk);
      for (const [, id, e] of c) {
        if (this.cacheBytes <= to) break;
        this._drop(id, e, drop);
        this.stats.evict_ref++;
      }
    }
    this._wkDrop(drop);
  }
  // a cache entry gone (its worker share too: into keys, else posted now)
  _drop(id, e, keys = null) {
    this.cache.delete(id);
    this._rdyGen++;
    if (e.st === 'ok' || e.st === 'absent') { this.cacheBytes -= e.bytes; if (this._refs.has(id)) this._refBytes -= e.bytes; }
    if (e.data && e.data.wk) { if (keys) keys.push(e.data.wk); else this._wkDrop([e.data.wk]); }
    if (e.data && e.data.bmp && !(this._job && this._job.bmp === e.data.bmp)) e.data.bmp.close();
  }
  _wkDrop(keys) {
    const K = ggWorker();
    if (K && keys.length) K.w.postMessage({ op: 'drop', keys });
  }

  _data(id) { const e = this.cache.get(id); return e && e.st === 'ok' ? e.data : null; }

  // ---------------------------------------------------------------- the job
  _run(gl, job, ms) {
    const t0 = now();
    // a job that ended this same frame (the queue starts the next one at once): its last run's GPU
    // estimate + submissions are this frame's too (ms and the class's per-run caps both pay them)
    const carry = this._carry && t0 - this._carry.t < 2 ? this._carry : null;
    if (carry) ms -= carry.est;
    this._carry = null;
    if (!(ms > 0)) return 'yield';
    let J = this._job;
    if (J && J.job !== job) { this._abandon('replaced'); J = null; }
    if (!J) {
      const key = job.tile.key;
      if (!this.P || !this.P.ok || !this.world) return 'done';
      // its inputs as listed may be gone (the cache's class cap evicts what no job pins; a step
      // would read the none class / no ways for them — gpupainter-p2-5 quest, prep.cls 11-28 ms
      // calls re-mapping a class tile no longer cached): held out, loaded again
      const ids = this._inputs(key);
      if (!ids || !this._ready(ids, t0)) { this.held.set(key, t0 + HOLD_MS); this.stats.held++; return 'done'; }
      const resv = this.vt.reserve(key);
      if (!resv) { this.held.set(key, t0 + HOLD_MS); this.stats.held++; return 'done'; }
      J = this._job = { job, key, resv, i: 0, steps: null, T: {}, t0, cpu: 0, est: 0, n: 0, prof: {}, maxRun: 0, over: 0, gms: 0, gb: 0, rec: null };
      this.stats.jobs++;
      this._tlm('job1');
    }
    const r = this.getRenderer();
    try {
      if (!this._resvOk(J)) { this._abandon('lost'); return 'done'; }
      // the whole slice a frame gives (gensched frame: min(gen.ms[phase], budget())); less = a
      // PARTIAL one (the queue ran another job before this one in this frame)
      const live = genLive(), full = Math.min((live && live.gen && live.gen.ms && live.gen.ms[phase()]) ?? 0, budget());
      const partial = ms < 0.9 * full, tail = this._unit('run.reset') + this._unit('run.end');
      // the plan (a job's first run) counts as a step: it waits for a slice it fits
      if (!J.steps && partial && this._unit('run.plan') + tail > ms) { this.stats.yields++; return 'yield'; }
      // the run's caps: the class's, x BEAM_K while hidden (runCaps, sched hidden: the Beam to the
      // overlay's drop; the pieces stay planned to the class's own)
      const g = this.g, ph = phase(), hid = hidden(), cap = runCaps(g.caps(), hid), M = g.meter, gl0 = this._glTot, rid = ++this._rid;
      M.reset();
      // this run's submissions (SUBMIT: units, draws, GPU estimate; a carried job's last run counts)
      // (the run's records reused: no allocation a run)
      const sub = this._subB;
      sub.n = carry ? 1 : 0; sub.units = carry ? carry.units : 0; sub.draws = carry ? carry.draws : 0; sub.est = carry ? carry.est : 0; sub.untimed = false;
      this._tqPoll(gl);
      this._label = 'run.state'; this._stepGl = 0; this._slowGl = null;
      let t1 = now();
      g.state();
      const dS = now() - t1;
      this._label = 'run';
      this._acc('run.state', dS, dS, this._stepGl);
      this._stall('state', dS);
      if (!J.steps) { t1 = now(); this._label = 'run.plan'; J.steps = this._plan(J); this._label = 'run'; this._acc('run.plan', now() - t1); }
      // the run factor of this mode (RUNK: the measured / estimated back-to-back GPU of the last runs)
      // scales every step's estimate; estB = the plain estimate of the steps after the first timed one
      // (what the run's b2b timer measures: the factor's next ratio)
      const K = this._k(hid);
      let n = 0, runEst = 0, runEst0 = 0, wait = false, gpuRun = false, estB = 0, atomic = null;
      const ran = this._ranB, PR = this._pr;
      ran.length = 0;
      while (J.i < J.steps.length) {
        const s = J.steps[J.i], rect = s.per !== undefined, m = this._model(s.label), unit = this._unit(s.label);
        // the step's units (a rect step: its rest — rectNext at Infinity into PR; 0 while its pass is off)
        const c = rect ? (s.on && !s.on() ? 0 : rectNext(s, Infinity, PR)) : (typeof s.cost === 'function' ? s.cost() : s.cost);
        const dr = c > 0 ? (typeof s.draws === 'function' ? s.draws() : s.draws ?? 1) : 0;
        // THE RE-SPLIT (ruling p8 (2)): re-estimated at the model now x the run factor; a rect step past
        // what the run has left — its caps; a later step or a partial slice: the slice's GPU share too
        // (the CPU spent, every step's GPU estimate this run, this step's CPU unit, the run's end) — runs
        // as its largest portion within it (packNext)
        const late = n > 0 || partial, capLeft = cap.gpu_ms - sub.est;
        const left = late ? Math.min(capLeft, ms - (now() - t0) - runEst - unit - tail) : capLeft;
        const v = packNext(sub, s, c, dr, m, K, cap, left, late, PR), cu = PR[0], est0 = PR[1], est = PR[2];
        if (v === STOP) { if (n === 0 && partial) this.stats.yields++; else this.stats.capped++; break; }
        // the CPU spent + every step's GPU estimate this run (not only the next one's) + this
        // step's CPU unit (its label's recent worst call / slice) + the run's end within ms
        if (now() - t0 + runEst + est + unit + tail > ms) {
          if (n > 0) break;
          if (partial) { this.stats.yields++; break; }          // the next frame's whole slice
          // a whole slice it does not fit either: it runs (an over-slice run, state().overs)
        }
        // an atomic step (or a rect step's fixed cost alone) a fresh run admits past its cap: named
        if (v === OVER) { atomic = rect && cu > 0 ? `${s.label} (fixed)` : s.label; this._atomicAdd(atomic, est); }
        sub.n++; sub.units += cu; sub.draws += dr; sub.est += est;
        runEst += est; runEst0 += est0;
        // the run's LEAD query (no work: its span takes the GPU work queued before the run — the frame's —
        // so the run's own steps are timed apart from it), before its first timed step
        if (cu > 0 && !gpuRun && !this._runG.has(rid)) { const lq = this._tqBegin(gl); if (lq) this._tqEnd(gl, lq, null, 0, J, false, rid, true); }
        if (cu > 0 && gpuRun) estB += est0;
        const tq = cu > 0 ? this._tqBegin(gl) : null;
        if (cu > 0 && !tq) sub.untimed = true;
        // the step's flags (state().overs_b2b / step_flags): 1 its label's first piece in the job, 2 the job's first
        // run, 4 the job's pool layer not written before on this page, 8 a label switch after the run's previous step
        let fl = 0;
        if (tq) {
          const SL = J.seenL || (J.seenL = new Set());
          if (!SL.has(s.label)) { fl |= 1; SL.add(s.label); }
          if (J.n === 0) fl |= 2;
          if (!this._touched.has(`${J.resv.p}:${J.resv.slot}`)) fl |= 4;
          if (J.lastL !== undefined && J.lastL !== s.label && gpuRun) fl |= 8;
          J.lastL = s.label;
        }
        this._label = s.label; this._stepGl = 0; this._slowGl = null; this._unitMs = null;
        t1 = now();
        // the step's CPU deadline: what the slice leaves after this run's GPU estimate and its end
        // (a sliced CPU step stops there and answers MORE; at least one slice a call). A rect step: its
        // portion (PR; its whole rest when its pass is off), the step moved past it (done: its rect's end)
        let rv;
        if (rect) {
          const y0 = c > 0 ? PR[3] : s.y0, y1 = c > 0 ? PR[4] : s.y1, x0 = c > 0 ? PR[5] : s.x0, x1 = c > 0 ? PR[6] : s.x1;
          if (c > 0) { s.y0 = PR[7]; s.cx = PR[8]; } else s.y0 = s.y1;
          const f = s.f;
          s.f = false;
          // (a pass that is off — its gate s.on — draws nothing: STAGE SKIPS)
          if (!(s.on && !s.on())) s.rf(y0, y1, x0, x1, f, s.l && s.y0 >= s.y1);
          rv = s.y0 >= s.y1 ? undefined : MORE;
        } else rv = s.fn(t0 + ms - runEst - tail);
        const dt = now() - t1;
        this._label = 'run';
        this._acc(s.label, dt, this._unitMs ?? dt, this._stepGl);
        if (dt > SLOW_MS) this._slowAdd(s.label, dt, J.key);
        ran.push(s.label, Math.round(dt * 100) / 100);
        J.prof[s.label] = (J.prof[s.label] || 0) + dt;
        if (tq) { this._tqEnd(gl, tq, s.label, cu, J, gpuRun, rid, false, est0, fl); gpuRun = true; }
        n++; J.est += est;
        if (rv === WAIT) { wait = true; break; }
        if (rv !== MORE) J.i++;
      }
      J.n++;
      if (wait) J.waits = (J.waits || 0) + 1;
      this._stepGl = 0; this._slowGl = null;
      t1 = now();
      r.resetState();
      const dR = now() - t1;
      this._acc('run.reset', dR);
      this._stall('reset', dR);
      // steps left: the frame's share is spent (CPU, or the GPU estimate) — 'yield', so the queue
      // does not run the job again this frame (the estimate holds a FRAME's GPU work, not a run's)
      const fin = J.i >= J.steps.length;
      let ok = false;
      if (fin) {
        t1 = now();
        this._free(J);
        ok = this.vt.commit(J.key);
        if (ok) { if (this._painted.has(J.key)) this.stats.repaint++; else this._painted.add(J.key); }
        // a shipped slot's bitmap goes with its job (the slot is resident; a later want fetches it again), a painted
        // job is a sample of THE EXPECTED TIME
        if (J.bmp) { const id = `s:${J.key}`, e = this.cache.get(id); if (e) this._drop(id, e); J.bmp.close(); if (ok) this._ship.committed++; }
        else if (ok) { this._shipAdd('paint', now() - J.t0); this._ship.painted++; }
        this._acc('run.end', now() - t1);
      }
      const runMs = now() - t0, runGl = this._glTot - gl0;
      J.cpu += runMs;
      if (runMs > J.maxRun) J.maxRun = runMs;
      // an over-slice run: OWN CPU (the run's time less its GL calls' still past ms) or GL-BLOCKED
      // (inside ms but for the driver's calls: a stall behind the GPU process / the frame)
      if (runMs > ms) {
        const own = runMs - runGl > ms, S = this.stats;
        J.over++; S.over_runs++; S.over_ms += runMs - ms;
        if (own) { S.over_cpu++; S.over_cpu_max = Math.max(S.over_cpu_max, Math.round((runMs - ms) * 100) / 100); }
        else { S.over_gl++; S.over_gl_max = Math.max(S.over_gl_max, Math.round((runMs - ms) * 100) / 100); }
        this._overs.push({ key: J.key, at: Math.round(t0 / 100) / 10, slice: Math.round(ms * 100) / 100, run: Math.round(runMs * 100) / 100,
          gl: Math.round(runGl * 100) / 100, own, partial, ph, hid, steps: ran.slice() });
        if (this._overs.length > 40) this._overs.shift();
      }
      // the run's submissions: the measured maxima (state().submit) + its GPU timer total once its queries land
      this._subRun(M, sub, runEst, cap, J, rid, hid, ran, estB, runEst0, atomic);
      if (runMs > this.stats.max_run_ms) this.stats.max_run_ms = Math.round(runMs * 100) / 100;
      this._idleAt = now();
      if (!fin) return 'yield';
      this._carry = { t: now(), est: sub.est, units: sub.units, draws: sub.draws };
      this.stats[ok ? 'committed' : 'dropped']++;
      if (ok) { this._tlm('commit1'); this._touched.add(`${J.resv.p}:${J.resv.slot}`); }
      this.stats.gpu_ms_est = Math.round(J.est * 100) / 100;
      if (ok) {
        const top = Object.entries(J.prof).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([l, v]) => [l, Math.round(v * 10) / 10]);
        this.done.set(J.key, { cpu_ms: Math.round(J.cpu * 10) / 10, wall_ms: Math.round(now() - J.t0), runs: J.n, waits: J.waits || 0, gpu_ms_est: this.stats.gpu_ms_est,
          max_run_ms: Math.round(J.maxRun * 100) / 100, over_runs: J.over, gpu_ms: Math.round(J.gms * 100) / 100, gpu_ms_b2b: Math.round(J.gb * 100) / 100,
          peak_mb: Math.round(this.g.peak / 1e5) / 10, ship: !!J.bmp, gpu_runs: J.gRuns || 0, gpu_run_max: Math.round((J.gRunMax || 0) * 100) / 100, steps: Object.fromEntries(top) });
        J.rec = this.done.get(J.key);              // its gpu_ms grows as the last timer queries land
      }
      if (this.done.size > 512) this.done.delete(this.done.keys().next().value);
      this._job = null;
      return 'done';
    } catch (e) {
      try { r.resetState(); } catch { /* lost context */ }
      this._free(J);
      this.vt.release(J.key, true);
      this._job = null;
      this.stats.failed++;
      throw e;
    }
  }

  // a ground node's GPU buffers: node-local (x, z, y - yn), yn = floor(its ymin) — the TIN pass
  // adds the slot's offsets (uOff), so one upload serves every slot over the node (LRU, the class's
  // node_mb). The vertex array comes made (groundcpu nodeCpu: the worker); it and the triangles go
  // up in pieces of the class's buf_bytes to the deadline: null = not yet (call again)
  _nodeBuf(id, d, dl) {
    let N = this._nodes.get(id);
    if (N && N.serial !== d.serial) { this._nodeFree(N); this._nodes.delete(id); N = null; }
    if (!N) {
      N = { serial: d.serial, yn: d.yn, count: d.tri.length, bytes: d.va.byteLength + d.tri.byteLength, vo: 0, io: 0, ok: false, vao: null, vb: null, ib: null };
      this._nodes.set(id, N);
    }
    if (N.ok) { this._nodes.delete(id); this._nodes.set(id, N); return N; }     // LRU touch
    const gl = this.g.gl, P = this.P, va = d.va, tri = d.tri;
    if (!N.vao) {
      N.vao = gl.createVertexArray(); N.vb = gl.createBuffer(); N.ib = gl.createBuffer();
      gl.bindVertexArray(N.vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, N.vb);
      gl.bufferData(gl.ARRAY_BUFFER, va.byteLength, gl.STATIC_DRAW);
      gl.enableVertexAttribArray(P.tin.attr.aP);
      gl.vertexAttribPointer(P.tin.attr.aP, 3, gl.FLOAT, false, 0, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, N.ib);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, tri.byteLength, gl.STATIC_DRAW);
      gl.bindVertexArray(null);
      this._nodeBytes += N.bytes;
    }
    N.vo = this._bufUp(N.vb, va, N.vo, dl);
    if (N.vo >= va.byteLength) N.io = this._bufUp(N.ib, tri, N.io, dl);
    if (N.io < tri.byteLength) return null;
    N.ok = true;
    // the LRU's oldest go past node_mb (never the current job's)
    const cap = caps().node_mb * 1e6;
    for (const [k, M] of this._nodes) {
      if (this._nodeBytes <= cap) break;
      if (this._nodePin.has(k) || !M.ok) continue;
      this._nodeFree(M);
      this._nodes.delete(k);
    }
    return N;
  }
  // a buffer's bytes [o, ..) of data in pieces of the class's buf_bytes (COPY_WRITE_BUFFER: any buffer's kind)
  // while the last piece's time still fits before dl (at least one) -> the new offset
  _bufUp(b, data, o, dl) {
    const r = this.g.bufUp(b, data, o, dl);
    this._unitMs = Math.max(this._unitMs || 0, r[1]);
    return r[0];
  }
  _nodeFree(N) {
    const gl = this.g && this.g.gl;
    if (!gl || !N.vao) return;
    gl.deleteVertexArray(N.vao); gl.deleteBuffer(N.vb); gl.deleteBuffer(N.ib);
    N.vao = null; N.ok = false;
    this._nodeBytes -= N.bytes;
  }

  // a slot's crown discs from the worker (the level-j node meshes + the packs it holds) -> the
  // request's handle; null: no crown grid, or an input the worker does not hold
  _discsAsk(K, key, t = now()) {
    const G = this._geom(key), cr = G.cr, ids = this._inputs(key);
    if (!cr.on || !ids) return null;
    const nodes = [], packs = [];
    for (const id of ids) {
      if (!id.startsWith('n:')) continue;
      const d = this._data(id);
      if (!d) continue;
      if (!d.wk) return null;
      nodes.push(d.wk);
    }
    for (const [pre, shrub] of [['t:', false], ['s:', true]]) {
      for (const id of ids) {
        if (!id.startsWith(pre)) continue;
        const d = this._data(id);
        if (!d) continue;
        if (!d.wk) return null;
        packs.push([d.wk, shrub]);
      }
    }
    const h = ask(K, { op: 'discs', nodes, packs, span: G.span, j: G.j, X0: cr.X0, Z0: cr.Z0 });
    if (key !== (this._job && this._job.key)) this._pre.set(key, { h, at: t });
    return h;
  }
  // ... made here (no worker): the cached meshes + packs
  _discsHere(key) {
    const G = this._geom(key), cr = G.cr, ids = this._inputs(key), nodes = [], packs = [];
    for (const id of ids) if (id.startsWith('n:')) { const d = this._data(id); if (d) { if (!d.mesh) throw new Error(`ground node ${id}: held by a gone worker`); nodes.push(d.mesh); } }
    for (const [pre, shrub] of [['t:', false], ['s:', true]]) {
      for (const id of ids) if (id.startsWith(pre)) { const d = this._data(id); if (d) { if (d.wk) throw new Error(`crown pack ${id}: held by a gone worker`); packs.push([d, shrub]); } }
    }
    return discsCpu(this.world.crownC, nodes, packs, G.span, G.j, cr.X0, cr.Z0);
  }

  // a 512 m class tile mapped for the painter, once per world (the decoded asset's WeakMap entry;
  // an absent tile: the none class): compact class | 0x80 at the cells whose centre lies in a
  // sheet lake's rings (even-odd — inRings' crossings, a scanline per row: the same bits)
  // Built in ~SLICE_CELLS row slices to the deadline dl (a slice starts while the last one's time
  // still fits; the worst = the step's unit): null = not yet (call again)
  _clsTile(tx, ty, dl = Infinity) {
    const e = this.cache.get(`c:${tx}_${ty}`), d = e && e.st === 'ok' ? e.data : null, W = this.world, tm = this.meta.surface.tiles.tile_m;
    let m = d ? this._clsMap.get(d) : this._clsNone.get(`${tx}_${ty}`);
    if (m) return m;
    const pk = `${tx}_${ty}`;
    let B = this._clsPart.get(pk);
    if (!B || B.d !== d) {
      B = { d, m: new Uint8Array(tm * tm), r: 0 };
      this._clsPart.set(pk, B);
      if (this._clsPart.size > 16) this._clsPart.delete(this._clsPart.keys().next().value);
    }
    m = B.m;
    const lut = W.lut, ch = Math.max(1, Math.floor(SLICE_CELLS / tm));
    let unit = 0;
    do {
      const t1 = now(), r0 = B.r, r1 = Math.min(tm, r0 + ch);
      if (d) { const a = d.data; for (let q = r0 * tm, qe = r1 * tm; q < qe; q++) m[q] = lut[a[q]]; } else m.fill(lut[W.noneId], r0 * tm, r1 * tm);
      for (const L of W.sheets) sheetBits(L, m, tx * tm, ty * tm, tm, r0, r1);
      B.r = r1;
      unit = Math.max(unit, now() - t1);
    } while (B.r < tm && now() + unit < dl);
    this._unitMs = Math.max(this._unitMs || 0, unit);
    if (B.r < tm) return null;
    this._clsPart.delete(pk);
    if (d) this._clsMap.set(d, m);
    else if (e && e.st === 'absent') { this._clsNone.set(`${tx}_${ty}`, m); if (this._clsNone.size > 64) this._clsNone.delete(this._clsNone.keys().next().value); }
    return m;
  }

  // a ways tile's cells interleaved (P0 RGBA, P1 RGBA as two uint32 a cell), once per world
  // (the decoded asset's WeakMap entry); null: no ways layer there (the empty cell)
  // (in ~SLICE_CELLS row slices to the deadline dl, as _clsTile: false = not yet, call again)
  _waysCells(tx, ty, dl = Infinity) {
    const d = this._data(`w:${tx}_${ty}`);
    if (!d) return null;
    let c = this._waysMap.get(d);
    if (c) return c;
    const tm = this.world.wayP.tile_m, n = tm * tm, a = new Uint32Array(d.data.buffer, d.data.byteOffset, 2 * n), pk = `${tx}_${ty}`;
    let B = this._waysPart.get(pk);
    if (!B || B.d !== d) {
      B = { d, c: new Uint32Array(2 * n), r: 0 };
      this._waysPart.set(pk, B);
      if (this._waysPart.size > 16) this._waysPart.delete(this._waysPart.keys().next().value);
    }
    c = B.c;
    const ch = Math.max(1, Math.floor(SLICE_CELLS / tm));
    let unit = 0;
    do {
      const t1 = now(), qe = Math.min(tm, B.r + ch) * tm;
      for (let q = B.r * tm; q < qe; q++) { c[2 * q] = a[q]; c[2 * q + 1] = a[n + q]; }
      B.r = qe / tm;
      unit = Math.max(unit, now() - t1);
    } while (B.r < tm && now() + unit < dl);
    this._unitMs = Math.max(this._unitMs || 0, unit);
    if (B.r < tm) return false;
    this._waysPart.delete(pk);
    this._waysMap.set(d, c);
    return c;
  }

  // the per-label timers: CPU ms (performance.now around a step / a sub-step), its unit (the call,
  // or a sliced step's one slice: _unit = the worst of the last UNIT_N, what a call is held to fit),
  // the GL ms inside it (timedGL)
  _acc(label, ms, unit = ms, glms = 0) {
    let e = this.prof.get(label);
    if (!e) {
      this.prof.set(label, (e = { n: 0, ms: 0, max: 0, gl: 0, u: new Float64Array(UNIT_N), un: 0, gn: 0, gms: 0, gc: 0,
        rs: new Float64Array(2 * RATE_N), rn: 0, rq: 0, bn: 0, bms: 0, bc: 0, bs: new Float64Array(2 * RATE_N), brn: 0, bq: 0,
        // the cost model (ruling p8 (1)): its large pieces' samples, the fit (refitted once a sample landed)
        gb: new Float64Array(2 * BIG_N), gbn: 0, m: { a: A_PRIOR, r: planRate(GPU_RATE), ts: 0, big: 0, n: 0 }, dirty: false }));
    }
    e.n++; e.ms += ms; e.gl += glms;
    if (ms > e.max) e.max = ms;
    e.u[e.un++ % UNIT_N] = unit;
  }
  _unit(label) {
    const e = this.prof.get(label);
    if (!e || !e.un) return 0;
    let m = 0;
    for (let k = 0; k < Math.min(e.un, UNIT_N); k++) if (e.u[k] > m) m = e.u[k];
    return m;
  }
  // a GL call on the painter's context (timedGL): state().gl, the step's GL share, the slow calls
  _glAcc(k, ms) {
    let e = this._glc.get(k);
    if (!e) this._glc.set(k, (e = { n: 0, ms: 0, max: 0 }));
    e.n++; e.ms += ms;
    if (ms > e.max) e.max = ms;
    this._stepGl += ms;
    this._glTot += ms;
    if (!this._slowGl || ms > this._slowGl[1]) this._slowGl = [k, ms];
    if (ms > SLOW_GL_MS) {
      this._slowCalls.push({ call: k, ms: Math.round(ms * 100) / 100, label: this._label, at: Math.round(now() / 100) / 10 });
      if (this._slowCalls.length > 40) this._slowCalls.shift();
    }
  }
  // a run.state / run.reset call past STALL_MS (k 'state' | 'reset'): its GL share and slowest call
  _stall(k, ms) {
    if (!(ms > STALL_MS)) return;
    const E = this._stalls[k], c = this._slowGl;
    E.n++;
    if (ms > E.max) E.max = Math.round(ms * 100) / 100;
    E.gl = Math.max(E.gl, Math.round(this._stepGl * 100) / 100);
    this._stalls.last.push({ k, ms: Math.round(ms * 100) / 100, gl: Math.round(this._stepGl * 100) / 100, call: c ? [c[0], Math.round(c[1] * 100) / 100] : null, at: Math.round(now() / 100) / 10 });
    if (this._stalls.last.length > 12) this._stalls.last.shift();
  }
  _slowAdd(label, ms, key) {
    const c = this._slowGl;
    this._slow.push({ label, key, ms: Math.round(ms * 100) / 100, gl_ms: Math.round(this._stepGl * 100) / 100,
      call: c ? [c[0], Math.round(c[1] * 100) / 100] : null, at: Math.round(now() / 100) / 10 });
    if (this._slow.length > 40) this._slow.shift();
  }
  // a step's cost units per GPU ms (history: p5-p7 took the cost over the ms of the label's last
  // RATE_N BACK-TO-BACK samples — a query that follows another of this run's GPU steps: its span
  // starts behind the painter's own work —, else of its last RATE_N samples). A TIME_ELAPSED span
  // is no clean measure on its own: on Chrome's virtualized contexts the first query of a run
  // holds whatever the GPU still ran before it (gpupainter-perf2, one band a frame on a GPU-bound
  // frame: the means fell to 1/15 .. 1/60 of the back-to-back ones and the staging starved), and
  // a quantile reads far FASTER than the work (perf3: a 0.75-quantile rate put the slot estimate at
  // 1/4 of its timer total). The cost formulas are scaled to GPU_RATE (perf1b / perf3 means within
  // 2x of it for every label but the apron).
  // The planning rate (ruling p7 (1), planRate): the back-to-back measure x RATE_SAFE; until a label
  // has RATE_B2B_N of them, every sample's (the first queries' spans hold queued work: slow, never
  // above GPU_RATE), else GPU_RATE — each x RATE_SAFE
  // ROUND 8 (ruling p8 (1)): the rate is the cost model's r (_model: fitModel over the label's
  // back-to-back samples, its large pieces' guard; the prior until RATE_B2B_N of them)
  _rate(label) { return this._model(label).r; }
  // a label's cost model {a, r} now (refitted lazily: once per landed sample batch)
  _model(label) {
    const e = this.prof.get(label);
    const P = this._aPool;
    if (!e) return P.m;
    if (e.dirty || (e.m.n < RATE_B2B_N && e.av !== P.v)) {
      const fit0 = e.m.n >= RATE_B2B_N;
      fitModel(e.bs, e.brn, e.gb, e.gbn, e.m, P.a);
      e.dirty = false; e.av = P.v;
      if (e.m.n >= RATE_B2B_N || fit0) this._aPoolFit();
    }
    return e.m;
  }
  // the pooled a: the median a of the fitted labels (none: A_PRIOR) -> the prior of the labels not fitted yet
  _aPoolFit() {
    let k = 0;
    for (const e of this.prof.values()) if (e.m && e.m.n >= RATE_B2B_N && k < TS_V.length) TS_V[k++] = e.m.a;
    const a = k ? medOf(TS_V, k) : A_PRIOR, P = this._aPool;
    if (Math.abs(a - P.a) < 1e-3) return;
    P.a = a; P.v++; P.m.a = a;
  }
  // a step a fresh run admitted past its cap (packNext OVER), by name
  _atomicAdd(name, est) {
    const A = this._atomic, x = A.by[name] || (A.by[name] = { n: 0, max_est: 0 });
    A.n++; x.n++;
    if (est > x.max_est) x.max_est = Math.round(est * 100) / 100;
  }
  // the run factor now (RUNK: hidden / shown apart)
  _k(hid) { return this._runK[hid ? 'hidden' : 'shown'].k; }
  // a run's GPU landed: its measured back-to-back ms over the estimate of those steps (at the models now)
  // -> the mode's factor (estB / est0: their plan-time estimate / the run's whole — estB >= RUNK_EST and its
  // share >= RUNK_B2B)
  // (every landed run of the mode ticks its clock t: a ratio older than RUNK_AGE runs no longer counts;
  // pre: the run is PREEMPTED (learnPre) — no ratio, counted in K.pre)
  _kAdd(hid, ms, est, estB = est, est0 = estB, pre = false) {
    const K = this._runK[hid ? 'hidden' : 'shown'];
    K.t++;
    if (pre) K.pre++;
    else if (est > 0 && estB >= RUNK_EST && estB >= RUNK_B2B * est0) { const i = K.n++ % RUNK_N; K.r[i] = ms / est; K.at[i] = K.t; }
    K.k = runKAged(K.r, K.at, K.n, K.t);
    if (K.k > K.max) K.max = K.k;
  }
  _rateAdd(rs, n, cost, ms) {
    const i = (n % RATE_N) * 2;
    rs[i] = cost; rs[i + 1] = ms;
    let c = 0, m = 0;
    for (let k = 0; k < Math.min(n + 1, RATE_N) * 2; k += 2) { c += rs[k]; m += rs[k + 1]; }
    return c / m;
  }
  _tqBegin(gl) {
    const X = this.g.tq;
    if (!X || this._tq.length > 64 || gl.getQuery(X.TIME_ELAPSED_EXT, gl.CURRENT_QUERY)) return null;
    const q = gl.createQuery();
    gl.beginQuery(X.TIME_ELAPSED_EXT, q);
    return q;
  }
  // (lead: the run's lead query — no step, no label: its ms is the run's R.lead alone)
  _tqEnd(gl, q, label, cost, J, b2b, rid, lead = false, est = 0, fl = 0) {
    gl.endQuery(this.g.tq.TIME_ELAPSED_EXT);
    this._tq.push({ q, label, cost, J, b2b, rid, lead, est, fl });
    let R = this._runG.get(rid);
    if (!R) this._runG.set(rid, (R = { ms: 0, b2b: 0, lead: 0, left: 0, nq: 0, ended: false, est: 0, est0: 0, estB: 0, cap: 0, atomic: null, bl: [], J, bad: false, q: [], at: now() }));
    R.left++;
    if (!lead) R.nq++;
  }
  // a run's submissions measured (the meter: the GL calls' own arguments) vs the class's caps ->
  // state().submit; its GPU ms (the timer queries) lands later (_tqPoll -> _runDone)
  // (cap: the run's caps; S.play: the shown runs' own maxima, S.beam the hidden ones' — x BEAM_K)
  _subRun(M, sub, est, cap, J, rid, hid, ran, estB = 0, est0 = est, atomic = null) {
    const S = this._sub, units = sub.units, over = M.tex > cap.up_bytes || M.buf > cap.buf_bytes || M.draws > cap.draws || units > cap.units;
    S.runs++;
    if (M.tex > S.tex) S.tex = M.tex;
    if (M.buf > S.buf) S.buf = M.buf;
    if (M.calls > S.calls) S.calls = M.calls;
    if (M.draws > S.draws) S.draws = M.draws;
    if (units > S.units) S.units = units;
    if (sub.est > S.est) S.est = Math.round(sub.est * 100) / 100;
    const B = S[hid ? 'beam' : 'play'];
    B.runs++;
    if (M.tex > B.tex) B.tex = M.tex;
    if (M.buf > B.buf) B.buf = M.buf;
    if (M.draws > B.draws) B.draws = M.draws;
    if (units > B.units) B.units = units;
    if (sub.est > B.est) B.est = Math.round(sub.est * 100) / 100;
    // the GPU estimate past the run's gpu_ms (a first step whose rate fell since it was planned): apart
    if (sub.est > cap.gpu_ms + 1e-9) S.over_est++;
    if (over) {
      // the step labels of the run (ran: label, ms pairs), its phase and estimate
      const labels = [];
      for (let i = 0; i < ran.length; i += 2) if (labels[labels.length - 1] !== ran[i]) labels.push(ran[i]);
      S.over++;
      S.worst = { key: J.key, hid, tex: M.tex, buf: M.buf, draws: M.draws, units, est: Math.round(sub.est * 100) / 100, steps: labels };
      S.overs.push(S.worst);
      if (S.overs.length > 8) S.overs.shift();
    }
    const R = this._runG.get(rid);
    if (R) { R.ended = true; R.est = est; R.est0 = est0; R.estB = estB; R.cap = cap.gpu_ms; R.hid = hid; R.atomic = atomic; if (sub.untimed) R.bad = true; if (!R.left) this._runDone(rid, R); }
  }
  _runDone(rid, R) {
    this._runG.delete(rid);
    if (R.bad) return;
    this._gpuRuns.push([R.ms, R.b2b, R.est, R.lead, R.cap, R.hid ? 1 : 0]);
    if (this._gpuRuns.length > GPU_RUNS) this._gpuRuns.shift();
    // every back-to-back step's measured / plain estimate by label (the (iii) rings) and flags (state().step_flags)
    for (let i = 0; i < R.q.length; i += 5) {
      if (!(R.q[i + 4] & 16) || !(R.q[i + 2] > 0)) continue;
      const k = `${R.q[i]}|${R.q[i + 4] & 15}`, F = this._flagS || (this._flagS = new Map());
      let x = F.get(k);
      if (!x) F.set(k, (x = { n: 0, ms: 0, est: 0, max_r: 0, over2: 0 }));
      const r = R.q[i + 3] / R.q[i + 2];
      x.n++; x.ms += R.q[i + 3]; x.est += R.q[i + 2];
      let L = this._ratioL.get(R.q[i]);
      if (!L) this._ratioL.set(R.q[i], (L = { a: new Float32Array(256), n: 0, i: 0 }));
      L.a[L.i] = r; L.i = (L.i + 1) & 255; if (L.n < 256) L.n++;
      if (r > x.max_r) x.max_r = r;
      if (r > 2) x.over2++;
    }
    // PREEMPTED (preemptStep + learnPre: THE LEARNING SKIPS PREEMPTION) — a SHOWN scored run's spikes; a hidden
    // run learns every sample (the old learning: behind the overlay K stays the guard of the x BEAM_K caps)
    const c = R.nq > 1 ? preemptStep(R.q, R.cap, R.b2b) : null, pre = !R.hid && !!c && learnPre(c, this._medL, this._medR);
    // the back-to-back samples into their labels' models (a preempted run's spiked steps apart)
    for (let i = 0; i < R.q.length; i += 5) {
      const u = R.q[i + 1], ms = R.q[i + 3], p = (R.q[i + 4] & 16) && u > 0 && ms > 0 ? this.prof.get(R.q[i]) : null;
      if (!p || (pre && c.at.includes(i))) continue;
      p.bq = this._rateAdd(p.bs, p.brn++, u, ms); p.gbn = bigAdd(p.gb, p.gbn, u, ms); p.dirty = true;
    }
    // the run factor: this run's measured back-to-back GPU over those steps' estimate at the models NOW (its
    // own samples in: K holds what the fitted model misses — a model refitted past a prior's error no longer
    // inflates it; gate row 'fixed cost': at the plan-time estimate the prior a's first runs held K at 4) —
    // none from a preempted run
    let estN = 0;
    for (let i = 0; i < R.bl.length; i += 2) estN += modelMs(this._model(R.bl[i]), R.bl[i + 1]);
    this._kAdd(R.hid, R.b2b, estN, R.estB, R.est0, pre);
    // every run's maxima, hidden apart from shown (the ruling's bound: a run's measured GPU <= its gpu_ms)
    const X = this._gpuMax[R.hid ? 'hidden' : 'shown'];
    X.n++;
    X.cap = R.cap;
    if (R.ms > X.max_ms) X.max_ms = R.ms;
    if (R.b2b > X.max_b2b) X.max_b2b = R.b2b;
    if (R.lead > X.max_lead) X.max_lead = R.lead;
    if (R.est > X.max_est) X.max_est = R.est;
    if (R.ms > 8) X.over8++;
    if (R.ms > R.cap + 1e-9) X.over++;
    // the shown-cap measure (ruling p8 (3)): a run is scored on its back-to-back samples; one timed step
    // alone is lead-only (the frame's queued work in its span)
    if (R.nq <= 1) { X.lead_only++; if (R.ms > R.cap + 1e-9) X.lead_only_over++; }
    else {
      X.scored++;
      if (R.b2b > R.cap + 1e-9) {
        if (R.atomic) X.over_atomic++;
        else {
          X.over_b2b++;
          if (c) { c.b2b = R.b2b; c.cap = R.cap; c.hid = R.hid ? 1 : 0; this._preC.push(c); if (this._preC.length > 256) this._preC.shift(); }
        }
        // the over's timed steps [label, M units, plain est ms, measured ms, flags (_run; 16 = back-to-back)]
        const r2 = (v) => Math.round(v * 100) / 100, st = [];
        for (let i = 0; i < R.q.length; i += 5) st.push([R.q[i], r2(R.q[i + 1] / 1e6), r2(R.q[i + 2]), r2(R.q[i + 3]), R.q[i + 4]]);
        this._oversB.push({ key: R.J.key, hid: R.hid ? 1 : 0, at: Math.round(R.at / 100) / 10, ms: r2(R.ms), b2b: r2(R.b2b), lead: r2(R.lead), est: r2(R.est), cap: R.cap, atomic: R.atomic, steps: st });
        if (this._oversB.length > 32) this._oversB.shift();
      }
    }
    const J = R.J;
    J.gRuns = (J.gRuns || 0) + 1;
    if (R.ms > (J.gRunMax || 0)) J.gRunMax = R.ms;
    if (J.rec) { J.rec.gpu_runs = J.gRuns; J.rec.gpu_run_max = Math.round(J.gRunMax * 100) / 100; }
  }
  _tqPoll(gl) {
    const X = this.g && this.g.tq;
    if (!X || !this._tq.length) return;
    const dis = gl.getParameter(X.GPU_DISJOINT_EXT);
    while (this._tq.length) {
      const e = this._tq[0];
      if (!gl.getQueryParameter(e.q, gl.QUERY_RESULT_AVAILABLE)) break;
      this._tq.shift();
      const R = this._runG.get(e.rid);
      if (!dis && e.lead) {
        if (R) R.lead += gl.getQueryParameter(e.q, gl.QUERY_RESULT) / 1e6;
      } else if (!dis) {
        const ms = gl.getQueryParameter(e.q, gl.QUERY_RESULT) / 1e6, p = this.prof.get(e.label);
        if (p && e.cost > 0 && ms > 0) {
          p.gn++; p.gms += ms; p.gc += e.cost; p.rq = this._rateAdd(p.rs, p.rn++, e.cost, ms);
          // (a back-to-back sample enters the label's model at its run's end: _runDone, unless preempted)
          if (e.b2b) { p.bn++; p.bms += ms; p.bc += e.cost; }
        }
        if (e.J) {
          e.J.gms += ms;
          if (e.b2b) e.J.gb += ms;
          if (e.J.rec) { e.J.rec.gpu_ms = Math.round(e.J.gms * 100) / 100; e.J.rec.gpu_ms_b2b = Math.round(e.J.gb * 100) / 100; }
        }
        // the run's own total (its first query's span may hold GPU work queued before the run: b2b = the rest)
        if (R) { R.ms += ms; if (e.b2b && e.cost > 0 && ms > 0) { R.b2b += ms; R.bl.push(e.label, e.cost); } else if (e.b2b) R.b2b += ms; R.q.push(e.label, e.cost, e.est, ms, e.fl | (e.b2b ? 16 : 0)); }
      } else if (R) R.bad = true;
      if (R && --R.left <= 0 && R.ended) this._runDone(e.rid, R);
      gl.deleteQuery(e.q);
    }
  }


  _resvOk(J) {
    const vt = this.vt, t = vt._resv.get(J.key);
    return !!t && t.slot === J.resv.slot && t.p === J.resv.p && vt.pools[t.p] === J.resv.pool && vt.slots[t.p][t.slot] === J.key;
  }

  _abandon(why) {
    const J = this._job;
    if (!J) return;
    this._free(J);
    this.vt.release(J.key, false);
    this._job = null;
    this.stats.abandoned++;
    if (why === 'lost') this.stats.dropped++;
  }

  // every per-job GL object
  _free(J) {
    const g = this.g, gl = g && g.gl, T = J.T;
    if (!gl) return;
    for (const k of Object.keys(T)) {
      const v = T[k];
      if (v && v.t) g.free(v);
      else if (v && v.vao) { gl.deleteVertexArray(v.vao); gl.deleteBuffer(v.vb); if (v.ib) gl.deleteBuffer(v.ib); }
      else if (Array.isArray(v)) for (const n of v) { gl.deleteVertexArray(n.vao); gl.deleteBuffer(n.vb); if (n.ib) gl.deleteBuffer(n.ib); }
      delete T[k];
    }
    g.bytes = 0;
  }

  // ---------------------------------------------------------------- the stages as steps (CPU prep, GL passes / bands)
  // the job's pool texture (its reservation still held)
  _layer(J) {
    if (!this._resvOk(J)) throw new Error('reservation lost');
    const t = this.getRenderer().properties.get(J.resv.pool).__webglTexture;
    if (!t) throw new Error('pool texture not initialised');
    return t;
  }
  // the mips 1 .. 9: from the scratch's level L - 1 (2 x 2 box in linear: the sRGB fetch / store).
  // MIP-PINGPONG: level L is written to the OTHER scratch of the pair (tMip even levels, tMip1 odd) —
  // Chrome's validating decoder (Android / Quest) drops a draw that samples ANY texture attached to the
  // draw framebuffer, whatever the levels (INVALID_OPERATION "Source and destination textures of the
  // draw are the same"); ANGLE (desktop) checks levels and drew it
  // a step a level (the timer queries: ~1.5 ms of GPU-process time a slot on desktop-igpu, mostly
  // the per-pass fixed cost)
  _mips(J, add) {
    const W = this.world, gl = this.g.gl, P = this.P, LM = Math.log2(SLOT_PX);
    const fbo = (cols, fn) => { const f = this.g.fbo(cols, null); try { fn(f); } finally { gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.deleteFramebuffer(f); } };
    for (let l = 1; l <= LM; l++) {
      add('mips', SLOT_PX * SLOT_PX * COST.mips, () => {
        const t = this._layer(J), n = SLOT_PX >> l, src = l & 1 ? W.tMip : W.tMip1, dst = l & 1 ? W.tMip1 : W.tMip;
        gl.bindTexture(gl.TEXTURE_2D, src.t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, l - 1);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, l - 1);
        fbo([{ t, level: l, layer: J.resv.layer }, { t: dst.t, level: l }], (f) => this.g.pass(P.mip, f, n, n, { uS: src }));
        gl.bindTexture(gl.TEXTURE_2D, src.t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAX_LEVEL, LM);
      });
    }
  }
  // A SHIPPED SLOT's job: its bitmap's rows into the pool layer's level 0 and the scratch chain's (the bytes the light
  // pass writes to both: MRT), in row bands of the class's up_bytes, then this pass's mips
  _planShip(J) {
    const g = this.g, gl = g.gl, W = this.world, id = `s:${J.key}`, e = this.cache.get(id), steps = [];
    const add = (label, cost, fn, draws) => steps.push({ label, cost, fn, draws });
    if (!e || e.st !== 'ok' || !e.data || !e.data.bmp) throw new Error(`shipped slot ${J.key}: not cached`);
    const bmp = (J.bmp = e.data.bmp), n = planChunks(4 * SLOT_PX, g.caps().up_bytes);
    for (let y0 = 0; y0 < SLOT_PX; y0 += n) {
      const y1 = Math.min(SLOT_PX, y0 + n);
      add('slot.up', 0, () => {
        gl.pixelStorei(gl.UNPACK_SKIP_ROWS, y0);
        gl.bindTexture(gl.TEXTURE_2D_ARRAY, this._layer(J));
        gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, 0, y0, J.resv.layer, SLOT_PX, y1 - y0, 1, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
        gl.bindTexture(gl.TEXTURE_2D_ARRAY, null);
        gl.bindTexture(gl.TEXTURE_2D, W.tMip.t);
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, y0, SLOT_PX, y1 - y0, gl.RGBA, gl.UNSIGNED_BYTE, bmp);
        gl.bindTexture(gl.TEXTURE_2D, null);
        gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
      });
    }
    this._mips(J, add);
    return steps;
  }

  _plan(J) {
    if (this._geom(J.key).ship) return this._planShip(J);
    const G = this._geom(J.key), W = this.world, g = this.g, gl = g.gl, P = this.P, T = J.T;
    g.bytes = 0; g.peak = 0;
    // a step: its GPU cost units (a number or fn), fn(deadline), its draw calls (default: 1 when it costs)
    const steps = [], add = (label, cost, fn, draws) => steps.push({ label, cost, fn, draws });
    // A RECT STEP (ruling p8 (2)): a pass's piece [y0, y1) x [x0, x1) of per units a texel (on: the
    // pass's gate, 0 units while it is off), rf(y0, y1, x0, x1, first, last) — split at run time
    // (packNext / rectNext: the step stays at J.i until its rect is done; first = its pass's first
    // portion, last = the last one)
    const piece = (label, [y0, y1, x0, x1, c], rf, first, last, draws, on = null) =>
      steps.push({ label, per: c / ((y1 - y0) * (x1 - x0)), y0, y1, x0, x1, cx: x0, rf, f: first, l: last, draws, on });
    const PW = G.PW, cr = G.cr, cap = g.caps();
    // every pass's pieces (slotPieces: planRect at the label's cost model, each <= the class's per-run
    // units / GPU ms / draws) -> a rect step a piece: rf(y0, y1, x0, x1, first, last). No run factor
    // here (ruling p8 (2): the run applies it, re-splitting a piece its run has no room for)
    const PC = slotPieces(G, cap, (l) => this._model(l).r, false, (l) => this._model(l).a);
    const band = (label, fn, on = null, draws, pcs = PC[label] || []) => {     // (none: the slot has no such pass)
      pcs.forEach((p, i) => piece(label, p, fn, i === 0, i === pcs.length - 1, draws, on));
    };
    const fbo = (cols, depth, fn) => {
      const f = g.fbo(cols, depth);
      try { fn(f); } finally { gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.deleteFramebuffer(f); }
    };
    const RU = () => ({ uTexel: G.texel, uM: G.m, uPW: [PW, PW], uX0: [G.x0, G.z0] });
    const U = {};                                 // the uniforms the raster programs share (prep fills it)

    // a CPU loop over [0, n) in chunks, sliced to the step's deadline: a chunk starts while the
    // last one's time still fits (MORE until done; the worst chunk = the label's unit)
    const sliced = (n, chunk, body) => {
      let i = 0;
      return (dl) => {
        let unit = 0;
        do {
          const t0 = now(), e = Math.min(n, i + chunk);
          body(i, e);
          i = e;
          unit = Math.max(unit, now() - t0);
        } while (i < n && now() + unit < dl);
        this._unitMs = unit;
        return i < n ? MORE : undefined;
      };
    };
    // ... in chunks of about SLICE_CELLS cells (whole rows of rowCells)
    const slicedRows = (n, rowCells, body) => sliced(n, Math.max(1, Math.floor(SLICE_CELLS / rowCells)), body);
    // a texture's (Tf()) rows from its data (Df()) in calls of <= the class's up_bytes (planChunks;
    // sliced: MORE until every row is up); data row y -> texture row y + dy
    const upload = (Tf, Df, h, rowBytes, dy = 0) => sliced(h, planChunks(rowBytes, cap.up_bytes), (y0, y1) => g.sub(Tf(), Df(), y0, y1, dy));

    // ---- prep: the class window (its 512 m tiles mapped once per world, sliced steps; the window
    // from their row slices), the record table, the noise frames, the residual / s2 frames
    const c = G.cls, ctm = this.meta.surface.tiles.tile_m;
    let cdata = null;                             // the window (made at its first step: no allocation in the plan)
    const cd = () => cdata || (cdata = new Uint8Array(c.w * c.h));
    if (G.fine1) {
      for (let ty = Math.floor(c.z0 / ctm); ty <= Math.floor((c.z0 + c.h - 1) / ctm); ty++) {
        for (let tx = Math.floor(c.x0 / ctm); tx <= Math.floor((c.x0 + c.w - 1) / ctm); tx++) add('cls.tile', 0, (dl) => (this._clsTile(tx, ty, dl) ? undefined : MORE));
      }
      // the window's rows from the mapped tiles (compact class | 0x80 sheet bed)
      const tx0 = Math.floor(c.x0 / ctm), tx1 = Math.floor((c.x0 + c.w - 1) / ctm);
      add('prep.cls', 0, slicedRows(c.h, c.w, (k0, k1) => {
        for (let k = k0; k < k1; k++) {
          const Z = c.z0 + k, ty = Math.floor(Z / ctm);
          for (let tx = tx0; tx <= tx1; tx++) {
            const m = this._clsTile(tx, ty), i0 = Math.max(c.x0, tx * ctm), i1 = Math.min(c.x0 + c.w, (tx + 1) * ctm), o = (Z - ty * ctm) * ctm - tx * ctm;
            cd().set(m.subarray(o + i0, o + i1), k * c.w + (i0 - c.x0));
          }
        }
      }));
    } else {
      // the coarse grid as surface.js holds it (no copy: read only — a 7 MB slice() was 6-8 ms)
      add('cls.coarse', 0, () => { if (!this._coarse) this._coarse = this.surf.field(c.S, false); });
      add('prep.coarse', 0, slicedRows(c.h, c.w, (k0, k1) => {
        const f = this._coarse, lut = W.lut, fw = f.w, fd = f.data, cdata = cd();
        for (let k = k0; k < k1; k++) {
          const Z = clamp(c.z0 + k, 0, f.h - 1), o = k * c.w, r = Z * fw;
          for (let i = 0; i < c.w; i++) { const X = c.x0 + i; cdata[o + i] = lut[fd[r + (X < 0 ? 0 : X >= fw ? fw - 1 : X)]]; }
        }
      }));
    }
    add('prep.clsup', 0, () => {
      T.cls = g.tex('R8UI', c.w, c.h);
      Object.assign(U, { uCls: T.cls, uClsW: [c.x0, c.z0, c.S, 0], uWorld: [W.W, W.H] });
    });
    add('prep.clsup', 0, upload(() => T.cls, cd, c.h, c.w));
    // the genome record table at this texel (the worker's constants, else genomeTexelConsts here)
    // + its octave frames at the slot origin
    // (a record a slice: one call of every record was up to 2.3 ms)
    const rkeys = [...W.recRow.keys()];
    let rec = null;
    add('prep.rec', 0, sliced(rkeys.length, 1, (r0, r1) => {
      if (!rec) rec = new Float32Array(RW * 4 * Math.max(1, rkeys.length));
      genomeRecRows(rec, rkeys, W.gP, W.gC, W.recs, G.texel, G.x0, G.z0, r0, r1);
    }));
    add('prep.rec', 0, () => {
      if (!rec) rec = new Float32Array(RW * 4);
      T.rec = g.tex('RGBA32F', RW, Math.max(1, rkeys.length), rec);
      const { uFM, uFO, uFS } = fixedFrames(G.x0, G.z0);
      const R = W.res, S2 = W.s2;
      Object.assign(U, {
        uGrad: W.tGrad, uGz: W.tGz, uRec: T.rec, uFM, uFO, uFS,
        uKeep: [octaveKeep(47.0, G.texel), octaveKeep(21.0, G.texel), octaveKeep(9.5, G.texel), octaveKeep(4.3, G.texel)],
        uCB: W.CB, uCP: W.CP, uCI: W.CI, uNC: W.raw.length, uGravelC: W.gravelC,
        uRes: W.tRes, uResN: R ? [R.w, R.h, 1] : [1, 1, 0], uResO: R ? [G.x0 - R.x0, G.z0 - R.z0, R.cell] : [0, 0, 1],
        uS2: W.tS2, uS2N: S2 ? [S2.w, S2.h, 1] : [1, 1, 0], uS2O: S2 ? [G.x0 - S2.x0, G.z0 - S2.z0, S2.cell] : [0, 0, 1],
        uAnchor: [W.anchor, W.grassSat], uSportPh: ((G.x0 % 8) + 8) % 8, uR: G.r, uSealedWin: G.fine1 ? 0 : 1,
        uExG: W.tExG, uExL: W.tExL, ...this._tileU(G),
      });
    });

    // ---- 0. heights: the level-j nodes, TIN -> row fill -> column fill
    // the nodes in groundref nodesOver's order (ty, tx); their GPU buffers kept across slots
    // (_nodeBuf: a step each, its upload in pieces)
    const nid = this._inputs(J.key).filter((id) => id.startsWith('n:') && this._data(id));
    nid.sort((a, b) => { const p = this._data(a), q = this._data(b); return p.ty - q.ty || p.tx - q.tx; });
    if (!nid.length) throw new Error(`no level-${G.j} ground node under ${J.key}`);
    const yref = Math.floor(Math.min(...nid.map((id) => this._data(id).ymin)));
    const nodes = [];
    this._nodePin = new Set(nid);
    for (const id of nid) {
      add('nodes', 0, (dl) => {
        const d = this._data(id);
        if (!d) throw new Error(`ground node ${id} evicted under ${J.key}`);
        const N = this._nodeBuf(id, d, dl);
        if (!N) return MORE;
        nodes.push({ N, off: [d.tx * G.S - G.x0, d.ty * G.S - G.z0, N.yn - yref] });
      });
    }
    // TIN into (w x h) of texel px whose (0, 0) corner is local (ox, oz): the piece [x0, x1) x
    // [y0, y1) (the scissor; a piece's first group clears it), the nodes [n0, n1) (a draw each;
    // uDepth = the node's place in the whole list: first triangle wins across groups as in one draw sequence)
    const tin = (dst, depth, w, h, ox, oz, px, y0, y1, x0, x1, n0, n1) => fbo([dst], depth, () => {
      gl.viewport(0, 0, w, h);
      gl.enable(gl.SCISSOR_TEST);
      gl.scissor(x0, y0, x1 - x0, y1 - y0);
      if (n0 === 0) { gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 0]); gl.clearBufferfv(gl.DEPTH, 0, [1]); }
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LESS);
      g.use(P.tin, { uMap: [ox + TIN_EX * px, oz + TIN_EZ * px, 2 / (w * px), 2 / (h * px)], uYref: yref, uPx: px, uE: [TIN_EX, TIN_EZ] });
      const ud = P.tin.u.get('uDepth').loc, uo = P.tin.u.get('uOff').loc;
      for (let k = n0; k < Math.min(n1, nodes.length); k++) {
        const n = nodes[k];
        gl.uniform1f(ud, ((k + 0.5) / nodes.length) * 2 - 1);
        gl.uniform3fv(uo, n.off);
        gl.bindVertexArray(n.N.vao);
        gl.drawElements(gl.TRIANGLES, n.N.count, gl.UNSIGNED_INT, 0);
      }
      gl.bindVertexArray(null);
      gl.disable(gl.DEPTH_TEST);
      gl.disable(gl.SCISSOR_TEST);
    });
    // the TIN as steps: its pieces x node groups of <= the class's draws a run
    const NG = Math.max(1, cap.draws), nN = nid.length;
    const tinSteps = (label, fn, on = null) => {
      const pcs = PC[label];
      pcs.forEach(([y0, y1, x0, x1, c], i) => {
        for (let n0 = 0; n0 < nN; n0 += NG) {
          const n1 = Math.min(nN, n0 + NG);
          piece(label, [y0, y1, x0, x1, (c * (n1 - n0)) / nN], (a, b, e, f, first, last) => fn(a, b, e, f, n0, n1, first, last), i === 0 && n0 === 0, i === pcs.length - 1 && n1 === nN, n1 - n0, on);
        }
      });
    };
    // the row fill (a -> b), then the column fill (b -> a), each in its pieces (a column fill piece
    // reads b's whole columns: every row-fill piece is in before the first)
    const fills = (label, a, b, w, h, on = null) => {
      band(`${label}.row`, (y0, y1, x0, x1) => fbo([b()], null, (f) => g.pass(P.rowfill, f, w, h, { uT: a(), uN: [w, h] }, y0, y1, x0, x1)), on);
      band(`${label}.col`, (y0, y1, x0, x1) => fbo([a()], null, (f) => g.pass(P.colfill, f, w, h, { uT: b(), uN: [w, h] }, y0, y1, x0, x1)), on);
    };
    tinSteps('heights', (y0, y1, x0, x1, n0, n1, first, last) => {
      if (first) { T.H = g.tex('RGBA32F', PW, PW); T.B = g.tex('RGBA32F', PW, PW); T.D = g.tex('D32F', PW, PW); }
      tin(T.H, T.D, PW, PW, -G.hm, -G.hm, G.texel, y0, y1, x0, x1, n0, n1);
      if (last) g.free(T.D);
    });
    fills('heights', () => T.H, () => T.B, PW, PW);
    add('heights.free', 0, () => {
      g.free(T.B);                                // the fill scratch (a target again for the ways / apron)
      U.uH = T.H;
    });

    // ---- 2a. the ways' CPU half first (its answer comes back while the albedo bands run): the
    // field window (8 bytes a cell: P0 RGBA, P1 RGBA) from the ways tiles' interleaved cells (once
    // per world, sliced steps) in row slices, uploaded, then to the worker (groundcpu waysCpu:
    // waysTile + the coarse marks) — or, without one, waysCpu here at 2b
    let wt = null, wq = null, field = null, mk = null;
    const K = ggWorker();
    if (G.ways) {
      const w = G.ways, tm = W.wayP.tile_m, tx0 = Math.floor(w.x0 / tm), tx1 = Math.floor((w.x0 + w.w - 1) / tm);
      for (let ty = Math.floor(w.z0 / tm); ty <= Math.floor((w.z0 + w.h - 1) / tm); ty++) {
        for (let tx = tx0; tx <= tx1; tx++) add('ways.tilecells', 0, (dl) => (this._waysCells(tx, ty, dl) === false ? MORE : undefined));
      }
      const E = new Uint32Array(Uint8Array.from(W.wayP.empty).buffer);
      let fu = null;
      add('ways.field', 0, slicedRows(w.h, w.w, (k0, k1) => {
        if (!fu) { field = new Uint8Array(w.w * w.h * 8); fu = new Uint32Array(field.buffer); }
        for (let k = k0; k < k1; k++) {
          const Z = w.z0 + k, ty = Math.floor(Z / tm);
          for (let tx = tx0; tx <= tx1; tx++) {
            const cells = this._waysCells(tx, ty), i0 = Math.max(w.x0, tx * tm), i1 = Math.min(w.x0 + w.w, (tx + 1) * tm);
            const o = 2 * (k * w.w + (i0 - w.x0));
            if (cells) { const q = (Z - ty * tm) * tm - tx * tm; fu.set(cells.subarray(2 * (q + i0), 2 * (q + i1)), o); }
            else for (let e = o; e < o + 2 * (i1 - i0); e += 2) { fu[e] = E[0]; fu[e + 1] = E[1]; }
          }
        }
      }));
      add('ways.up', 0, () => { T.wf = g.tex('RGBA8UI', 2 * w.w, w.h); });
      add('ways.up', 0, upload(() => T.wf, () => field, w.h, 8 * w.w));
      if (K) add('ways.post', 0, () => { if (K.dead) return; wq = ask(K, { op: 'ways', field, W1: w.w, H1: w.h, x0: G.x0 - w.x0, z0: G.z0 - w.z0, S: G.S, px: SLOT_PX }, [field.buffer]); field = null; });
    }
    // WAYS-Q4 (past the 1 m field): the 4 m mass window from its tiles (Q4.B bytes a cell = three RGBA8UI texels, so
    // the cells ARE the texture's rows), uploaded, then to the worker (groundcpu q4Cpu: waysTile) — or q4Cpu at 2b
    if (G.q4) {
      const w = G.q4, n = W.wayP.q4.px, tx0 = Math.floor(w.x0 / n), tx1 = Math.floor((w.x0 + w.w - 1) / n), CB = Q4.B;
      add('ways.q4', 0, slicedRows(w.h, w.w, (k0, k1) => {
        if (!field) field = new Uint8Array(w.w * w.h * CB);
        for (let k = k0; k < k1; k++) {
          const Z = w.z0 + k, ty = Math.floor(Z / n);
          for (let tx = tx0; tx <= tx1; tx++) {
            const d = this._data(`q:${tx}_${ty}`), i0 = Math.max(w.x0, tx * n), i1 = Math.min(w.x0 + w.w, (tx + 1) * n);
            for (let i = i0; i < i1; i++) {
              const o = CB * (k * w.w + i - w.x0);
              if (!d) { field.fill(0, o, o + CB); field[o + 8] = 255; continue; }   // an absent tile: no way (rmin 255)
              const q = (Z - ty * n) * n + i - tx * n;
              for (let p = 0; p < 3; p++) field.set(d.data.subarray(4 * (p * n * n + q), 4 * (p * n * n + q) + 4), o + 4 * p);
            }
          }
        }
      }));
      add('ways.up', 0, () => { T.qf = g.tex('RGBA8UI', 3 * w.w, w.h); });
      add('ways.up', 0, upload(() => T.qf, () => field, w.h, CB * w.w));
      if (K) add('ways.post', 0, () => { if (K.dead) return; wq = ask(K, { op: 'q4', cells: field, w: w.w, h: w.h, x0: G.x0 - Q4.M * w.x0, z0: G.z0 - Q4.M * w.z0, S: G.S, px: SLOT_PX }, [field.buffer]); field = null; });
    }

    // ---- 1. albedo -> A
    add('albedo.alloc', 0, () => { T.A = g.tex('RGBA32F', PW, PW); });
    band('albedo', (y0, y1, x0, x1) => fbo([T.A], null, (f) => g.pass(P.albedo, f, PW, PW, { ...RU(), ...U }, y0, y1, x0, x1)));

    // ---- 2b. the ways (1 m class tiles, groundways waysTile live): the worker's answer (WAIT
    // until it lands), the marks' texture, the uniforms
    add('ways.cpu', 0, () => {
      if (!G.ways && !G.q4) return undefined;
      const w = G.ways || G.q4;
      let res;
      if (wq) {
        if (!wq.done) return WAIT;
        if (wq.error) throw new Error(`groundgen worker: ${wq.error}`);
        res = wq.out;
      } else if (G.q4) res = q4Cpu(W.ST0, field, w.w, w.h, G.x0 - Q4.M * w.x0, G.z0 - Q4.M * w.z0, G.S, SLOT_PX);
      else res = waysCpu(W.ST0, field, w.w, w.h, G.x0 - w.x0, G.z0 - w.z0, G.S, SLOT_PX);
      field = null;
      if (!res) return undefined;
      wt = res;
      // the regime's other feed a 1 x 1 stand-in (every sampler bound): the 1 m field, or the 4 m mass
      if (G.q4) T.wf = g.tex('RGBA8UI', 1, 1, new Uint8Array(4));
      else T.qf = g.tex('RGBA8UI', 3, 1, Uint8Array.of(0, 0, 0, 0, 0, 0, 0, 0, 255, 0, 0, 0));
      // the mark cells (read by the coarse regime only): zero, + their bounding rows uploaded
      if (res.mq && res.mq.length) {
        let qa = Infinity, qb = -1;
        for (const q of res.mq) { if (q < qa) qa = q; if (q > qb) qb = q; }
        const ra = Math.floor(qa / w.w), rb = Math.floor(qb / w.w), mc = new Float32Array((rb - ra + 1) * w.w * 4);
        for (let i = 0; i < res.mq.length; i++) mc.set(res.mv.subarray(4 * i, 4 * i + 4), 4 * (res.mq[i] - ra * w.w));
        T.wm = g.tex('RGBA32F', w.w, w.h);
        fbo([T.wm], null, () => { gl.viewport(0, 0, w.w, w.h); gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 0]); });
        mk = { mc, ra, n: rb - ra + 1 };            // its rows go up at ways.mk (row bands: one call of the whole was 2.5-6 ms)
      } else T.wm = g.tex('RGBA32F', 1, 1, new Float32Array(4));
      const tb = new Float32Array(16), ta = new Float32Array(16).fill(1);
      let nk = 0;
      for (const [bv, ka] of wt.treadKa) { if (nk < 16) { tb[nk] = bv; ta[nk] = ka; nk++; } }
      const hc = WAYSLOOK.colour.host, lm = W.ST0.luma;
      Object.assign(U, {
        uWF: T.wf, uWWin: G.q4 ? [0, 0, 1, 1] : [w.x0, w.z0, w.W1, w.H1], uWMk: T.wm, uMarkOn: wt.mark ? 1 : 0, uFine: wt.fine ? 1 : 0,
        uQ4: T.qf, uQWin: G.q4 ? [w.x0, w.z0, w.QW, w.QH] : [0, 0, 1, 1], uQOn: G.q4 ? 1 : 0,
        uWT0: [wt.kc, wt.pk, wt.amp, wt.hh], uWT1: [wt.ea, wt.eb, wt.wa, wt.wb], uWN: wt.wn, uTKb: tb, uTKa: ta, uTKn: nk,
        uBare: W.bare, uTree: [W.ST0.treeline ?? 0, W.ST0.treeSoft ?? 1],
      });
      J.rad = [Math.max(1, roundEven(hc.radius_m / G.texel)), lm ? Math.max(1, roundEven(lm.radius_m / G.texel)) : 0];
      return undefined;
    });
    // the mark rows in calls of <= the class's up_bytes (data row 0 = texture row mk.ra)
    if (G.ways) {
      const w = G.ways;
      let upm = null;
      add('ways.mk', 0, (dl) => {
        if (!mk) return undefined;
        if (!upm) upm = upload(() => T.wm, () => mk.mc, mk.n, 16 * w.w, mk.ra);
        const r = upm(dl);
        if (r !== MORE) mk = null;
        return r;
      });
    }
    const wOn = () => !!wt;
    band('ways.prep', (y0, y1, x0, x1, first) => {
      if (!wt) return;
      if (first) { T.P0 = g.tex('RGBA32F', PW, PW); T.P1 = g.tex('RGBA32F', PW, PW); T.P2 = g.tex('RGBA32F', PW, PW); }
      fbo([T.P0, T.P1, T.P2], null, (f) => g.pass(P.wprep, f, PW, PW, { ...RU(), ...U, uA: T.A }, y0, y1, x0, x1));
    }, wOn);
    // the row sums a target at a time (row bands), each prep target freed after its own (the
    // scratch peak: 6 raster targets, not 8)
    for (let w2 = 0; w2 < 3; w2++) {
      band('ways.boxh', (y0, y1, x0, x1, first, last) => {
        if (!wt) return;
        if (first) T[`S${w2}`] = g.tex('RGBA32F', PW, PW);
        fbo([T[`S${w2}`]], null, (f) => g.pass(P.wboxh, f, PW, PW, { uP: T[`P${w2}`], uPW: [PW, PW], uRad: J.rad, uW: w2 }, y0, y1, x0, x1));
        if (!last) return;
        g.free(T[`P${w2}`]);
        if (w2 === 2) T.B = g.tex('RGBA32F', PW, PW);
      }, wOn);
    }
    band('ways.path', (y0, y1, x0, x1) => {
      if (!wt) return;
      fbo([T.B], null, (f) => g.pass(P.wpath, f, PW, PW, { ...RU(), ...U, uA: T.A, uS0: T.S0, uS1: T.S1, uS2: T.S2, uRad: J.rad }, y0, y1, x0, x1));
    }, wOn);
    add('ways.free', 0, () => {
      g.free(T.wf); g.free(T.wm); g.free(T.qf);
      if (!wt) return;
      g.free(T.S0); g.free(T.S1); g.free(T.S2);
      [T.A, T.B] = [T.B, T.A];
    });

    // ---- 3. the apron (past the class map)
    if (!G.allIn) {
      band('apron', (y0, y1, x0, x1, first, last) => {
        const b = W.bands;
        if (first && (!T.B || !T.B.t)) T.B = g.tex('RGBA32F', PW, PW);
        fbo([T.B], null, (f) => g.pass(P.apron, f, PW, PW, {
          ...RU(), ...U, uA: T.A, uInX: G.inA, uRa: G.ra, uJit: b.jitter_m,
          uBand: [...b.meadow_m, ...b.turf_m, ...b.turf_out_m, ...b.scree_m, ...b.rock_m, ...b.snow_m],
        }, y0, y1, x0, x1));
        if (last) [T.A, T.B] = [T.B, T.A];
      });
    }
    add('apron.free', 0, () => { g.free(T.B); });

    // ---- 4. the crown grid -> fsun / fsky
    // CROWN SKIP (painter-throughput, crownSkip): a slot whose window holds no crown disc (nd 0, known at
    // crowns.discs) runs none of the crown passes — its grid would be fsun = fsky = 1 exactly, the crowns-off
    // light (uCG.w 0) — so every crown pass is gated on nd (cOn) and the light reads crOn()
    let nd = 0;
    const cOn = () => !crownSkip(nd), crOn = () => cr.on && cOn();
    if (cr.on) {
      const nx = cr.nx, nz = cr.nz, NC = nx * nz, ox = cr.X0 - G.x0, oz = cr.Z0 - G.z0;
      let dyref = 0, disc = null, vo = 0;
      // the discs of the window's crown / shrub packs on the level-j drawn ground -> the instance
      // array (groundcpu discsCpu: groundref crownDiscsOf, its order): the worker's — asked at the
      // scan ahead of the job (PRE_DISCS) or now — else made here (one call)
      const K2 = ggWorker(), pq = this._pre.get(J.key);
      this._pre.delete(J.key);
      const dq = K2 ? (pq && pq.h) || this._discsAsk(K2, J.key) : null;
      add('crowns.discs', 0, () => {
        if (dq) {
          if (!dq.done) return WAIT;
          if (dq.error) throw new Error(`groundgen worker: ${dq.error}`);
          disc = dq.out;
        } else disc = this._discsHere(J.key);
        nd = disc.nd; dyref = disc.dyref;
        return undefined;
      });
      // uploaded in pieces (+ the march steps' table)
      add('crowns.up', 0, (dl) => {
        if (!cOn()) { disc = null; return undefined; }
        if (nd) {
          if (!T.discs) {
            const vao = gl.createVertexArray(), vb = gl.createBuffer(), A = P.disc.attr;
            gl.bindVertexArray(vao);
            gl.bindBuffer(gl.ARRAY_BUFFER, vb);
            gl.bufferData(gl.ARRAY_BUFFER, disc.a.byteLength, gl.STATIC_DRAW);
            gl.enableVertexAttribArray(A.aC);
            gl.vertexAttribPointer(A.aC, 4, gl.FLOAT, false, 28, 0);
            gl.vertexAttribDivisor(A.aC, 1);
            gl.enableVertexAttribArray(A.aT);
            gl.vertexAttribPointer(A.aT, 3, gl.FLOAT, false, 28, 16);
            gl.vertexAttribDivisor(A.aT, 1);
            gl.bindVertexArray(null);
            T.discs = { vao, vb };
          }
          vo = this._bufUp(T.discs.vb, disc.a, vo, dl);
          if (vo < disc.a.byteLength) return MORE;
        }
        disc = null;
        const sa = new Float32Array(4 * Math.max(1, cr.steps.length));
        cr.steps.forEach(([sx, sy, thr], i) => sa.set([sx, sy, thr, 0], 4 * i));
        T.steps = g.tex('RGBA32F', Math.max(1, cr.steps.length), 1, sa);
        return undefined;
      });
      // the grid's ground: (h, cov) only (the obj pass reads h; the fills h + cov) -> RG32F
      tinSteps('crowns.tin', (y0, y1, x0, x1, n0, n1, first) => {
        if (first) { T.Ga = g.tex('RG32F', nx, nz); T.Gb = g.tex('RG32F', nx, nz); T.GD = g.tex('D32F', nx, nz); }
        tin(T.Ga, T.GD, nx, nz, ox, oz, cr.cell, y0, y1, x0, x1, n0, n1);
      }, cOn);
      fills('crowns', () => T.Ga, () => T.Gb, nx, nz, cOn);
      // the discs: the tallest key per cell (pieces: the scissor; each piece draws every disc)
      band('crowns.key', (y0, y1, x0, x1, first, last) => {
        if (first) { g.free(T.Gb); T.CK = g.tex('R32F', nx, nz); }
        fbo([T.CK], T.GD, () => {
          gl.viewport(0, 0, nx, nz);
          gl.enable(gl.SCISSOR_TEST);
          gl.scissor(x0, y0, x1 - x0, y1 - y0);
          gl.clearBufferfv(gl.COLOR, 0, [-1, 0, 0, 0]);
          gl.clearBufferfv(gl.DEPTH, 0, [0]);
          if (nd) {
            gl.enable(gl.DEPTH_TEST);
            gl.depthFunc(gl.GREATER);
            g.use(P.disc, { uMap: [cr.cell, 0, 2 / (nx * cr.cell), 2 / (nz * cr.cell)], uCell: cr.cell });
            gl.bindVertexArray(T.discs.vao);
            gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, DISC_VERTS, nd);
            gl.bindVertexArray(null);
            gl.disable(gl.DEPTH_TEST);
          }
          gl.disable(gl.SCISSOR_TEST);
        });
        if (last) { g.free(T.GD); T.CG = g.tex('RGBA32F', nx, nz); }
      }, cOn);
      let UO = null, CGc = null;                   // (made at the first band: no object a call)
      band('crowns.obj', (y0, y1, x0, x1) => fbo(CGc || (CGc = [T.CG]), null, (f) => g.pass(P.obj, f, nx, nz, UO || (UO = { uK: T.CK, uG: T.Ga, uYref: dyref }), y0, y1, x0, x1)), cOn);
      add('crowns.objfree', 0, () => {
        if (!cOn()) return;
        g.free(T.CK); g.free(T.Ga);
        T.VO = g.tex('R32F', nx, nz);
      });
      band('crowns.march', (y0, y1, x0, x1) => fbo([T.VO], null, (f) => g.pass(P.march, f, nx, nz,
        { uCG: T.CG, uSteps: T.steps, uNS: cr.steps.length, uIn: [cr.ja, cr.jb, cr.la, cr.lb], uN: [nx, nz] }, y0, y1, x0, x1)), cOn);
      // the sky box sums (slotPieces' sky: its bands of the inner rows, each band's H0 / H1 row sums
      // then its FS rows, both in pieces)
      const SK = PC.sky, HW = SK.HW, HH = SK.HH;
      add('crowns.fs', 0, () => {
        if (!cOn()) return;
        T.H0 = g.tex('RGBA32F', HW, HH); T.H1 = g.tex('RG32F', HW, HH);    // H1: the third radius' (obj, occupancy)
        T.FS = g.tex('RG32F', nx, nz);
        fbo([T.FS], null, () => { gl.viewport(0, 0, nx, nz); gl.clearBufferfv(gl.COLOR, 0, [0, 0, 0, 0]); });
      });
      for (const B of SK.bands) {
        const k0 = B.k0, r0 = B.r0, ja = cr.ja;
        for (const p of B.h) {
          piece('crowns.skyh', p, (y0, y1, x0, x1) => fbo([T.H0, T.H1], null, (f) => g.pass(P.skyh, f, HW, HH,
            { uCG: T.CG, uSR: cr.sr, uN: [nx, nz], uOff: [ja, k0] }, y0, y1, x0, x1)), false, false, undefined, cOn);
        }
        for (const p of B.v) {
          piece('crowns.skyv', p, (y0, y1, x0, x1) => fbo([T.FS], null, (f) => g.pass(P.skyv, f, nx, nz,
            { uCG: T.CG, uVo: T.VO, uH0: T.H0, uH1: T.H1, uHOff: [ja, k0], uSR: cr.sr, uN: [nx, nz], uBox: cr.box, uCellM: cr.cell }, r0 + y0, r0 + y1, ja + x0, ja + x1)), false, false, undefined, cOn);
        }
      }
      add('crowns.free', 0, () => { g.free(T.CG); g.free(T.VO); g.free(T.H0); g.free(T.H1); g.free(T.steps); });
    }

    // ---- 5. the light -> the pool layer's level 0 + the scratch chain
    for (const ch of ['vis', 'sky']) {
      // the window from the level's tiles (255 past a tile / where none is), in row slices of
      // ~SLICE_CELLS (sliced: one call of the whole L2+ window was up to 18 ms: gpupainter-final igpu
      // 2_0_1), then uploaded in calls of <= up_bytes
      const w = G.shd[ch], px = W.shd2.px, cs = String(w.L.cell);
      let a = null;
      add('light.cpu', 0, slicedRows(w.h, w.w, (r0, r1) => {
        if (!a) a = new Uint8Array(w.w * w.h).fill(255);
        for (let Z = w.z0 + r0; Z < w.z0 + r1; Z++) {
          const gz = Math.floor(Z / px);
          for (let gx = Math.floor(w.x0 / px); gx <= Math.floor((w.x0 + w.w - 1) / px); gx++) {
            const d = this._data(`v:${ch}:${cs}:${gx}_${gz}`);
            if (!d || Z - gz * px >= d.h) continue;
            const i0 = Math.max(w.x0, gx * px), i1 = Math.min(w.x0 + w.w, gx * px + Math.min(px, d.w)), r = (Z - gz * px) * d.w - gx * px;
            if (i1 > i0) a.set(d.data.subarray(r + i0, r + i1), (Z - w.z0) * w.w + (i0 - w.x0));
          }
        }
      }));
      add('light.up', 0, () => { T[ch] = g.tex('R8UI', w.w, w.h); });
      add('light.up', 0, upload(() => T[ch], () => a, w.h, w.w));
    }
    const layer = () => this._layer(J);
    band('light', (y0, y1, x0, x1) => {
      const L = this.world.light, sun = this.world.sun, v = G.shd.vis, s = G.shd.sky, SF = W.strata;
      fbo([{ t: layer(), level: 0, layer: J.resv.layer }, { t: W.tMip.t, level: 0 }], null, (f) => g.pass(P.light, f, SLOT_PX, SLOT_PX, {
        ...RU(), uCls: U.uCls, uClsW: U.uClsW, uWorld: U.uWorld, uA: T.A, uH: T.H,
        uVis: T.vis, uSky: T.sky, uVisN: [v.L.w, v.L.h, v.x0, v.z0], uSkyN: [s.L.w, s.L.h, s.x0, s.z0], uShdC: [v.L.cell, s.L.cell],
        uFS: (crOn() && T.FS) || W.tGrad, uCG: crOn() ? [G.x0 - cr.X0, G.z0 - cr.Z0, cr.cell, 1] : [0, 0, 1, 0], uCGN: crOn() ? [cr.nx, cr.nz] : [1, 1],
        uSun: [sun.sx, sun.sy, sun.sz], uLSky: L.sky, uLGnd: L.ground, uLSun: L.sun, uLW: L.white, uWaterC: W.waterC,
        uStr: W.tStr, uStrN: SF ? [SF.w, SF.h] : [1, 1], uStrP: SF ? [SF.cell, G.str ? this.strata : 0, SF.t0, SF.t1] : [1, 0, 0, 1],
        uStrR: SF ? SF.mR : 0, uStrC: SF ? SF.mC : 0,
      }, y0, y1, x0, x1));
    });
    add('light.free', 0, () => { g.free(T.A); g.free(T.H); g.free(T.FS); g.free(T.vis); g.free(T.sky); g.free(T.cls); g.free(T.rec); });
    this._mips(J, add);
    return steps;
  }

  // ---------------------------------------------------------------- the probe (window.__vr.ground)
  // first-minute-measure (telemetry `gen.g`, main.js genTel): the wanted ground tiles now — pend = wanted, not
  // resident (naked + optic); want / miss of them ON SCREEN (gmtex vtVis, the view order's test; the optic's
  // always, everything before play); per missing on-screen tile what holds it (why: run = the job, resv =
  // reserved, held = HOLD_MS, fail = backing off, gat = inputs gathering, queue = listed / waiting) and the
  // FALLBACK the fragment draws instead: fb[d - 1] = tiles whose nearest resident level is d coarser (d >= 4
  // in fb[3]), none = nothing resident under it (the node is not drawn: texReady). ground-paint-coverage: the
  // naked want counts its HELD SET only (gmtex vtHeld: what the pool holds at once — the rest is never
  // listed); over = the on-screen naked keys past it. Read-only.
  visState() {
    const vt = this.vt, out = { want: 0, miss: 0, pend: 0, fb: [0, 0, 0, 0], none: 0, over: 0,
      why: { run: 0, resv: 0, held: 0, fail: 0, gat: 0, queue: 0 } };
    if (!vt || !vt.lv.length) return out;
    const V = vt._V, top = vt.lv.length - 1, cur = this._job && this._job.key, H = vt._held;
    for (const [M, p] of [[vt.want.naked, 0], [vt.want.focus, 1]]) {
      for (const key of M.keys()) {
        const have = vt.res.has(key), vis = p === 1 || !vt.viewOn || !V.on || vtVis(vt.lv, vt._eye, V, key);
        if (p === 0 && H && !H.has(key)) { if (vis) out.over++; continue; }
        if (!have) out.pend++;
        if (!vis) continue;
        out.want++;
        if (have) continue;
        out.miss++;
        const W = out.why;
        if (key === cur) W.run++; else if (vt.loading.has(key)) W.resv++; else if (this.held.has(key)) W.held++;
        else if (vt.failed.has(key)) W.fail++; else if (this._gat.has(key)) W.gat++; else W.queue++;
        const [j, tx, ty] = key.split('_').map(Number), m = vt.lv[j].m, x = (tx + 0.5) * m, z = (ty + 0.5) * m;
        let d = 0;
        for (let k = j + 1; k <= top && !d; k++) {
          const L = vt.lv[k];
          if (vt.res.has(`${k}_${Math.min(L.nx - 1, Math.floor(x / L.m))}_${Math.min(L.ny - 1, Math.floor(z / L.m))}`)) d = k - j;
        }
        if (d) out.fb[Math.min(d, 4) - 1]++; else out.none++;
      }
    }
    return out;
  }

  state() {
    const J = this._job, g = this.g;
    return {
      fatal: this.fatal, ready: !!(this.P && this.P.ok), finest: this.finest, finest_now: this.fin, finest_log: this._finLog.slice(), hidden: hidden(),
      // the one named one-off past 2 ms a step: the Worker constructor (an idle callback, off the frame)
      one_off: { 'worker.new': _wkMs === null ? null : Math.round(_wkMs * 100) / 100 },
      stats: { ...this.stats },
      job: J ? { key: J.key, step: J.i, steps: J.steps ? J.steps.length : 0, runs: J.n } : null,
      cache_mb: Math.round(this.cacheBytes / 1e5) / 10, cache_n: this.cache.size, loads: this.loads,
      // the want's references (refSync): the cached bytes of inputs a wanted key reads, the inputs counted,
      // the keys counting; fetched: inputs ever fetched (stats.refetch: fetches of one again)
      ref_mb: Math.round(this._refBytes / 1e5) / 10, ref_n: this._refs.size, ref_keys: this._refK.size, fetched: this._fetchN.size,
      world_gpu_mb: this.world && this.world.worldBytes ? Math.round(this.world.worldBytes / 1e5) / 10 : 0,
      scratch_gpu_mb: g ? Math.round(g.bytes / 1e5) / 10 : 0,
      pool_gpu_mb: g ? Math.round(g.poolBytes / 1e5) / 10 : 0, node_gpu_mb: Math.round(this._nodeBytes / 1e5) / 10,
      node_n: this._nodes.size, worker: _wk ? (_wk.dead ? 'dead' : 'up') : 'none',
      gpu_rate: GPU_RATE, rate_safe: RATE_SAFE, warm_ms: Math.round(this._warmMs), warm_top: this._warmTop.slice(),
      // every main-thread fetch() call of the page's asset queue (assetq fetchCallStats: the bare call's ms, its span)
      fetch_calls: fetchCallStats(), scan: { n: this._scanMs.n, ms: Math.round(this._scanMs.ms), max: Math.round(this._scanMs.max * 10) / 10, charged_ms: Math.round(this._scanMs.charged) },
      cls: deviceClass(), caps: caps(), up_bytes: submitCaps().up_bytes, up_buf: submitCaps().buf_bytes,
      // the per-run submissions (SUBMIT): the caps + the measured maxima over every run (the meter:
      // the GL calls' own payloads / draws) + runs past a cap; the GPU ms per RUN (timer queries:
      // ms all of a run's, b2b without its first; est the staging's) med / max over the last GPU_RUNS
      // (play / beam: each phase's maxima; over: runs past their PHASE's caps — beam x BEAM_K —, overs
      // the last 8 with their step labels; over_est: a run's GPU estimate past its gpu_ms)
      submit: { cap: submitCaps(), cap_beam: runCaps(submitCaps(), true), beam_k: BEAM_K, max_upload_bytes: this._sub.tex, max_buf_bytes: this._sub.buf, max_calls: this._sub.calls, max_draws: this._sub.draws,
        max_units: this._sub.units, max_est_ms: this._sub.est, runs: this._sub.runs, over: this._sub.over, over_est: this._sub.over_est, worst: this._sub.worst,
        overs: this._sub.overs.slice(), play: { ...this._sub.play }, beam: { ...this._sub.beam }, capped: this.stats.capped,
        // the steps a fresh run admitted past its cap (ruling p8 (2): atomic only — a mip level's pass, or a
        // rect step whose K x fixed cost alone fills the cap, 'label (fixed)'): count, by name {n, max_est}
        atomic: { n: this._atomic.n, by: JSON.parse(JSON.stringify(this._atomic.by)) } },
      // the cost model per label (ruling p8 (1)): a ms, r M units a ms (x RATE_SAFE, held), ts 1 = the
      // Theil-Sen slope, big = the large pieces' guard M / ms (0: none), n = samples fitted
      model: Object.fromEntries([...this.prof].filter(([, e]) => e.brn > 0).map(([l]) => { const m = this._model(l);
        return [l, { a: Math.round(m.a * 1000) / 1000, r: Math.round(m.r / 1e4) / 100, ts: m.ts, big: Math.round(m.big / 1e4) / 100, n: m.n }]; })),
      // run.state / run.reset calls past STALL_MS (the per-run outliers: GL-blocked when gl ~ ms)
      stall: { ms: STALL_MS, state: { ...this._stalls.state }, reset: { ...this._stalls.reset }, last: this._stalls.last.slice() },
      gpu_run: this._gpuRunStats(), min_run_ms: Math.round(this.minMs() * 100) / 100,
      // the start-up timeline (s since the page's start: ctor heavy w.fq w.genome.in w.res.in w.inputs w.genome world gl
      // progs linked warm scan1 listed1 job1 commit1 hidden latch.tiles near play shown; worker = the Worker made)
      t_log: { ...this._tl, worker: _wkAt },
      // the scored back-to-back overs with their timed steps (_runDone) + every b2b step's measured / estimate by
      // 'label|flags' (flags: _run's 1 first piece of the label in the job, 2 the job's first run, 4 a pool layer
      // first written, 8 a label switch) {n, ms, est, max_r, over2 = steps past 2x their estimate}
      overs_b2b: this._oversB.slice(),
      ratio_med: Object.fromEntries([...this._ratioL].map(([l, L]) => [l, Math.round(ringMed(L.a, L.n) * 100) / 100])),
      step_flags: this._flagS ? Object.fromEntries([...this._flagS].map(([k, x]) => [k, { n: x.n, ms: Math.round(x.ms * 10) / 10, est: Math.round(x.est * 10) / 10, max_r: Math.round(x.max_r * 100) / 100, over2: x.over2 }])) : {},
      // rate: all samples' cost / ms; rate_b2b: the back-to-back ones'; rate_use: what _rate gives now
      steps: Object.fromEntries([...this.prof].map(([l, e]) => [l, {
        n: e.n, ms: Math.round(e.ms * 10) / 10, max: Math.round(e.max * 100) / 100, gl_ms: Math.round(e.gl * 10) / 10, unit: Math.round(this._unit(l) * 100) / 100,
        ...(e.gn ? { gpu_n: e.gn, gpu_ms: Math.round(e.gms * 100) / 100, rate: Math.round(e.gc / Math.max(e.gms, 1e-6)), rate_q: Math.round(e.rq),
          gpu_b2b_n: e.bn, gpu_b2b_ms: Math.round(e.bms * 100) / 100, rate_b2b: e.bn ? Math.round(e.bc / Math.max(e.bms, 1e-6)) : null, rate_use: Math.round(this._rate(l)) } : {}),
      }])),
      gl: Object.fromEntries([...this._glc].sort((a, b) => b[1].ms - a[1].ms).map(([k, e]) => [k, { n: e.n, ms: Math.round(e.ms * 10) / 10, max: Math.round(e.max * 100) / 100 }])),
      slow: this._slow.slice(), slow_gl: this._slowCalls.slice(), overs: this._overs.slice(),
      // [t s, phase, gen debt ms, gen debt_ms summed, gen runs, over-slice runs, their ms over, committed,
      //  near floor tiles missing, terrain.nearReady, held GPU MB (scratch + pool + nodes), input cache MB,
      //  tiles listed (-1: none yet: the world / warm), this painter's loads in flight, the queue's fetches,
      //  the ground tree's near wait s [none, net, dec, tex, vt, job], sched hidden 0 / 1, the scan's finest level]
      trace_cols: ['t', 'phase', 'debt', 'debt_ms', 'gen_runs', 'over_runs', 'over_ms', 'committed', 'near_miss', 'near', 'held_mb', 'cache_mb', 'listed', 'loads', 'q_fetching', 'gm_wait', 'hidden', 'finest', 'debt_ground'],
      trace: this._trace.slice(),
      held_gpu_mb: g ? Math.round((g.bytes + g.poolBytes + this._nodeBytes) / 1e5) / 10 : 0,
      done: Object.fromEntries([...this.done].slice(-64)),
    };
  }

  _gpuRunStats() {
    const a = this._gpuRuns, n = a.length, r2 = (v) => Math.round(v * 100) / 100;
    if (!n) return { n: 0 };
    const q = (c) => { const v = a.map((x) => x[c]).sort((x, y) => x - y); return [r2(v[n >> 1]), r2(v[Math.floor(0.95 * (n - 1))]), r2(v[n - 1])]; };
    const medOf = this._medL;
    const [m, m95, mx] = q(0), [b, b95, bx] = q(1), [e, e95, ex] = q(2);
    // per mode: every run's maxima + the runs past their cap (ms / b2b), the run factor (now / max), and
    // the last GPU_RUNS runs' med / p95 of ms, b2b, lead, est and of ms / est
    const X = (o, hid) => {
      const s = a.filter((x) => x[5] === hid), k = s.length, Q = (f) => { const v = s.map(f).sort((x, y) => x - y); return k ? [r2(v[k >> 1]), r2(v[Math.floor(0.95 * (k - 1))])] : [null, null]; };
      const K = this._runK[hid ? 'hidden' : 'shown'];
      // PREEMPTED: the candidates whose label's median holds (iii); own = the rest of the scored overs
      const pc = this._preC.filter((c) => c.hid === hid && preempted(c, medOf)), pl = {};
      for (const c of pc) for (const l of c.labels) pl[l] = (pl[l] || 0) + 1;
      const pre = { n: pc.length, share: o.over_b2b ? r2(pc.length / o.over_b2b) : 0, max_ms: r2(Math.max(0, ...pc.map((c) => c.ms))), max_b2b: r2(Math.max(0, ...pc.map((c) => c.b2b))), labels: pl,
        held: this._preC.filter((c) => c.hid === hid && !preempted(c, medOf)).length };
      return { over_own: o.over_b2b - pc.length, preempted: pre, n: o.n, cap_ms: o.cap, max_ms: r2(o.max_ms), max_b2b: r2(o.max_b2b), max_lead: r2(o.max_lead), max_est: r2(o.max_est), over: o.over, over_b2b: o.over_b2b, over8: o.over8,
        scored: o.scored, over_atomic: o.over_atomic, lead_only: o.lead_only, lead_only_over: o.lead_only_over,
        k: r2(K.k), k_max: r2(K.max), k_pre: K.pre, last: k, ms: Q((x) => x[0]), b2b: Q((x) => x[1]), lead: Q((x) => x[3]), est: Q((x) => x[2]), ratio: Q((x) => (x[2] > 0 ? x[0] / x[2] : 0)) };
    };
    return { n, med_ms: m, p95_ms: m95, max_ms: mx, med_b2b: b, p95_b2b: b95, max_b2b: bx, med_est: e, p95_est: e95, max_est: ex,
      hidden: X(this._gpuMax.hidden, 1), shown: X(this._gpuMax.shown, 0) };
  }
  // the declared indivisible run (PASS.minMs; gensched banks a frame's slice below it): the run's
  // fixed CPU (state, reset, end) + the next step's CPU unit + its GPU estimate (a job's first run:
  // the plan's), held to MIN_RUN_MAX
  minMs() {
    const J = this._job, u = (l) => this._unit(l), tail = u('run.state') + u('run.reset') + u('run.end');
    let m = tail + u('run.plan');
    if (J && J.steps && J.i < J.steps.length) {
      // (a rect step: its rest's, ruling p8 — the plain whole-step estimate, as the run admits it)
      const s = J.steps[J.i], c = s.per !== undefined ? (s.on && !s.on() ? 0 : rectNext(s, Infinity, this._pr)) : typeof s.cost === 'function' ? s.cost() : s.cost;
      m = tail + u(s.label) + modelMs(this._model(s.label), c) * this._k(hidden());
    }
    return Math.min(MIN_RUN_MAX, m);
  }

  // the probe (--ground-check-keys): a resident slot this painter wrote, its inputs loaded again
  // when the cache let them go (the same assets: a deterministic paint) and pinned until
  // ensure(null) -> true once all are in (false: not resident / not ours / not in by ms)
  async ensure(key, ms = 30000) {
    if (key === null) { this._probePin.clear(); return true; }
    if (!this.vt.res.has(key) || !this.done.has(key) || this.done.get(key).ship) return false;
    const ids = this._inputs(key);
    if (!ids) return false;
    for (const id of ids) this._probePin.add(id);
    const t0 = now(), inn = () => ids.every((id) => { const e = this.cache.get(id); return e && (e.st === 'ok' || e.st === 'absent'); });
    while (!inn()) {
      if (now() - t0 > ms) return false;
      this._load(ids, now(), 'near');
      await new Promise((r) => setTimeout(r, 100));
    }
    return true;
  }

  // the resident keys this painter wrote whose inputs are all still cached (refWorld can
  // rebuild them: the probe's ground check; the cache evicts per class)
  keys() {
    const kept = (k) => {
      const ids = this._inputs(k);
      return !!ids && ids.every((id) => { const e = this.cache.get(id); return e && (e.st === 'ok' || e.st === 'absent'); });
    };
    return [...this.vt.res.keys()].filter((k) => this.done.has(k) && !this.done.get(k).ship && kept(k));
  }

  // a resident slot's level 0 as stored (sRGB bytes, row 0 = the south edge): Uint8Array 512 x 512 x 4
  read(key) {
    const t = this.vt.res.get(key), r = this.getRenderer && this.getRenderer();
    if (!t || !r || !this.g) return null;
    const gl = this.g.gl, tex = r.properties.get(this.vt.pools[t.p]).__webglTexture;
    if (!tex) return null;
    const f = gl.createFramebuffer(), out = new Uint8Array(SLOT_PX * SLOT_PX * 4);
    try {
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, f);
      gl.framebufferTextureLayer(gl.READ_FRAMEBUFFER, gl.COLOR_ATTACHMENT0, tex, 0, t.slot);
      gl.readBuffer(gl.COLOR_ATTACHMENT0);
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null);
      gl.pixelStorei(gl.PACK_ALIGNMENT, 1);
      gl.readPixels(0, 0, SLOT_PX, SLOT_PX, gl.RGBA, gl.UNSIGNED_BYTE, out);
    } finally {
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, null);
      gl.deleteFramebuffer(f);
      r.resetState();
    }
    return out;
  }

  // groundref's world of slot key from the cached assets the GPU painted it from (slotworld.js — the
  // pre-paint's own builder, windowed to the slot): the probe's ground check. Async: the node meshes +
  // crown packs the worker holds come back as copies
  async refWorld(key) {
    const W = this.world, st = this._wp, M = this.meta, s = M.surface;
    if (!W || !st) return null;
    const ids = this._inputs(key);
    if (!ids) return null;
    const got = new Map(), K = ggWorker(), wk = new Map();
    for (const id of ids) {
      const e = this.cache.get(id);
      if (!e || (e.st !== 'ok' && e.st !== 'absent')) return null;
      if (e.st !== 'ok') continue;
      if (K && e.data && e.data.wk) wk.set(id, e.data.wk); else got.set(id, e.data);
    }
    if (wk.size) {
      const out = await askP(K, { op: 'dump', keys: [...wk.values()] });
      for (const [id, k] of wk) { if (!out[k]) throw new Error(`groundgen: the worker lost ${k}`); got.set(id, out[k]); }
    }
    // the base made once per painted params (a tunables change between two checks makes it again)
    const pk = `${this.strata}|${!!this.tiling}`;
    if (!this._refB || this._refB.pk !== pk) {
      const R = W.res, rgb = (a, n) => { const o = new Uint8Array(3 * n); for (let q = 0; q < n; q++) { o[3 * q] = a[4 * q]; o[3 * q + 1] = a[4 * q + 1]; o[3 * q + 2] = a[4 * q + 2]; } return o; };
      const ix = this.tm.gm.index, ex = {};
      for (const [k, e] of Object.entries(W.exIn || {})) ex[k] = { w: e.n, h: e.n, data: e.img };
      this._refB = worldBase(M, {
        genome: W.genome, water: st.water, nodeAt: (k) => ix.nodes.has(k), pal: PAL, gtTable: GNT, look: WAYSLOOK, marks: MARKS_IN,
        strata: this.strata, tiling: this.tiling,
        residual: R ? [{ tx: 0, ty: 0, w: R.w, h: R.h, data: rgb(R.codes, R.w * R.h) }] : null,
        s2col: W.s2 ? { w: W.s2.w, h: W.s2.h, data: W.s2.img } : null,
        rockfield: W.strataIn ? { w: s.rockfield.w, h: s.rockfield.h, data: W.strataIn.img } : null, exemplars: ex,
        coarse: this.surf ? this.surf.field(s.coarse.stride, false) : null,
      });
      this._refB.pk = pk;
    }
    return slotWorld(this._refB, this._geom(key), got);
  }

  dispose() {
    this._gone = true;                            // the fetches not started yet (_soon) never start
    if (this._job) this._abandon('dispose');
    const g = this.g, W = this.world;
    if (g) {
      if (W) for (const k of ['tGrad', 'tGz', 'tRes', 'tS2', 'tStr', 'tExG', 'tExL', 'tMip', 'tMip1']) g.free(W[k]);
      g.drain();
      for (const N of this._nodes.values()) this._nodeFree(N);
      this._nodes.clear();
      if (this.P) for (const P of this.P.list) g.gl.deleteProgram(P.p);
      g.gl.deleteVertexArray(g.vao);
    }
    this._wkDrop([...this.cache.values()].filter((e) => e.data && e.data.wk).map((e) => e.data.wk));
    this.cache.clear();
    this.cacheBytes = 0;
    this._pre.clear();
    this._gat.clear();
    this._refs.clear();
    this._refK.clear();
    this._refBytes = 0;
    if (this.vt.onScan) this.vt.onScan = null;
    gen.want('ground', [], null);
    if (_inst === this) _inst = null;
  }
}

// the decoded bytes of a cached input (the LRU's measure)
const sizeOf = bytesOf;

// a sheet lake's bed bits (|= 0x80) on an n x n tile of 1 m cells at world (X0, Z0): per row the
// rings' crossings at z = Z + 0.5 (inRings' expression), the cell centre x = X + 0.5 inside when an
// odd count of them lies right of it — inRings' parity, cell for cell
function sheetBits(L, m, X0, Z0, n, r0 = 0, r1 = n) {
  const [bx0, bz0, bx1, bz1] = L.bb, xs = [];
  const za = Math.max(Z0 + r0, Math.floor(bz0)), zb = Math.min(Z0 + r1 - 1, Math.ceil(bz1));
  const xa = Math.max(X0, Math.floor(bx0)), xb = Math.min(X0 + n - 1, Math.ceil(bx1));
  if (xb < xa) return;
  for (let Z = za; Z <= zb; Z++) {
    const z = Z + 0.5;
    xs.length = 0;
    for (const R of L.rings) {
      for (let i = 0, j = R.length - 1; i < R.length; j = i++) {
        const [xi, zi] = R[i], [xj, zj] = R[j];
        if ((zi > z) !== (zj > z)) xs.push(((xj - xi) * (z - zi)) / (zj - zi) + xi);
      }
    }
    if (!xs.length) continue;
    xs.sort((a, b) => a - b);
    const o = (Z - Z0) * n - X0;
    for (let X = xa, p = 0; X <= xb; X++) {
      const x = X + 0.5;
      while (p < xs.length && xs[p] <= x) p++;
      if ((xs.length - p) & 1) m[o + X] |= 0x80;
    }
  }
}
